'use client';

import { Link } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import maplibregl, { LngLatBounds, type Map as MapLibreMap, type MapLayerMouseEvent } from 'maplibre-gl';
import type { Feature, FeatureCollection, LineString, Point, Polygon } from 'geojson';
import { useCustomParkings } from '../hooks/useCustomParkings';
import {
  type CameraPlacement,
  type CameraSpot,
  type CameraSpotPoint,
  type CustomParkingRecord,
  type ParkingPolygon,
  type StoredCustomParkingRecord,
} from '../lib/customParkingStorage';
import type { ParkingData, ParkingLayoutDefinition } from '../lib/parkingTypes';
import { callParkingBoxApi } from '../lib/parkingBoxApi';
import {
  extractInternalParkingSnapshot,
  flattenInternalParkingSpots,
  type InternalParkingSnapshot,
  type InternalParkingSpot,
} from '../lib/internalParkingData';
import { normalizeParkingListResponse, type ParkingListItem } from '../lib/parkingList';
import { ParkingLayoutBuilder, type ParkingLayoutBuilderValue, type ParkingSpotType } from './ParkingLayoutBuilder';

type ParkingPolygonAdminProps = {
  parkingData: ParkingData;
};

type DraftFeatureProperties = {
  kind: 'line' | 'vertex';
  index?: number;
};

type WorkingPolygonProperties = {
  status: 'preview' | 'final';
};

type AreaPolygonProperties = {
  id: string;
  label: string;
  source: 'local' | 'saved';
};

type WizardStep = 1 | 2 | 3 | 4 | 5;

type ParkingDetailsFormState = {
  name: string;
  address: string;
  city: string;
  space_regular: string;
  space_handi: string;
  space_family: string;
  space_disabled: string;
  space_ev: string;
};

type RateFormState = {
  id: string;
  areaId: string;
  templateId: string | null;
  name: string;
  charging_period: string;
  passenger_cars: string;
  trucks: string;
  buses: string;
  moto_bikes: string;
  campers: string;
  charging_station: string;
};

type RateFeeField = 'passenger_cars' | 'trucks' | 'buses' | 'moto_bikes' | 'campers' | 'charging_station';

type ParkingPriceTemplate = {
  id: string;
  name: string;
  charging_period: string;
  passenger_cars: string;
  trucks: string;
  buses: string;
  moto_bikes: string;
  campers: string;
  charging_station: string;
};

type ParkingPricePayload = {
  name: string;
  charging_period: number;
  passenger_cars: number;
  trucks: number;
  buses: number;
  moto_bikes: number;
  campers: number;
  charging_station: number;
};

const isRateEmpty = (rate: RateFormState): boolean =>
  !rate.charging_period.trim() &&
  !rate.passenger_cars.trim() &&
  !rate.trucks.trim() &&
  !rate.buses.trim() &&
  !rate.moto_bikes.trim() &&
  !rate.campers.trim() &&
  !rate.charging_station.trim() &&
  !rate.templateId;

const applyTemplateToRate = (rate: RateFormState, template: ParkingPriceTemplate): RateFormState => ({
  ...rate,
  templateId: template.id,
  name: template.name,
  charging_period: template.charging_period,
  passenger_cars: template.passenger_cars,
  trucks: template.trucks,
  buses: template.buses,
  moto_bikes: template.moto_bikes,
  campers: template.campers,
  charging_station: template.charging_station,
});

type ParkingSpotsRequestPayload = {
  size: [number, number];
  spots: {
    id: string;
    label: string;
    cx: number;
    cy: number;
    width: number;
    height: number;
    rotation: number;
    type: ParkingSpotType | 'entry' | 'exit';
  }[];
};

type LayoutReferenceSpot = {
  id: string;
  label: string;
  occupancy?: 0 | 1 | null;
};

type LayoutSpotReferenceOption = {
  id: string;
  label: string;
  occupancy: 0 | 1 | null;
};

type AreaFormEntry = {
  id: string;
  serverId: string | number | null;
  name: string;
  polygon: ParkingPolygon;
  rate: RateFormState;
  cameraPlacement: CameraPlacement;
  cameraUrl: string;
  cameraLongitude: string;
  cameraLatitude: string;
  cameraDevice: string;
  cameraDescription: string;
  externalCameraId: string;
  internalCameraId: string;
  cameraSpots: CameraSpot[];
  internalParkingSpots: InternalParkingSpot[];
};

type CameraDrawingState = {
  areaId: string;
  points: CameraSpotPoint[];
  hoverPoint: CameraSpotPoint | null;
};

type CameraSpotDragState = {
  areaId: string;
  spotId: string;
  pointerId: number;
  startPointer: CameraSpotPoint;
  originPoints: CameraSpotPoint[];
};

type CameraSnapshotCacheEntry = {
  src: string;
  camId: string;
  fetchedAt: number;
};

type EditingParkingMeta = {
  id: string | number;
  label: string;
};

type PendingDeleteState = {
  identifier: { raw: string | number; text: string };
  name: string;
  address: string;
};

const DEFAULT_LIGHT_STYLE_URL = 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json';
const DEFAULT_DARK_STYLE_URL = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';
const LIGHT_STYLE_URL = process.env.REACT_APP_MAP_LIGHT_STYLE_URL ?? DEFAULT_LIGHT_STYLE_URL;
const DARK_STYLE_URL = process.env.REACT_APP_MAP_DARK_STYLE_URL ?? DEFAULT_DARK_STYLE_URL;

const DEFAULT_CENTER: [number, number] = [19.1451, 52.2159];
const DEFAULT_CAMERA_PLACEMENT: CameraPlacement = 'internal';

const EMPTY_FEATURE_COLLECTION: FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};
const ensureWorkerClass = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const globalAny = maplibregl as typeof maplibregl & { workerClass?: typeof Worker };
  if (globalAny.workerClass) {
    return;
  }

  globalAny.workerClass = class MapLibreWorker extends Worker {
    constructor() {
      super(new URL('maplibre-gl/dist/maplibre-gl-csp-worker.js', import.meta.url), { type: 'module' });
    }
  } as typeof Worker;
};

const ensureClosedPolygon = (polygon: ParkingPolygon): ParkingPolygon => {
  if (polygon.length === 0) {
    return [];
  }
  const first = polygon[0];
  const last = polygon[polygon.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) {
    return polygon;
  }
  return [...polygon, first];
};

const buildDraftFeatureCollection = (points: ParkingPolygon): FeatureCollection<LineString | Point, DraftFeatureProperties> => {
  const features: Feature<LineString | Point, DraftFeatureProperties>[] = [];

  if (points.length >= 2) {
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: points,
      },
      properties: {
        kind: 'line',
      },
    });
  }

  points.forEach(([lng, lat], index) => {
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [lng, lat],
      },
      properties: {
        kind: 'vertex',
        index,
      },
    });
  });

  return {
    type: 'FeatureCollection',
    features,
  };
};

const buildWorkingFeatureCollection = (polygon: ParkingPolygon, status: 'preview' | 'final' | null): FeatureCollection<Polygon, WorkingPolygonProperties> => {
  if (!polygon.length || !status) {
    return EMPTY_FEATURE_COLLECTION as FeatureCollection<Polygon, WorkingPolygonProperties>;
  }

  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [polygon],
        },
        properties: {
          status,
        },
      },
    ],
  };
};

const buildAreasFeatureCollection = (areas: AreaFormEntry[]): FeatureCollection<Polygon, AreaPolygonProperties> => {
  if (!areas.length) {
    return EMPTY_FEATURE_COLLECTION as FeatureCollection<Polygon, AreaPolygonProperties>;
  }

  return {
    type: 'FeatureCollection',
    features: areas.map((area) => ({
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [ensureClosedPolygon(area.polygon)],
      },
      properties: {
        id: area.id,
        label: area.name,
        source: 'local',
      },
    })),
  };
};

const buildSavedFeatureCollection = (records: StoredCustomParkingRecord[], excludeId?: string): FeatureCollection<Polygon, AreaPolygonProperties> => {
  const features: Feature<Polygon, AreaPolygonProperties>[] = [];

  records.forEach((record) => {
    if (excludeId && record.id === excludeId) {
      return;
    }
    record.areaMeta.forEach((area) => {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [area.footprint.polygon],
        },
        properties: {
          id: `${record.id}-${area.id}`,
          label: `${record.parking.name} - ${area.name}`,
          source: 'saved',
        },
      });
    });
  });

  if (!features.length) {
    return EMPTY_FEATURE_COLLECTION as FeatureCollection<Polygon, AreaPolygonProperties>;
  }

  return {
    type: 'FeatureCollection',
    features,
  };
};

const focusPolygon = (map: MapLibreMap, polygon: ParkingPolygon) => {
  if (polygon.length === 0) {
    return;
  }
  let bounds: LngLatBounds | null = null;
  polygon.forEach(([lng, lat]) => {
    if (!bounds) {
      bounds = new LngLatBounds([lng, lat], [lng, lat]);
    } else {
      bounds.extend([lng, lat]);
    }
  });
  if (bounds) {
    map.fitBounds(bounds, { padding: 70, maxZoom: 17, duration: 600 });
  }
};

const createRecordId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `parking-${Date.now().toString(36)}`;
};

const createAreaId = () => `area-${Math.random().toString(36).slice(2, 10)}`;
const createCameraSpotId = () => `spot-${Math.random().toString(36).slice(2, 10)}`;
const createLayoutSpotId = () => `layout-spot-${Math.random().toString(36).slice(2, 10)}`;
const createLayoutGateId = (type: 'entry' | 'exit' = 'entry') => `layout-${type}-${Math.random().toString(36).slice(2, 10)}`;

const createRateForm = (areaId: string, fallbackName: string, base?: Partial<RateFormState>): RateFormState => ({
  id: base?.id ?? `${areaId}-pricing`,
  areaId,
  templateId: base?.templateId ?? null,
  name: base?.name ?? fallbackName,
  charging_period: base?.charging_period ?? '',
  passenger_cars: base?.passenger_cars ?? '',
  trucks: base?.trucks ?? '',
  buses: base?.buses ?? '',
  moto_bikes: base?.moto_bikes ?? '',
  campers: base?.campers ?? '',
  charging_station: base?.charging_station ?? '',
});

const parsePositiveNumber = (value: string): number => {
  const parsed = Number(value);
  if (Number.isFinite(parsed)) {
    return Math.max(0, parsed);
  }
  return 0;
};
const roundLayoutValue = (value: number) => Number(value.toFixed(2));
const normalizeLayoutRotation = (rotation: number) => {
  const wrapped = ((rotation % 360) + 360) % 360;
  return Number(wrapped.toFixed(1));
};
const parseAutoSpotLabelIndex = (label: string): number | null => {
  const normalized = label.trim();
  if (!normalized) {
    return null;
  }

  const prefixedMatch = normalized.match(/^(?:miejsce|spot)\s+(\d+)$/i);
  if (prefixedMatch) {
    const value = Number(prefixedMatch[1]);
    return Number.isInteger(value) && value > 0 ? value : null;
  }

  const shortPrefixedMatch = normalized.match(/^m\s*(\d+)$/i);
  if (shortPrefixedMatch) {
    const value = Number(shortPrefixedMatch[1]);
    return Number.isInteger(value) && value > 0 ? value : null;
  }

  const numericMatch = normalized.match(/^(\d+)$/);
  if (numericMatch) {
    const value = Number(numericMatch[1]);
    return Number.isInteger(value) && value > 0 ? value : null;
  }

  return null;
};
const formatAutoSpotLabel = (index: number) => `${index}`;
const normalizeAutoSpotLabel = (label: string) => {
  const normalized = label.trim();
  if (!normalized) {
    return '';
  }

  const parsedIndex = parseAutoSpotLabelIndex(normalized);
  return parsedIndex !== null ? formatAutoSpotLabel(parsedIndex) : normalized;
};
const createLayoutBuilderState = (): ParkingLayoutBuilderValue => ({
  width: 600,
  height: 400,
  spots: [],
  gates: [],
});

const layoutDefinitionToBuilder = (layout: ParkingLayoutDefinition | null | undefined): ParkingLayoutBuilderValue => {
  if (!layout || !layout.canvas) {
    return createLayoutBuilderState();
  }
  const safeWidth = typeof layout.canvas.width === 'number' && layout.canvas.width > 0 ? layout.canvas.width : 600;
  const safeHeight = typeof layout.canvas.height === 'number' && layout.canvas.height > 0 ? layout.canvas.height : 400;
  const sourceObjects = Array.isArray(layout.objects) ? layout.objects : [];
  const builderSpots: ParkingLayoutBuilderValue['spots'] = [];
  const builderGates: ParkingLayoutBuilderValue['gates'] = [];
  const gateCounters: Record<'entry' | 'exit', number> = { entry: 0, exit: 0 };

  sourceObjects.forEach((object) => {
    const width = typeof object.width === 'number' && object.width > 0 ? object.width : object.type === 'spot' ? 48 : 120;
    const height = typeof object.height === 'number' && object.height > 0 ? object.height : object.type === 'spot' ? 96 : 42;
    const cx = typeof object.x === 'number' ? object.x + width / 2 : width / 2;
    const cy = typeof object.y === 'number' ? object.y + height / 2 : height / 2;
    const rawLabel =
      typeof object.meta?.label === 'string' && object.meta.label.trim().length > 0
        ? object.meta.label.trim()
        : `${object.id ?? ''}`.trim();

    if (object.type === 'spot') {
      builderSpots.push({
        id: `${object.id ?? createLayoutSpotId()}` || createLayoutSpotId(),
        label: normalizeAutoSpotLabel(rawLabel) || formatAutoSpotLabel(builderSpots.length + 1),
        width,
        height,
        cx,
        cy,
        rotation: typeof object.rotation === 'number' ? object.rotation : 0,
        type: normalizeSpotTypeValue(object.meta?.type),
      });
      return;
    }

    if (object.type === 'entry' || object.type === 'exit') {
      gateCounters[object.type] += 1;
      builderGates.push({
        id: `${object.id ?? createLayoutGateId(object.type)}` || createLayoutGateId(object.type),
        label: rawLabel || `${object.type === 'entry' ? 'Wjazd' : 'Wyjazd'} ${gateCounters[object.type]}`,
        width,
        height,
        cx,
        cy,
        rotation: typeof object.rotation === 'number' ? object.rotation : 0,
        type: object.type,
      });
    }
  });

  return {
    width: safeWidth,
    height: safeHeight,
    spots: builderSpots,
    gates: builderGates,
  };
};

const builderToLayoutDefinition = (builder: ParkingLayoutBuilderValue): ParkingLayoutDefinition | null => {
  if (builder.spots.length === 0 && builder.gates.length === 0) {
    return null;
  }
  return {
    canvas: {
      width: roundLayoutValue(builder.width),
      height: roundLayoutValue(builder.height),
      gridSize: 1,
    },
    objects: [
      ...builder.spots.map((spot) => {
        const trimmedLabel = spot.label?.trim() ?? '';
        const meta: Record<string, unknown> = {
          type: normalizeSpotTypeValue(spot.type),
        };
        if (trimmedLabel.length > 0) {
          meta.label = trimmedLabel;
        }
        return {
          id: spot.id,
          type: 'spot' as const,
          x: roundLayoutValue(spot.cx - spot.width / 2),
          y: roundLayoutValue(spot.cy - spot.height / 2),
          width: roundLayoutValue(spot.width),
          height: roundLayoutValue(spot.height),
          rotation: normalizeLayoutRotation(spot.rotation ?? 0),
          meta,
        };
      }),
      ...builder.gates.map((gate, index) => {
        const trimmedLabel = gate.label?.trim() ?? '';
        const resolvedLabel = trimmedLabel.length > 0 ? trimmedLabel : `${gate.type === 'entry' ? 'Wjazd' : 'Wyjazd'} ${index + 1}`;
        return {
          id: gate.id,
          type: gate.type,
          x: roundLayoutValue(gate.cx - gate.width / 2),
          y: roundLayoutValue(gate.cy - gate.height / 2),
          width: roundLayoutValue(gate.width),
          height: roundLayoutValue(gate.height),
          rotation: normalizeLayoutRotation(gate.rotation ?? 0),
          meta: {
            label: resolvedLabel,
          },
        };
      }),
    ],
    meta: {
      pattern: 'custom-layout',
    },
  };
};

const tryParseJsonValue = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }
  const trimmed = value.trim();
  if (!trimmed.length) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const parseLayoutNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.length) {
      return null;
    }
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const PARKING_SPOT_TYPES: ParkingSpotType[] = ['space_regular', 'space_handi', 'space_family', 'space_disabled', 'space_ev'];
const normalizeSpotTypeValue = (value: unknown): ParkingSpotType =>
  typeof value === 'string' && PARKING_SPOT_TYPES.includes(value.trim() as ParkingSpotType) ? (value.trim() as ParkingSpotType) : 'space_regular';
const LAYOUT_GATE_TYPES = ['entry', 'exit'] as const;
const normalizeLayoutGateType = (value: unknown): 'entry' | 'exit' | null =>
  typeof value === 'string' && LAYOUT_GATE_TYPES.includes(value.trim().toLowerCase() as 'entry' | 'exit')
    ? (value.trim().toLowerCase() as 'entry' | 'exit')
    : null;
const resolveLayoutLabel = (record: Record<string, unknown>) => {
  const metaLabel =
    record.meta && typeof record.meta === 'object' && typeof (record.meta as Record<string, unknown>).label === 'string'
      ? ((record.meta as Record<string, unknown>).label as string)
      : null;
  const raw = [record.label, record.name, metaLabel].find((value): value is string => typeof value === 'string' && value.trim().length > 0);
  return raw?.trim() ?? '';
};

type ParkingLayoutBuilderSpotEntry = ParkingLayoutBuilderValue['spots'][number];
type ParkingLayoutBuilderGateEntry = ParkingLayoutBuilderValue['gates'][number];

const resolveLayoutCenter = (record: Record<string, unknown>, width: number, height: number, topLeftFallback = false) => {
  let cx = parseLayoutNumber(record.cx ?? record.center_x ?? record.centerX);
  let cy = parseLayoutNumber(record.cy ?? record.center_y ?? record.centerY);
  const fallbackX = parseLayoutNumber(record.x);
  const fallbackY = parseLayoutNumber(record.y);
  if (cx === null && fallbackX !== null) {
    cx = topLeftFallback ? fallbackX + width / 2 : fallbackX;
  }
  if (cy === null && fallbackY !== null) {
    cy = topLeftFallback ? fallbackY + height / 2 : fallbackY;
  }
  if (cx === null || cy === null) {
    return null;
  }
  return {
    cx: roundLayoutValue(cx),
    cy: roundLayoutValue(cy),
  };
};

const normalizeLayoutSpotEntry = (entry: unknown, index: number, topLeftFallback = false): ParkingLayoutBuilderSpotEntry | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const record = entry as Record<string, unknown>;
  const width = parseLayoutNumber(record.width) ?? 48;
  const height = parseLayoutNumber(record.height) ?? 96;
  const center = resolveLayoutCenter(record, width, height, topLeftFallback);
  if (!center) {
    return null;
  }
  const rotation = normalizeLayoutRotation(parseLayoutNumber(record.rotation) ?? 0);
  const rawId = record.id;
  const id =
    (typeof rawId === 'string' && rawId.trim().length > 0
      ? rawId.trim()
      : typeof rawId === 'number' && Number.isFinite(rawId)
        ? String(rawId)
        : createLayoutSpotId()) || createLayoutSpotId();
  const label = normalizeAutoSpotLabel(resolveLayoutLabel(record)) || formatAutoSpotLabel(index + 1);
  const type = normalizeSpotTypeValue(record.type ?? (record.meta as Record<string, unknown> | undefined)?.type);
  return {
    id,
    label,
    cx: center.cx,
    cy: center.cy,
    width: roundLayoutValue(width),
    height: roundLayoutValue(height),
    rotation,
    type,
  };
};

const parseLayoutSpotsArray = (value: unknown): ParkingLayoutBuilderSpotEntry[] => {
  const resolved = tryParseJsonValue(value);
  if (!Array.isArray(resolved)) {
    return [];
  }
  return resolved.map((entry, index) => normalizeLayoutSpotEntry(entry, index)).filter((entry): entry is ParkingLayoutBuilderSpotEntry => Boolean(entry));
};

const parseLayoutSpotsCollection = (value: unknown): Pick<ParkingLayoutBuilderValue, 'spots' | 'gates'> => {
  const resolved = tryParseJsonValue(value);
  if (!Array.isArray(resolved)) {
    return { spots: [], gates: [] };
  }

  const spots: ParkingLayoutBuilderValue['spots'] = [];
  const gates: ParkingLayoutBuilderValue['gates'] = [];
  const gateCounters: Record<'entry' | 'exit', number> = { entry: 0, exit: 0 };

  resolved.forEach((entry) => {
    if (!entry || typeof entry !== 'object') {
      return;
    }

    const record = entry as Record<string, unknown>;
    const gateType = normalizeLayoutGateType(record.type);
    if (gateType) {
      const parsedGate = normalizeLayoutGateEntry(record, gateCounters[gateType], gateType);
      if (parsedGate) {
        gates.push(parsedGate);
        gateCounters[gateType] += 1;
      }
      return;
    }

    const parsedSpot = normalizeLayoutSpotEntry(record, spots.length);
    if (parsedSpot) {
      spots.push(parsedSpot);
    }
  });

  return { spots, gates };
};

const normalizeLayoutGateEntry = (
  entry: unknown,
  index: number,
  forcedType?: 'entry' | 'exit',
  topLeftFallback = false,
): ParkingLayoutBuilderGateEntry | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const record = entry as Record<string, unknown>;
  const gateType = forcedType ?? normalizeLayoutGateType(record.type);
  if (!gateType) {
    return null;
  }
  const width = parseLayoutNumber(record.width) ?? 120;
  const height = parseLayoutNumber(record.height) ?? 42;
  const center = resolveLayoutCenter(record, width, height, topLeftFallback);
  if (!center) {
    return null;
  }
  const rotation = normalizeLayoutRotation(parseLayoutNumber(record.rotation) ?? 0);
  const rawId = record.id;
  const id =
    (typeof rawId === 'string' && rawId.trim().length > 0
      ? rawId.trim()
      : typeof rawId === 'number' && Number.isFinite(rawId)
        ? String(rawId)
        : createLayoutGateId(gateType)) || createLayoutGateId(gateType);
  const fallbackLabel = `${gateType === 'entry' ? 'Wjazd' : 'Wyjazd'} ${index + 1}`;
  const label = resolveLayoutLabel(record) || fallbackLabel;
  return {
    id,
    label,
    cx: center.cx,
    cy: center.cy,
    width: roundLayoutValue(width),
    height: roundLayoutValue(height),
    rotation,
    type: gateType,
  };
};

const parseLayoutGatesArray = (value: unknown, forcedType?: 'entry' | 'exit'): ParkingLayoutBuilderGateEntry[] => {
  const resolved = tryParseJsonValue(value);
  if (!Array.isArray(resolved)) {
    return [];
  }
  return resolved
    .map((entry, index) => normalizeLayoutGateEntry(entry, index, forcedType))
    .filter((entry): entry is ParkingLayoutBuilderGateEntry => Boolean(entry));
};

const parseLayoutObjectsArray = (value: unknown): Pick<ParkingLayoutBuilderValue, 'spots' | 'gates'> => {
  const resolved = tryParseJsonValue(value);
  if (!Array.isArray(resolved)) {
    return { spots: [], gates: [] };
  }

  const spots: ParkingLayoutBuilderValue['spots'] = [];
  const gates: ParkingLayoutBuilderValue['gates'] = [];
  const gateCounters: Record<'entry' | 'exit', number> = { entry: 0, exit: 0 };

  resolved.forEach((entry) => {
    if (!entry || typeof entry !== 'object') {
      return;
    }
    const record = entry as Record<string, unknown>;
    const objectType = typeof record.type === 'string' ? record.type.trim().toLowerCase() : '';
    if (objectType === 'spot') {
      const parsed = normalizeLayoutSpotEntry(record, spots.length, true);
      if (parsed) {
        spots.push(parsed);
      }
      return;
    }
    const gateType = normalizeLayoutGateType(objectType);
    if (!gateType) {
      return;
    }
    const parsed = normalizeLayoutGateEntry(record, gateCounters[gateType], gateType, true);
    if (parsed) {
      gates.push(parsed);
      gateCounters[gateType] += 1;
    }
  });

  return { spots, gates };
};

const parseLayoutSize = (value: unknown): [number, number] | null => {
  const resolved = tryParseJsonValue(value);
  if (Array.isArray(resolved) && resolved.length >= 2) {
    const width = parseLayoutNumber(resolved[0]);
    const height = parseLayoutNumber(resolved[1]);
    if (width !== null && height !== null) {
      return [Math.max(120, roundLayoutValue(width)), Math.max(120, roundLayoutValue(height))];
    }
  }
  if (resolved && typeof resolved === 'object') {
    const record = resolved as Record<string, unknown>;
    const width = parseLayoutNumber(record.width ?? record.w);
    const height = parseLayoutNumber(record.height ?? record.h);
    if (width !== null && height !== null) {
      return [Math.max(120, roundLayoutValue(width)), Math.max(120, roundLayoutValue(height))];
    }
  }
  return null;
};

const extractParkingSpotsRecord = (payload: unknown, depth = 0): Record<string, unknown> | null => {
  if (!payload || depth > 4) {
    return null;
  }
  const resolved = tryParseJsonValue(payload);
  if (Array.isArray(resolved)) {
    for (const entry of resolved) {
      const extracted = extractParkingSpotsRecord(entry, depth + 1);
      if (extracted) {
        return extracted;
      }
    }
    return null;
  }
  if (resolved && typeof resolved === 'object') {
    const record = resolved as Record<string, unknown>;
    const hasSizeCandidate = Boolean(record.size ?? record.Size ?? record.layout_size ?? record.dimensions);
    const hasSpotsCandidate = Boolean(record.spots ?? record.Spots ?? record.layout_spots ?? record.slots);
    const hasGateCandidate = Boolean(record.entries ?? record.entry ?? record.exits ?? record.exit ?? record.gates ?? record.layout_gates);
    const hasObjectsCandidate = Boolean(record.objects ?? record.layout_objects);
    if (hasSizeCandidate || hasSpotsCandidate || hasGateCandidate || hasObjectsCandidate) {
      return record;
    }
    const candidates = [record['_data_'], record._data_, record.data, record.body, record.response, record.result, record.results, record.payload];
    for (const candidate of candidates) {
      if (!candidate || candidate === resolved) {
        continue;
      }
      const extracted = extractParkingSpotsRecord(candidate, depth + 1);
      if (extracted) {
        return extracted;
      }
    }
    for (const value of Object.values(record)) {
      if (!value || value === resolved) {
        continue;
      }
      const extracted = extractParkingSpotsRecord(value, depth + 1);
      if (extracted) {
        return extracted;
      }
    }
  }
  return null;
};

const normalizeParkingLayoutBuilderValue = (payload: unknown): ParkingLayoutBuilderValue | null => {
  const source = extractParkingSpotsRecord(payload);
  if (!source) {
    return null;
  }
  const size = parseLayoutSize(source.size ?? source.Size ?? source.layout_size ?? source.dimensions);
  if (!size) {
    return null;
  }
  const objectState = parseLayoutObjectsArray(source.objects ?? source.layout_objects);
  const mixedSpotsState = parseLayoutSpotsCollection(source.spots ?? source.Spots ?? source.layout_spots ?? source.slots);
  const parsedSpots =
    objectState.spots.length > 0
      ? objectState.spots
      : mixedSpotsState.spots.length > 0
        ? mixedSpotsState.spots
        : parseLayoutSpotsArray(source.spots ?? source.Spots ?? source.layout_spots ?? source.slots);
  const parsedGates =
    objectState.gates.length > 0
      ? objectState.gates
      : mixedSpotsState.gates.length > 0
        ? mixedSpotsState.gates
      : [
          ...parseLayoutGatesArray(source.entries ?? source.entry ?? source.entrances, 'entry'),
          ...parseLayoutGatesArray(source.exits ?? source.exit ?? source.outputs, 'exit'),
          ...parseLayoutGatesArray(source.gates ?? source.layout_gates),
        ];
  return {
    width: size[0],
    height: size[1],
    spots: parsedSpots,
    gates: parsedGates,
  };
};

const buildParkingSpotsPayload = (builder: ParkingLayoutBuilderValue): ParkingSpotsRequestPayload | null => {
  if (builder.spots.length === 0 && builder.gates.length === 0) {
    return null;
  }
  return {
    size: [roundLayoutValue(builder.width), roundLayoutValue(builder.height)],
    spots: [
      ...builder.spots.map((spot, index) => ({
        id: spot.id || createLayoutSpotId(),
        label: normalizeAutoSpotLabel(spot.label ?? '') || formatAutoSpotLabel(index + 1),
        cx: roundLayoutValue(spot.cx),
        cy: roundLayoutValue(spot.cy),
        width: roundLayoutValue(spot.width),
        height: roundLayoutValue(spot.height),
        rotation: normalizeLayoutRotation(spot.rotation ?? 0),
        type: normalizeSpotTypeValue(spot.type),
      })),
      ...builder.gates.map((gate, index) => ({
        id: gate.id || createLayoutGateId(gate.type),
        label: gate.label.trim() || `${gate.type === 'entry' ? 'Wjazd' : 'Wyjazd'} ${index + 1}`,
        cx: roundLayoutValue(gate.cx),
        cy: roundLayoutValue(gate.cy),
        width: roundLayoutValue(gate.width),
        height: roundLayoutValue(gate.height),
        rotation: normalizeLayoutRotation(gate.rotation ?? 0),
        type: gate.type,
      })),
    ],
  };
};

const normalizeReferenceSpotKey = (value: string) => normalizeAutoSpotLabel(value).trim().toLowerCase();

const getAreaReferenceSpots = (area: AreaFormEntry | null | undefined, internalReferenceSpots: InternalParkingSpot[] = []): LayoutReferenceSpot[] => {
  if (!area) {
    return [];
  }
  if (area.cameraPlacement === 'external') {
    return area.cameraSpots.map((spot) => ({
      id: spot.id,
      label: spot.label,
    }));
  }
  return internalReferenceSpots.map((spot) => ({
    id: spot.id,
    label: spot.label,
    occupancy: spot.occupancy,
  }));
};

const applyReferenceSpotIdsToBuilder = (builder: ParkingLayoutBuilderValue, referenceSpots: LayoutReferenceSpot[]): ParkingLayoutBuilderValue => {
  if (!referenceSpots.length || !builder.spots.length) {
    return builder;
  }

  const findReferenceSpotForLayout = (spot: ParkingLayoutBuilderSpotEntry, index: number) => {
    const spotKeys = [spot.id, spot.label].map((value) => normalizeReferenceSpotKey(value)).filter((value) => value.length > 0);
    if (spotKeys.length > 0) {
      const byKey = referenceSpots.find((referenceSpot) => {
        const referenceKeys = [referenceSpot.id, referenceSpot.label]
          .map((value) => normalizeReferenceSpotKey(value))
          .filter((value) => value.length > 0);
        return spotKeys.some((key) => referenceKeys.includes(key));
      });
      if (byKey) {
        return byKey;
      }
    }
    return referenceSpots[index] ?? null;
  };

  const remappedSpots = builder.spots.map((spot, index) => {
    const referenceSpot = findReferenceSpotForLayout(spot, index);
    if (!referenceSpot) {
      return spot;
    }
    const referenceId = referenceSpot.id?.trim() ?? '';
    const referenceLabel = normalizeAutoSpotLabel(referenceSpot.label ?? '');
    if (!referenceId.length && !referenceLabel.length) {
      return spot;
    }
    return {
      ...spot,
      id: referenceId.length ? referenceId : spot.id,
      label: referenceLabel.length ? referenceLabel : referenceId.length ? referenceId : spot.label,
    };
  });

  return {
    ...builder,
    spots: remappedSpots,
  };
};

const applyReferenceSpotToLayoutSpot = (
  spot: ParkingLayoutBuilderSpotEntry,
  referenceSpot: LayoutReferenceSpot | null | undefined,
): ParkingLayoutBuilderSpotEntry => {
  if (!referenceSpot) {
    return spot;
  }
  const referenceId = referenceSpot.id.trim();
  const referenceLabel = normalizeAutoSpotLabel(referenceSpot.label);
  if (!referenceId.length && !referenceLabel.length) {
    return spot;
  }
  const nextLabel = referenceLabel.length ? referenceLabel : referenceId;
  return {
    ...spot,
    id: referenceId.length ? referenceId : spot.id,
    label: nextLabel.length ? nextLabel : spot.label,
  };
};

const autoAssignReferenceIdsToAddedSpots = (
  previousBuilder: ParkingLayoutBuilderValue,
  nextBuilder: ParkingLayoutBuilderValue,
  referenceSpots: LayoutReferenceSpot[],
): ParkingLayoutBuilderValue => {
  if (!referenceSpots.length || nextBuilder.spots.length <= previousBuilder.spots.length) {
    return nextBuilder;
  }

  const previousSpotIds = new Set(previousBuilder.spots.map((spot) => spot.id));
  const addedSpotIds = new Set(nextBuilder.spots.filter((spot) => !previousSpotIds.has(spot.id)).map((spot) => spot.id));
  if (!addedSpotIds.size) {
    return nextBuilder;
  }

  const usedReferenceIds = new Set(
    nextBuilder.spots
      .filter((spot) => !addedSpotIds.has(spot.id))
      .map((spot) => spot.id)
      .filter((spotId) => referenceSpots.some((referenceSpot) => referenceSpot.id === spotId)),
  );

  let hasChanges = false;
  const remappedSpots = nextBuilder.spots.map((spot) => {
    if (!addedSpotIds.has(spot.id)) {
      return spot;
    }
    const nextReferenceSpot = referenceSpots.find((referenceSpot) => !usedReferenceIds.has(referenceSpot.id));
    if (!nextReferenceSpot) {
      return spot;
    }
    usedReferenceIds.add(nextReferenceSpot.id);
    hasChanges = true;
    return applyReferenceSpotToLayoutSpot(spot, nextReferenceSpot);
  });

  return hasChanges
    ? {
        ...nextBuilder,
        spots: remappedSpots,
      }
    : nextBuilder;
};

const parseCoordinateValue = (value: string): number => {
  const parsed = Number(value);
  if (Number.isFinite(parsed)) {
    return parsed;
  }
  return 0;
};
const isValidCoordinateInput = (value: string): boolean => {
  if (!value.trim().length) {
    return false;
  }
  return Number.isFinite(Number(value));
};

const extractAssignedCameraId = (payload: unknown): string | null => {
  if (!payload) {
    return null;
  }
  const tryNormalize = (record: Record<string, unknown>) => {
    const candidate = record.cam_id ?? record.id ?? record.camId;
    if (typeof candidate === 'string' || typeof candidate === 'number') {
      const id = String(candidate).trim();
      return id.length ? id : null;
    }
    return null;
  };
  if (Array.isArray(payload)) {
    for (const entry of payload) {
      if (entry && typeof entry === 'object') {
        const normalized = tryNormalize(entry as Record<string, unknown>);
        if (normalized) {
          return normalized;
        }
      }
    }
  }
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const normalized = tryNormalize(record);
    if (normalized) {
      return normalized;
    }
    const nested = record.data ?? record._data_ ?? record.response;
    if (nested && typeof nested === 'object') {
      return extractAssignedCameraId(nested);
    }
  }
  return null;
};

type AreaCameraDetails = {
  camId: string | null;
  camType: CameraPlacement | null;
  areaId: string | number | null;
  camUrl: string | null;
  longitude: number | null;
  latitude: number | null;
  device: string | null;
  description: string | null;
};

const normalizeAreaCameraPlacement = (value: unknown): CameraPlacement | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === 'external') {
    return 'external';
  }
  if (normalized === 'internal') {
    return 'internal';
  }
  return null;
};

const normalizeAreaCameraId = (value: unknown): string | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  return null;
};

const normalizeAreaIdentifier = (value: unknown): string | number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  return null;
};

const extractAreaCameraDetails = (payload: unknown): AreaCameraDetails | null => {
  if (!payload) {
    return null;
  }
  const tryNormalize = (record: Record<string, unknown>): AreaCameraDetails | null => {
    const camId = normalizeAreaCameraId(record.cam_id ?? record.id ?? record.camId);
    const camType = normalizeAreaCameraPlacement(record.cam_type ?? record.type ?? record.camType ?? record.camera_type);
    const areaId = normalizeAreaIdentifier(record.area_id ?? record.areaId ?? record.area ?? record.areaID);
    const camUrl = typeof record.cam_url === 'string' ? record.cam_url.trim() : null;
    const longitude =
      typeof record.longitude === 'number' && Number.isFinite(record.longitude)
        ? record.longitude
        : typeof record.longitude === 'string' && record.longitude.trim().length
          ? Number(record.longitude)
          : null;
    const latitude =
      typeof record.latitude === 'number' && Number.isFinite(record.latitude)
        ? record.latitude
        : typeof record.latitude === 'string' && record.latitude.trim().length
          ? Number(record.latitude)
          : null;
    const device = typeof record.device === 'string' ? record.device.trim() : null;
    const description = typeof record.description === 'string' ? record.description.trim() : null;
    if (camId || camType || areaId !== null) {
      return {
        camId,
        camType,
        areaId,
        camUrl,
        longitude: Number.isFinite(longitude) ? Number(longitude) : null,
        latitude: Number.isFinite(latitude) ? Number(latitude) : null,
        device,
        description,
      };
    }
    return null;
  };
  if (Array.isArray(payload)) {
    for (const entry of payload) {
      if (entry && typeof entry === 'object') {
        const normalized = tryNormalize(entry as Record<string, unknown>);
        if (normalized) {
          return normalized;
        }
      }
    }
  }
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const normalized = tryNormalize(record);
    if (normalized) {
      return normalized;
    }
    const nestedCandidates = [record.data, record._data_, record.response, record.result, record.payload];
    for (const nested of nestedCandidates) {
      if (nested && typeof nested === 'object') {
        const extracted = extractAreaCameraDetails(nested);
        if (extracted) {
          return extracted;
        }
      }
    }
  }
  return null;
};

const hasCompleteExternalCameraData = (area: AreaFormEntry): boolean => {
  if (area.cameraPlacement !== 'external') {
    return true;
  }
  return (
    area.cameraUrl.trim().length > 0 &&
    isValidCoordinateInput(area.cameraLongitude) &&
    isValidCoordinateInput(area.cameraLatitude) &&
    area.cameraDevice.trim().length > 0 &&
    area.cameraDescription.trim().length > 0
  );
};

const formatPolygon = (polygon: ParkingPolygon): ParkingPolygon =>
  polygon.map(([lng, lat]) => [Number(lng.toFixed(6)), Number(lat.toFixed(6))]) as ParkingPolygon;

const clampNormalizedValue = (value: number) => Math.min(Math.max(value, 0), 1);
const formatCameraSpotValue = (value: number) => Number(clampNormalizedValue(value).toFixed(4));
const formatCameraSpotPoint = (point: CameraSpotPoint): CameraSpotPoint => [formatCameraSpotValue(point[0]), formatCameraSpotValue(point[1])];
const dedupeCameraSpotPoints = (points: CameraSpotPoint[]): CameraSpotPoint[] => {
  if (!points.length) {
    return [];
  }
  const normalized: CameraSpotPoint[] = [];
  points.forEach((point) => {
    const last = normalized[normalized.length - 1];
    if (!last || last[0] !== point[0] || last[1] !== point[1]) {
      normalized.push(point);
    }
  });
  if (normalized.length > 1) {
    const first = normalized[0];
    const last = normalized[normalized.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) {
      normalized.pop();
    }
  }
  return normalized;
};
const sanitizeCameraSpotPoints = (points: CameraSpotPoint[]): CameraSpotPoint[] => {
  if (!Array.isArray(points)) {
    return [];
  }
  const normalized = points
    .map((point) => (Array.isArray(point) && point.length >= 2 ? (point as CameraSpotPoint) : null))
    .filter((point): point is CameraSpotPoint => Boolean(point))
    .map((point) => formatCameraSpotPoint(point))
    .filter((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]));
  return dedupeCameraSpotPoints(normalized);
};
const MIN_CAMERA_SPOT_POINTS = 3;
const CAMERA_POINT_CLOSE_DISTANCE = 0.02;
const CAMERA_SVG_SCALE = 1000;
const cameraPointDistance = (a: CameraSpotPoint, b: CameraSpotPoint) => Math.hypot((a?.[0] ?? 0) - (b?.[0] ?? 0), (a?.[1] ?? 0) - (b?.[1] ?? 0));
const formatCameraSpotLabel = (index: number) => formatAutoSpotLabel(index);
const parseCameraSpotLabelIndex = (label: string): number | null => parseAutoSpotLabelIndex(label);
const getNextCameraSpotLabel = (spots: CameraSpot[]) => {
  const usedIndexes = new Set<number>();

  spots.forEach((spot, index) => {
    const parsedIndex = parseCameraSpotLabelIndex(spot.label);
    if (parsedIndex !== null) {
      usedIndexes.add(parsedIndex);
      return;
    }

    if (!spot.label.trim()) {
      usedIndexes.add(index + 1);
    }
  });

  let nextIndex = 1;
  while (usedIndexes.has(nextIndex)) {
    nextIndex += 1;
  }

  return formatCameraSpotLabel(nextIndex);
};
const calculateCameraSpotCentroid = (points: CameraSpotPoint[]): CameraSpotPoint | null => {
  if (points.length < 3) {
    return null;
  }
  let doubleArea = 0;
  let centroidX = 0;
  let centroidY = 0;
  points.forEach((point, index) => {
    const next = points[(index + 1) % points.length];
    const cross = point[0] * next[1] - next[0] * point[1];
    doubleArea += cross;
    centroidX += (point[0] + next[0]) * cross;
    centroidY += (point[1] + next[1]) * cross;
  });
  if (!doubleArea) {
    return null;
  }
  const factor = 1 / (3 * doubleArea);
  const normalizedX = clampNormalizedValue(centroidX * factor);
  const normalizedY = clampNormalizedValue(centroidY * factor);
  return [normalizedX, normalizedY];
};

const getCameraSpotBounds = (points: CameraSpotPoint[]) => {
  if (!points.length) {
    return null;
  }
  return points.reduce(
    (bounds, point) => ({
      minX: Math.min(bounds.minX, point[0]),
      maxX: Math.max(bounds.maxX, point[0]),
      minY: Math.min(bounds.minY, point[1]),
      maxY: Math.max(bounds.maxY, point[1]),
    }),
    {
      minX: points[0][0],
      maxX: points[0][0],
      minY: points[0][1],
      maxY: points[0][1],
    },
  );
};

const clampCameraSpotOffset = (points: CameraSpotPoint[], deltaX: number, deltaY: number) => {
  const bounds = getCameraSpotBounds(points);
  if (!bounds) {
    return [0, 0] as const;
  }
  return [
    Math.min(Math.max(deltaX, -bounds.minX), 1 - bounds.maxX),
    Math.min(Math.max(deltaY, -bounds.minY), 1 - bounds.maxY),
  ] as const;
};

const translateCameraSpotPoints = (points: CameraSpotPoint[], deltaX: number, deltaY: number): CameraSpotPoint[] =>
  points.map((point) => formatCameraSpotPoint([point[0] + deltaX, point[1] + deltaY] as CameraSpotPoint));

const cameraSpotPointsToSvg = (points: CameraSpotPoint[]) =>
  points.map(([x, y]) => `${(x * CAMERA_SVG_SCALE).toFixed(1)},${(y * CAMERA_SVG_SCALE).toFixed(1)}`).join(' ');
const sanitizeCameraSpots = (area: AreaFormEntry): CameraSpot[] => {
  if (area.cameraPlacement !== 'external') {
    return [];
  }
  return area.cameraSpots.flatMap((spot, index) => {
    const fallbackLabel = formatCameraSpotLabel(index + 1);
    const trimmedLabel = normalizeAutoSpotLabel(spot.label);
    const trimmedId = spot.id.trim();
    const normalizedPoints = sanitizeCameraSpotPoints(spot.points);
    if (normalizedPoints.length < 3) {
      return [];
    }
    return [
      {
        id: trimmedId || `${area.id}-spot-${index + 1}`,
        label: trimmedLabel || fallbackLabel,
        points: normalizedPoints,
      },
    ];
  });
};

const normalizeCameraPolygonEntry = (entry: unknown, index: number): CameraSpot | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const record = entry as Record<string, unknown>;
  const points = sanitizeCameraSpotPoints(Array.isArray(record.points) ? (record.points as CameraSpotPoint[]) : []);
  if (points.length < 3) {
    return null;
  }
  const rawId = typeof record.id === 'string' && record.id.trim().length ? record.id.trim() : '';
  const rawLabel = typeof record.label === 'string' && record.label.trim().length ? normalizeAutoSpotLabel(record.label) : '';
  return {
    id: rawId || `spot-${index + 1}`,
    label: rawLabel || formatCameraSpotLabel(index + 1),
    points,
  };
};

const normalizeCameraPolygonsResponse = (payload: unknown): CameraSpot[] => {
  const base =
    (payload && typeof payload === 'object' && (payload as Record<string, unknown>)._data_) ||
    (payload && typeof payload === 'object' && (payload as Record<string, unknown>).data) ||
    payload;

  const extractArray = (value: unknown): unknown[] | null => {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.length) {
        return null;
      }
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      if ('polygons' in record) {
        return extractArray(record.polygons);
      }
    }
    return null;
  };

  const arraySource = extractArray(base);
  if (!arraySource) {
    return [];
  }
  return arraySource.map((entry, index) => normalizeCameraPolygonEntry(entry, index)).filter((spot): spot is CameraSpot => Boolean(spot));
};

const resolveCameraIdentifier = (area: AreaFormEntry): string => {
  if (area.cameraPlacement === 'external') {
    return area.externalCameraId.trim();
  }
  return area.internalCameraId.trim() || area.cameraDevice.trim() || area.cameraUrl.trim();
};

const extractCameraSnapshotData = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  if ('_data_' in payload) {
    const data = (payload as Record<string, unknown>)['_data_'];
    if (typeof data === 'string' && data.trim().length > 0) {
      return data.trim();
    }
  }

  if ('data' in payload) {
    const data = (payload as Record<string, unknown>).data;
    if (typeof data === 'string' && data.trim().length > 0) {
      return data.trim();
    }
  }

  return null;
};

const normalizeCameraSnapshotSource = (raw: string): string => {
  const trimmed = raw.trim();
  if (trimmed.startsWith('data:image/')) {
    return trimmed;
  }
  const sanitized = trimmed.replace(/\s+/g, '');
  return `data:image/jpeg;base64,${sanitized}`;
};

const stepRequirements: Record<WizardStep, WizardStep[]> = {
  1: [],
  2: [1],
  3: [1, 2],
  4: [1, 2, 3],
  5: [1, 2, 3, 4],
};

const parseParkingIdentifier = (id: ParkingListItem['id']): { raw: string | number; text: string } | null => {
  if (typeof id === 'number' && Number.isFinite(id)) {
    return { raw: id, text: String(id) };
  }
  if (typeof id === 'string') {
    const trimmed = id.trim();
    if (trimmed.length > 0) {
      return { raw: trimmed, text: trimmed };
    }
  }
  return null;
};

const formatInputValue = (value: number | null): string => (typeof value === 'number' && Number.isFinite(value) ? String(value) : '');

const formatSpaceCount = (value: number | null): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

const identifiersAreEqual = (left: string | number | null, right: string | number | null) => {
  if (left === null || right === null) {
    return false;
  }
  return String(left) === String(right);
};

type AreaCameraAssignment = {
  areaId: string | number;
  camId: string;
};

const normalizeExternalCameraAssignments = (payload: unknown, requested: Array<{ area_id: string | number }>): AreaCameraAssignment[] => {
  const assignments: AreaCameraAssignment[] = [];

  const addAssignment = (areaIdRaw: unknown, camIdRaw: unknown) => {
    const camId = typeof camIdRaw === 'string' || typeof camIdRaw === 'number' ? String(camIdRaw).trim() : '';
    const areaId = typeof areaIdRaw === 'string' || typeof areaIdRaw === 'number' ? areaIdRaw : null;

    if (!camId.length || areaId === null) {
      return;
    }
    if (assignments.some((entry) => identifiersAreEqual(entry.areaId, areaId))) {
      return;
    }
    assignments.push({ areaId, camId });
  };

  const tryFromArray = (collection: unknown[], fallbackAreas: Array<{ area_id: string | number }>) => {
    collection.forEach((entry, index) => {
      if (entry === null || entry === undefined) {
        return;
      }
      if (typeof entry === 'string' || typeof entry === 'number') {
        addAssignment(fallbackAreas[index]?.area_id ?? null, entry);
        return;
      }
      if (typeof entry !== 'object') {
        return;
      }
      const record = entry as Record<string, unknown>;
      const camId = record.cam_id ?? record.id ?? record.camId ?? extractAssignedCameraId(record);
      const areaId = record.area_id ?? record.areaId ?? record.id_area ?? record.area ?? fallbackAreas[index]?.area_id ?? null;
      addAssignment(areaId, camId);
    });
  };

  const candidates: unknown[] = [];
  if (Array.isArray(payload)) {
    candidates.push(payload);
  }
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const nested = record._data_ ?? record.data ?? record.response;
    if (Array.isArray(record.cameras)) {
      candidates.push(record.cameras);
    }
    if (Array.isArray(nested)) {
      candidates.push(nested);
    } else if (nested && typeof nested === 'object') {
      const nestedRecord = nested as Record<string, unknown>;
      if (Array.isArray(nestedRecord.data)) {
        candidates.push(nestedRecord.data);
      }
      if (Array.isArray(nestedRecord._data_)) {
        candidates.push(nestedRecord._data_);
      }
    }
  }

  candidates.forEach((candidate) => {
    if (Array.isArray(candidate)) {
      tryFromArray(candidate, requested);
    }
  });

  if (!assignments.length && requested.length === 1) {
    const singleCamId = extractAssignedCameraId(payload);
    if (singleCamId) {
      addAssignment(requested[0].area_id, singleCamId);
    }
  }

  return assignments;
};

type CameraListEntry = {
  id: string;
  name: string;
  placement: CameraPlacement | null;
  camUrl?: string;
  longitude?: number;
  latitude?: number;
  device?: string;
  description?: string;
};

const normalizeCameraListResponse = (payload: unknown): CameraListEntry[] => {
  const listPayload = Array.isArray(payload)
    ? payload
    : ((payload as { data?: unknown; _data_?: unknown })?._data_ ?? (payload as { data?: unknown })?.data ?? null);

  if (!Array.isArray(listPayload)) {
    return [];
  }

  return listPayload
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const record = entry as Record<string, unknown>;
      const idCandidate = record.cam_id ?? record.id ?? record.camId;
      const nameCandidate = record.name ?? record.cam_name ?? record.label ?? record.device ?? record.description;
      const placement = normalizeAreaCameraPlacement(record.cam_type ?? record.type ?? record.camType ?? record.camera_type);
      const camUrlCandidate = record.cam_url ?? record.url ?? record.stream_url ?? record.source;
      const longitudeCandidate = record.longitude ?? record.lng ?? record.lon ?? record.long;
      const latitudeCandidate = record.latitude ?? record.lat;
      const deviceCandidate = record.device ?? record.cam_device ?? record.camName;
      const descriptionCandidate = record.description ?? record.label;
      const id = typeof idCandidate === 'string' || typeof idCandidate === 'number' ? String(idCandidate).trim() : '';
      if (!id.length) {
        return null;
      }
      const name = typeof nameCandidate === 'string' && nameCandidate.trim().length > 0 ? nameCandidate.trim() : `Kamera ${id}`;
      const cameraEntry: CameraListEntry = {
        id,
        name,
        placement,
      };
      const url = typeof camUrlCandidate === 'string' && camUrlCandidate.trim().length > 0 ? camUrlCandidate.trim() : undefined;
      const longitude = parseApiCoordinate(longitudeCandidate);
      const latitude = parseApiCoordinate(latitudeCandidate);
      const device = typeof deviceCandidate === 'string' && deviceCandidate.trim().length > 0 ? deviceCandidate.trim() : undefined;
      const description = typeof descriptionCandidate === 'string' && descriptionCandidate.trim().length > 0 ? descriptionCandidate.trim() : undefined;
      if (url) {
        cameraEntry.camUrl = url;
      }
      if (longitude !== null) {
        cameraEntry.longitude = longitude;
      }
      if (latitude !== null) {
        cameraEntry.latitude = latitude;
      }
      if (device) {
        cameraEntry.device = device;
      }
      if (description) {
        cameraEntry.description = description;
      }
      return cameraEntry;
    })
    .filter((item): item is CameraListEntry => Boolean(item));
};

const findCameraIdForExternalPayload = (
  cameras: CameraListEntry[],
  payload: {
    cam_url: string;
    device: string;
    description: string;
  },
): string | null => {
  const targetUrl = payload.cam_url.trim();
  const targetDevice = payload.device.trim();
  const matchByUrl = targetUrl.length > 0 ? cameras.find((camera) => camera.camUrl && camera.camUrl.trim() === targetUrl) : undefined;
  if (matchByUrl?.id) {
    return matchByUrl.id;
  }
  const matchByDevice = targetDevice.length > 0 ? cameras.find((camera) => camera.device && camera.device === targetDevice) : undefined;
  if (matchByDevice?.id) {
    return matchByDevice.id;
  }
  const matchByDescription =
    payload.description.trim().length > 0 ? cameras.find((camera) => camera.description && camera.description === payload.description.trim()) : undefined;
  return matchByDescription?.id ?? null;
};

const normalizeParkingBoxId = (value: unknown): string | number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return null;
};

const extractParkingBoxIdFromResponse = (payload: unknown): string | number | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const source = record['_data_'] ?? record.data;

  if (!source || typeof source !== 'object') {
    return null;
  }

  const idValue = (source as Record<string, unknown>).id;
  return normalizeParkingBoxId(idValue);
};

const parseApiCoordinate = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim().replace(',', '.');
    if (!trimmed.length) {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeApiPoint = (input: unknown): [number, number] | null => {
  if (Array.isArray(input)) {
    if (input.length < 2) {
      return null;
    }
    const lng = parseApiCoordinate(input[0]);
    const lat = parseApiCoordinate(input[1]);
    if (lng === null || lat === null) {
      return null;
    }
    return [Number(lng.toFixed(6)), Number(lat.toFixed(6))];
  }
  if (input && typeof input === 'object') {
    const record = input as Record<string, unknown>;
    const lat =
      parseApiCoordinate(record.lat) ??
      parseApiCoordinate(record.latitude) ??
      parseApiCoordinate(record.Lat) ??
      parseApiCoordinate(record.LAT) ??
      parseApiCoordinate(record.y) ??
      parseApiCoordinate(record.Y);
    const lng =
      parseApiCoordinate(record.lng) ??
      parseApiCoordinate(record.lon) ??
      parseApiCoordinate(record.longitude) ??
      parseApiCoordinate(record.long) ??
      parseApiCoordinate(record.Lng) ??
      parseApiCoordinate(record.Lon) ??
      parseApiCoordinate(record.Long) ??
      parseApiCoordinate(record.x) ??
      parseApiCoordinate(record.X);
    if (lat === null || lng === null) {
      return null;
    }
    return [Number(lng.toFixed(6)), Number(lat.toFixed(6))];
  }
  if (typeof input === 'string') {
    const parts = input.split(/[,;\s]+/).filter(Boolean);
    if (parts.length < 2) {
      return null;
    }
    const lng = parseApiCoordinate(parts[0]);
    const lat = parseApiCoordinate(parts[1]);
    if (lng === null || lat === null) {
      return null;
    }
    return [Number(lng.toFixed(6)), Number(lat.toFixed(6))];
  }
  return null;
};

const flattenPolygonPoints = (raw: unknown): unknown[] => {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed.length) {
      return [];
    }
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        return flattenPolygonPoints(parsed);
      } catch {
        const sanitized = trimmed.replace(/^\[+/, '').replace(/\]+$/, '');
        if (!sanitized.length) {
          return [];
        }
        const approxPairs = sanitized.split('],[').map((segment) => segment.replace(/[\[\]]+/g, ''));
        const approxPoints = approxPairs
          .map((segment) => segment.split(/[,;\s]+/).filter(Boolean))
          .filter((tokens) => tokens.length >= 2)
          .map((tokens) => [tokens[0], tokens[1]]);
        if (approxPoints.length) {
          return approxPoints;
        }
      }
    }
    const looseTokens = trimmed.split(/\s*\)\s*|\s*\(\s*/).filter(Boolean);
    if (looseTokens.length) {
      const fallbackPoints = looseTokens
        .map((entry) => entry.split(/[,;\s]+/).filter(Boolean))
        .filter((tokens) => tokens.length >= 2)
        .map((tokens) => [tokens[0], tokens[1]]);
      if (fallbackPoints.length) {
        return fallbackPoints;
      }
    }
    return [];
  }
  if (Array.isArray(raw)) {
    if (raw.length > 0) {
      const first = raw[0];
      if (Array.isArray(first) && first.length > 0 && Array.isArray((first as unknown[])[0])) {
        return flattenPolygonPoints(first as unknown[]);
      }
    }
    return raw;
  }
  if (raw && typeof raw === 'object') {
    const record = raw as Record<string, unknown>;
    const candidates = ['coordinates', 'polygon', 'coords', 'points', 'vertices', 'path'];
    for (const key of candidates) {
      if (Array.isArray(record[key])) {
        return flattenPolygonPoints(record[key]);
      }
    }
  }
  return [];
};

const normalizePolygonFromApi = (raw: unknown): ParkingPolygon => {
  const points = flattenPolygonPoints(raw);
  if (!Array.isArray(points) || !points.length) {
    return [];
  }
  const normalized: ParkingPolygon = [];
  points.forEach((candidate) => {
    const point = normalizeApiPoint(candidate);
    if (!point) {
      return;
    }
    const last = normalized[normalized.length - 1];
    if (!last || last[0] !== point[0] || last[1] !== point[1]) {
      normalized.push(point);
    }
  });
  if (normalized.length >= 3) {
    const first = normalized[0];
    const last = normalized[normalized.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) {
      normalized.pop();
    }
  }
  return normalized;
};

const extractParkingAreaArray = (payload: unknown, depth = 0): unknown[] | null => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (!payload || typeof payload !== 'object' || depth > 4) {
    return null;
  }
  const record = payload as Record<string, unknown>;
  const candidates = [record['_data_'], record._data_, record.data, record.area, record.areas, record.list, record.results];
  for (const candidate of candidates) {
    if (!candidate || candidate === payload) {
      continue;
    }
    const extracted = extractParkingAreaArray(candidate, depth + 1);
    if (extracted) {
      return extracted;
    }
  }
  return null;
};

const normalizeParkingAreaEntry = (entry: unknown, index: number): AreaFormEntry | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const record = entry as Record<string, unknown>;
  const polygonSource = record.polygon ?? record.geometry ?? record.coords ?? record.coordinates ?? record.points ?? record.vertices ?? record.path;
  const polygon = normalizePolygonFromApi(polygonSource);
  if (polygon.length < 3) {
    return null;
  }

  const rawName = typeof record.name === 'string' && record.name.trim().length > 0 ? record.name.trim() : `Strefa ${index + 1}`;
  const serverId = normalizeParkingBoxId(record.area_id) ?? normalizeParkingBoxId(record.id) ?? normalizeParkingBoxId(record.areaId);
  const localId = serverId !== null ? `remote-area-${serverId}` : createAreaId();

  return {
    id: localId,
    serverId,
    name: rawName,
    polygon,
    rate: createRateForm(localId, rawName),
    cameraPlacement: DEFAULT_CAMERA_PLACEMENT,
    cameraUrl: '',
    cameraLongitude: '',
    cameraLatitude: '',
    cameraDevice: '',
    cameraDescription: '',
    externalCameraId: '',
    internalCameraId: '',
    cameraSpots: [],
    internalParkingSpots: [],
  };
};

const normalizeParkingAreasResponse = (payload: unknown): AreaFormEntry[] => {
  const source = extractParkingAreaArray(payload);
  if (!source) {
    return [];
  }
  return source.map((entry, index) => normalizeParkingAreaEntry(entry, index)).filter((entry): entry is AreaFormEntry => Boolean(entry));
};

const normalizePriceString = (value: unknown): string => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return '';
};

const extractParkingPriceArray = (payload: unknown, depth = 0): unknown[] | null => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (!payload || typeof payload !== 'object' || depth > 4) {
    return null;
  }
  const record = payload as Record<string, unknown>;
  const candidates = [record['_data_'], record._data_, record.data, record.prices, record.price, record.list, record.results];
  for (const candidate of candidates) {
    if (!candidate || candidate === payload) {
      continue;
    }
    const extracted = extractParkingPriceArray(candidate, depth + 1);
    if (extracted) {
      return extracted;
    }
  }
  return null;
};

const normalizeParkingPriceEntry = (entry: unknown): ParkingPriceTemplate | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const record = entry as Record<string, unknown>;
  const idValue = normalizeParkingBoxId(record.id) ?? normalizeParkingBoxId(record.price_id);
  const name = typeof record.name === 'string' && record.name.trim().length > 0 ? record.name.trim() : null;
  if (!idValue || !name) {
    return null;
  }
  return {
    id: String(idValue),
    name,
    charging_period: normalizePriceString(record.charging_period),
    passenger_cars: normalizePriceString(record.passenger_cars),
    trucks: normalizePriceString(record.trucks),
    buses: normalizePriceString(record.buses),
    moto_bikes: normalizePriceString(record.moto_bikes),
    campers: normalizePriceString(record.campers),
    charging_station: normalizePriceString(record.charging_station),
  };
};

const normalizeParkingPriceResponse = (payload: unknown): ParkingPriceTemplate[] => {
  const source = extractParkingPriceArray(payload);
  if (!source) {
    return [];
  }
  return source.map((entry) => normalizeParkingPriceEntry(entry)).filter((entry): entry is ParkingPriceTemplate => Boolean(entry));
};

const parsePriceNumber = (value: string): number | null => {
  if (!value.trim().length) {
    return null;
  }
  const normalized = Number(value.replace(',', '.'));
  if (!Number.isFinite(normalized)) {
    return null;
  }
  return normalized;
};

const buildParkingPricePayload = (rate: RateFormState): ParkingPricePayload | null => {
  const name = rate.name.trim();
  if (!name) {
    return null;
  }
  const chargingPeriod = parsePriceNumber(rate.charging_period);
  const passengerCars = parsePriceNumber(rate.passenger_cars);
  const trucks = parsePriceNumber(rate.trucks);
  const buses = parsePriceNumber(rate.buses);
  const motoBikes = parsePriceNumber(rate.moto_bikes);
  const campers = parsePriceNumber(rate.campers);
  const chargingStation = parsePriceNumber(rate.charging_station);
  if (
    chargingPeriod === null ||
    passengerCars === null ||
    trucks === null ||
    buses === null ||
    motoBikes === null ||
    campers === null ||
    chargingStation === null
  ) {
    return null;
  }
  return {
    name,
    charging_period: chargingPeriod,
    passenger_cars: passengerCars,
    trucks,
    buses,
    moto_bikes: motoBikes,
    campers,
    charging_station: chargingStation,
  };
};

const findMatchingPriceTemplate = (templates: ParkingPriceTemplate[], payload: ParkingPricePayload): ParkingPriceTemplate | null => {
  const normalizedName = payload.name.trim().toLowerCase();
  return (
    templates.find(
      (template) =>
        template.name.trim().toLowerCase() === normalizedName &&
        template.charging_period === String(payload.charging_period) &&
        template.passenger_cars === String(payload.passenger_cars) &&
        template.trucks === String(payload.trucks) &&
        template.buses === String(payload.buses) &&
        template.moto_bikes === String(payload.moto_bikes) &&
        template.campers === String(payload.campers) &&
        template.charging_station === String(payload.charging_station),
    ) ?? null
  );
};

const describeParking = (item: ParkingListItem) => {
  const name = typeof item.name === 'string' && item.name.trim().length > 0 ? item.name.trim() : 'bez nazwy';
  const address = typeof item.address === 'string' && item.address.trim().length > 0 ? item.address.trim() : 'brak adresu';
  return { name, address };
};
export function ParkingPolygonAdmin({ parkingData }: ParkingPolygonAdminProps) {
  ensureWorkerClass();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const annotationOverlayRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const mapReadyRef = useRef(false);
  const isDrawingRef = useRef(false);
  const componentAliveRef = useRef(true);

  const { records: customRecords, upsert } = useCustomParkings();

  const [step, setStep] = useState<WizardStep>(1);
  const [recordId, setRecordId] = useState<string>(() => createRecordId());
  const [parkingForm, setParkingForm] = useState<ParkingDetailsFormState>({
    name: '',
    address: '',
    city: '',
    space_regular: '',
    space_handi: '',
    space_family: '',
    space_disabled: '',
    space_ev: '',
  });
  const [areas, setAreas] = useState<AreaFormEntry[]>([]);
  const [draftPoints, setDraftPoints] = useState<ParkingPolygon>([]);
  const [finalPolygon, setFinalPolygon] = useState<ParkingPolygon>([]);
  const [draftAreaName, setDraftAreaName] = useState('');
  const [editingAreaId, setEditingAreaId] = useState<string | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parkingBoxId, setParkingBoxId] = useState<string | number | null>(null);
  const [isSubmittingParking, setIsSubmittingParking] = useState(false);
  const [isSubmittingAreas, setIsSubmittingAreas] = useState(false);
  const [isSubmittingPricing, setIsSubmittingPricing] = useState(false);
  const [isSubmittingCamera, setIsSubmittingCamera] = useState(false);
  const [isSubmittingLayout, setIsSubmittingLayout] = useState(false);
  const [isFetchingRemoteAreas, setIsFetchingRemoteAreas] = useState(false);
  const [isFetchingLayout, setIsFetchingLayout] = useState(false);
  const [payloadCopied, setPayloadCopied] = useState(false);
  const [bulkRateSource, setBulkRateSource] = useState<string>('');
  const [bulkRateTargets, setBulkRateTargets] = useState<string[]>([]);
  const [activeCameraAreaId, setActiveCameraAreaId] = useState<string | null>(null);
  const [cameraDrawingState, setCameraDrawingState] = useState<CameraDrawingState | null>(null);
  const [cameraSpotDragState, setCameraSpotDragState] = useState<CameraSpotDragState | null>(null);
  const [cameraSnapshots, setCameraSnapshots] = useState<Record<string, CameraSnapshotCacheEntry>>({});
  const [cameraSnapshotErrors, setCameraSnapshotErrors] = useState<Record<string, string>>({});
  const [cameraSnapshotLoadingId, setCameraSnapshotLoadingId] = useState<string | null>(null);
  const [cameraPolygonsLoadingId, setCameraPolygonsLoadingId] = useState<string | null>(null);
  const [availableCameras, setAvailableCameras] = useState<CameraListEntry[]>([]);
  const [isFetchingCameras, setIsFetchingCameras] = useState(false);
  const [isFetchingAreaCameras, setIsFetchingAreaCameras] = useState(false);
  const [assigningAreaCameraId, setAssigningAreaCameraId] = useState<string | null>(null);
  const [layoutBuilder, setLayoutBuilder] = useState<ParkingLayoutBuilderValue>(() => createLayoutBuilderState());
  const [layoutAreaLocalId, setLayoutAreaLocalId] = useState<string | null>(null);
  const [layoutDrafts, setLayoutDrafts] = useState<Record<string, ParkingLayoutBuilderValue>>({});
  const [parkingListItems, setParkingListItems] = useState<ParkingListItem[]>([]);
  const [parkingListError, setParkingListError] = useState<string | null>(null);
  const [isParkingListLoading, setIsParkingListLoading] = useState(false);
  const [editingParkingMeta, setEditingParkingMeta] = useState<EditingParkingMeta | null>(null);
  const [deletingParkingId, setDeletingParkingId] = useState<string | number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDeleteState | null>(null);
  const [savedPriceTemplates, setSavedPriceTemplates] = useState<ParkingPriceTemplate[]>([]);
  const [isFetchingPriceTemplates, setIsFetchingPriceTemplates] = useState(false);
  const [priceTemplateError, setPriceTemplateError] = useState<string | null>(null);
  const [savingPriceAreaId, setSavingPriceAreaId] = useState<string | null>(null);
  const [layoutFetchError, setLayoutFetchError] = useState<string | null>(null);
  const [internalParkingSnapshot, setInternalParkingSnapshot] = useState<InternalParkingSnapshot | null>(null);
  const [internalParkingSnapshotError, setInternalParkingSnapshotError] = useState<string | null>(null);
  const [isFetchingInternalParkingSnapshot, setIsFetchingInternalParkingSnapshot] = useState(false);
  const [isAddingExternalCamera, setIsAddingExternalCamera] = useState(false);
  const [isSubmittingCameraPolygons, setIsSubmittingCameraPolygons] = useState(false);

  const layoutBuilderRef = useRef(layoutBuilder);
  useEffect(() => {
    layoutBuilderRef.current = layoutBuilder;
  }, [layoutBuilder]);
  const areasRef = useRef(areas);
  useEffect(() => {
    areasRef.current = areas;
  }, [areas]);
  const layoutDraftsRef = useRef(layoutDrafts);
  useEffect(() => {
    layoutDraftsRef.current = layoutDrafts;
  }, [layoutDrafts]);
  const layoutSelectionRef = useRef<string | null>(null);
  const fetchedCameraPolygonsRef = useRef<Set<string>>(new Set());
  const internalParkingSnapshotKeyRef = useRef<string | null>(null);
  const internalParkingSnapshotAutoFetchKeyRef = useRef<string | null>(null);

  const fetchParkingList = useCallback(async () => {
    setIsParkingListLoading(true);
    try {
      const response = await callParkingBoxApi(
        {
          action: 'get-parking-list',
        },
        { useStoredToken: true },
      );
      const parsed = normalizeParkingListResponse(response.json);
      if (!componentAliveRef.current) {
        return;
      }
      setParkingListItems(parsed);
      setParkingListError(null);
    } catch (listError) {
      if (!componentAliveRef.current) {
        return;
      }
      const message = listError instanceof Error && listError.message.length > 0 ? listError.message : 'Nie udało się pobrać listy parkingów.';
      setParkingListError(message);
    } finally {
      if (componentAliveRef.current) {
        setIsParkingListLoading(false);
      }
    }
  }, []);

  const fetchAvailableCameras = useCallback(async () => {
    setIsFetchingCameras(true);
    try {
      const response = await callParkingBoxApi(
        {
          action: 'get-cams',
        },
        { useStoredToken: true },
      );
      const normalized = normalizeCameraListResponse(response.json);
      if (componentAliveRef.current) {
        setAvailableCameras(normalized);
        setError(null);
      }
    } catch (cameraListError) {
      const message = cameraListError instanceof Error && cameraListError.message.length > 0 ? cameraListError.message : 'Nie udało się pobrać listy kamer.';
      setError(message);
    } finally {
      if (componentAliveRef.current) {
        setIsFetchingCameras(false);
      }
    }
  }, [componentAliveRef]);

  const fetchParkingPrices = useCallback(async (options: { silent?: boolean } = {}): Promise<ParkingPriceTemplate[] | null> => {
    const { silent = false } = options;
    setIsFetchingPriceTemplates(true);
    try {
      const response = await callParkingBoxApi(
        {
          action: 'get-parking-prices',
        },
        { useStoredToken: true },
      );
      const normalized = normalizeParkingPriceResponse(response.json);
      console.log('[Pricing] get-parking-prices response', { count: normalized.length, raw: response.json });
      if (!componentAliveRef.current) {
        return normalized;
      }
      setSavedPriceTemplates(normalized);
      if (normalized.length) {
        setAreas((prev) =>
          prev.map((area) => {
            if (!isRateEmpty(area.rate)) {
              return area;
            }
            const template = normalized[0];
            console.log('[Pricing] Autofill empty rate with template', {
              areaId: area.id,
              templateId: template.id,
              templateName: template.name,
            });
            return {
              ...area,
              rate: applyTemplateToRate(area.rate, template),
            };
          }),
        );
      }
      setPriceTemplateError(null);
      return normalized;
    } catch (priceError) {
      if (!componentAliveRef.current) {
        return null;
      }
      const message = priceError instanceof Error && priceError.message.length > 0 ? priceError.message : 'Nie udało się pobrać zapisanych stawek.';
      if (!silent) {
        setPriceTemplateError(message);
      }
      return null;
    } finally {
      if (componentAliveRef.current) {
        setIsFetchingPriceTemplates(false);
      }
    }
  }, []);

  const fetchAreaCameraAssignments = useCallback(
    async (areaList: AreaFormEntry[]) => {
      const targets = areaList.filter((area) => area.serverId !== null);
      if (!targets.length) {
        return;
      }
      setIsFetchingAreaCameras(true);
      const assignments: Record<
        string,
        {
          camId: string | null;
          camType: CameraPlacement | null;
          camUrl: string | null;
          longitude: number | null;
          latitude: number | null;
          device: string | null;
          description: string | null;
        }
      > = {};
      await Promise.all(
        targets.map(async (area) => {
          try {
            const response = await callParkingBoxApi(
              {
                action: 'get-area-cam',
                _data_: {
                  area_id: area.serverId,
                },
              },
              { useStoredToken: true },
            );
            const details = extractAreaCameraDetails(response.json);
            if (details) {
              const targetArea =
                details.areaId !== null
                  ? (areaList.find((candidate) => candidate.serverId !== null && identifiersAreEqual(candidate.serverId, details.areaId)) ?? area)
                  : area;
              assignments[targetArea.id] = {
                camId: details.camId,
                camType: details.camType,
                camUrl: details.camUrl,
                longitude: details.longitude,
                latitude: details.latitude,
                device: details.device,
                description: details.description,
              };
            }
          } catch (assignError) {
            console.warn('[Area cameras] Nie udało się pobrać przypisanej kamery dla strefy:', area.id, assignError);
          }
        }),
      );
      if (!componentAliveRef.current) {
        return;
      }
      if (Object.keys(assignments).length) {
        setAreas((prev) =>
          prev.map((area) => {
            const assignment = assignments[area.id];
            if (!assignment) {
              return area;
            }
            const nextPlacement: CameraPlacement = assignment.camType ?? area.cameraPlacement;
            const isExternalCamera = nextPlacement === 'external';
            return {
              ...area,
              cameraPlacement: nextPlacement,
              externalCameraId: isExternalCamera ? (assignment.camId ?? area.externalCameraId) : area.externalCameraId,
              internalCameraId: isExternalCamera ? '' : (assignment.camId ?? area.internalCameraId),
              cameraUrl: isExternalCamera ? (assignment.camUrl ?? area.cameraUrl) : area.cameraUrl,
              cameraLongitude: isExternalCamera && assignment.longitude !== null ? String(assignment.longitude) : area.cameraLongitude,
              cameraLatitude: isExternalCamera && assignment.latitude !== null ? String(assignment.latitude) : area.cameraLatitude,
              cameraDevice: isExternalCamera ? (assignment.device ?? area.cameraDevice) : area.cameraDevice,
              cameraDescription: isExternalCamera ? (assignment.description ?? area.cameraDescription) : area.cameraDescription,
            };
          }),
        );
      }
      setIsFetchingAreaCameras(false);
    },
    [componentAliveRef],
  );

  const fetchParkingAreas = useCallback(
    async (targetParkingId: string | number, options: { silent?: boolean; resetDraft?: boolean } = {}) => {
      const { silent = false, resetDraft = false } = options;
      const previousAreas = areasRef.current;
      if (resetDraft) {
        setAreas([]);
        setDraftPoints([]);
        setFinalPolygon([]);
        setDraftAreaName('');
        setEditingAreaId(null);
        setActiveCameraAreaId(null);
        setCameraDrawingState(null);
        setBulkRateSource('');
        setBulkRateTargets([]);
      }
      setIsFetchingRemoteAreas(true);
      try {
        const response = await callParkingBoxApi(
          {
            action: 'get-parking-area',
            _data_: { id: targetParkingId },
          },
          { useStoredToken: true },
        );
        if (!componentAliveRef.current) {
          return;
        }
        const normalizedAreas = normalizeParkingAreasResponse(response.json);
        const mergedAreas = normalizedAreas.map((area) => {
          const match =
            previousAreas.find((prev) => prev.serverId !== null && prev.serverId === area.serverId) ??
            previousAreas.find((prev) => prev.name.trim().toLowerCase() === area.name.trim().toLowerCase());
          if (!match) {
            return area;
          }
          return {
            ...area,
            cameraPlacement: match.cameraPlacement,
            cameraUrl: match.cameraUrl,
            cameraLongitude: match.cameraLongitude,
            cameraLatitude: match.cameraLatitude,
            cameraDevice: match.cameraDevice,
            cameraDescription: match.cameraDescription,
            internalCameraId: match.internalCameraId,
            cameraSpots: match.cameraSpots,
            internalParkingSpots: match.internalParkingSpots,
          };
        });
        setAreas(mergedAreas);
        void fetchAreaCameraAssignments(mergedAreas);
        if (!silent) {
          const suffix = mergedAreas.length === 1 ? '' : 'y';
          setFeedback(
            mergedAreas.length ? `Pobrano ${mergedAreas.length} obszar${suffix} z systemu Parking Box.` : 'Parking nie ma jeszcze zapisanych obszarów.',
          );
          setError(null);
        }
      } catch (areaError) {
        if (!componentAliveRef.current) {
          return;
        }
        const message = areaError instanceof Error && areaError.message.length > 0 ? areaError.message : 'Nie udało się pobrać obszarów parkingu.';
        setError(message);
      } finally {
        if (componentAliveRef.current) {
          setIsFetchingRemoteAreas(false);
        }
      }
    },
    [componentAliveRef, fetchAreaCameraAssignments],
  );

  const fetchInternalParkingData = useCallback(
    async (options: { force?: boolean; silent?: boolean } = {}) => {
      const { force = false, silent = false } = options;
      const targetParkingId = parkingBoxId ?? editingParkingMeta?.id ?? null;
      if (targetParkingId === null) {
        if (!silent) {
          setInternalParkingSnapshotError('Brak identyfikatora parkingu. Zapisz dane parkingu przed pobraniem miejsc z kamery wewnetrznej.');
        }
        return null;
      }

      const requestKey = String(targetParkingId);
      if (!force && internalParkingSnapshotKeyRef.current === requestKey && internalParkingSnapshot) {
        if (!silent) {
          setInternalParkingSnapshotError(null);
        }
        return internalParkingSnapshot;
      }

      setIsFetchingInternalParkingSnapshot(true);
      if (!silent) {
        setInternalParkingSnapshotError(null);
      }

      try {
        const response = await callParkingBoxApi(
          {
            action: 'get-parking',
            _data_: { id: targetParkingId },
          },
          { useStoredToken: true },
        );
        const parsed = extractInternalParkingSnapshot(response.json);
        if (!parsed) {
          throw new Error('Nie znaleziono listy miejsc dla kamery wewnetrznej w odpowiedzi get-parking.');
        }

        if (!componentAliveRef.current) {
          return parsed;
        }

        internalParkingSnapshotKeyRef.current = requestKey;
        setInternalParkingSnapshot(parsed);
        setInternalParkingSnapshotError(null);
        return parsed;
      } catch (snapshotError) {
        if (!componentAliveRef.current) {
          return null;
        }
        const message =
          snapshotError instanceof Error && snapshotError.message.length > 0
            ? snapshotError.message
            : 'Nie udalo sie pobrac danych miejsc dla kamery wewnetrznej.';
        setInternalParkingSnapshotError(message);
        if (!silent) {
          setError(message);
        }
        return null;
      } finally {
        if (componentAliveRef.current) {
          setIsFetchingInternalParkingSnapshot(false);
        }
      }
    },
    [componentAliveRef, editingParkingMeta, internalParkingSnapshot, parkingBoxId],
  );

  const fetchParkingLayout = useCallback(
    async (
      targetParkingId: string | number,
      targetAreaId: string | number,
      options: { silent?: boolean; resetOnEmpty?: boolean; localAreaId?: string | null } = {},
    ): Promise<ParkingLayoutBuilderValue | null> => {
      const { silent = false, resetOnEmpty = false, localAreaId = null } = options;
      setIsFetchingLayout(true);
      try {
        const response = await callParkingBoxApi(
          {
            action: 'get-parking-spots',
            _data_: {
              parking_id: targetParkingId,
              area_id: targetAreaId,
            },
          },
          { useStoredToken: true },
        );
        const normalized = normalizeParkingLayoutBuilderValue(response.json);
        if (!componentAliveRef.current) {
          return normalized;
        }
        if (normalized) {
          setLayoutBuilder(normalized);
          if (localAreaId) {
            setLayoutDrafts((prev) => ({
              ...prev,
              [localAreaId]: normalized,
            }));
          }
        } else if (resetOnEmpty) {
          setLayoutBuilder((prev) => ({
            ...prev,
            spots: [],
            gates: [],
          }));
        }
        setLayoutFetchError(null);
        if (!silent) {
          setFeedback(normalized ? 'Pobrano schemat parkingu z systemu Parking Box.' : 'Brak zapisanego schematu dla wybranej strefy.');
          setError(null);
        }
        return normalized;
      } catch (layoutError) {
        if (!componentAliveRef.current) {
          return null;
        }
        const message = layoutError instanceof Error && layoutError.message.length > 0 ? layoutError.message : 'Nie udało się pobrać schematu parkingu.';
        setLayoutFetchError(message);
        if (!silent) {
          setError(message);
        }
        return null;
      } finally {
        if (componentAliveRef.current) {
          setIsFetchingLayout(false);
        }
      }
    },
    [componentAliveRef],
  );

  const initialCenter = useMemo<[number, number]>(() => {
    const firstCity = parkingData.cities[0];
    const firstSpot = firstCity?.spots[0];
    return firstSpot?.coords ?? DEFAULT_CENTER;
  }, [parkingData]);

  const resolveStyleUrl = useCallback(() => {
    if (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) {
      return DARK_STYLE_URL;
    }
    return LIGHT_STYLE_URL;
  }, []);

  useEffect(() => {
    isDrawingRef.current = isDrawing;
    const map = mapRef.current;
    if (!map) {
      return;
    }
    map.getCanvas().style.cursor = isDrawing ? 'crosshair' : '';
  }, [isDrawing]);

  useEffect(() => {
    componentAliveRef.current = true;
    return () => {
      componentAliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    void fetchParkingList();
  }, [fetchParkingList]);

  useEffect(() => {
    void fetchParkingPrices({ silent: true });
  }, [fetchParkingPrices]);

  useEffect(() => {
    if (!savedPriceTemplates.length || !areas.length) {
      return;
    }
    const defaultTemplate = savedPriceTemplates[0];
    const emptyAreas = areas.filter((area) => isRateEmpty(area.rate));
    if (!emptyAreas.length) {
      return;
    }
    console.log('[Pricing] Autofill empty rates', {
      templateId: defaultTemplate.id,
      templateName: defaultTemplate.name,
      emptyAreaIds: emptyAreas.map((area) => area.id),
    });
    setAreas((prev) =>
      prev.map((area) => {
        if (!isRateEmpty(area.rate)) {
          return area;
        }
        return {
          ...area,
          rate: applyTemplateToRate(area.rate, defaultTemplate),
        };
      }),
    );
  }, [areas, savedPriceTemplates]);

  const draftCollection = useMemo(() => buildDraftFeatureCollection(draftPoints), [draftPoints]);

  const workingPolygonClosed = useMemo<ParkingPolygon>(() => {
    if (finalPolygon.length >= 3) {
      return ensureClosedPolygon(finalPolygon);
    }
    if (draftPoints.length >= 3) {
      return ensureClosedPolygon(draftPoints);
    }
    return [];
  }, [draftPoints, finalPolygon]);

  const workingStatus = useMemo<'preview' | 'final' | null>(() => {
    if (finalPolygon.length >= 3) {
      return 'final';
    }
    if (draftPoints.length >= 3) {
      return 'preview';
    }
    return null;
  }, [draftPoints, finalPolygon]);

  const workingCollection = useMemo(() => buildWorkingFeatureCollection(workingPolygonClosed, workingStatus), [workingPolygonClosed, workingStatus]);

  const canFinishDrawing = draftPoints.length >= 3;
  const hasFinalPolygon = finalPolygon.length >= 3;
  const finishButtonActive = canFinishDrawing && isDrawing;
  const finishButtonClasses = finishButtonActive
    ? 'rounded-lg border border-emerald-500 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
    : 'rounded-lg border border-slate-300 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 transition disabled:cursor-not-allowed disabled:opacity-50';
  const canSaveArea = hasFinalPolygon && draftAreaName.trim().length > 0;
  const saveAreaButtonClasses = canSaveArea
    ? 'rounded-lg border border-emerald-500 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white'
    : 'rounded-lg border border-slate-300 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 transition';

  const areasCollection = useMemo(() => buildAreasFeatureCollection(areas), [areas]);
  const savedCollection = useMemo(() => buildSavedFeatureCollection(customRecords, recordId), [customRecords, recordId]);

  const draftCollectionRef = useRef(draftCollection);
  const workingCollectionRef = useRef(workingCollection);
  const areasCollectionRef = useRef(areasCollection);
  const savedCollectionRef = useRef(savedCollection);
  const draggingVertexRef = useRef<{ index: number } | null>(null);
  const preventClickRef = useRef(false);

  useEffect(() => {
    draftCollectionRef.current = draftCollection;
    const map = mapRef.current;
    if (!map || !mapReadyRef.current) {
      return;
    }
    (map.getSource('admin-draft') as maplibregl.GeoJSONSource | undefined)?.setData(draftCollection as FeatureCollection);
  }, [draftCollection]);

  useEffect(() => {
    workingCollectionRef.current = workingCollection;
    const map = mapRef.current;
    if (!map || !mapReadyRef.current) {
      return;
    }
    (map.getSource('admin-working') as maplibregl.GeoJSONSource | undefined)?.setData(workingCollection as FeatureCollection);
    if (workingStatus === 'final') {
      focusPolygon(map, workingPolygonClosed);
    }
  }, [workingCollection, workingStatus, workingPolygonClosed]);

  useEffect(() => {
    areasCollectionRef.current = areasCollection;
    const map = mapRef.current;
    if (!map || !mapReadyRef.current) {
      return;
    }
    (map.getSource('admin-areas') as maplibregl.GeoJSONSource | undefined)?.setData(areasCollection as FeatureCollection);
  }, [areasCollection]);

  useEffect(() => {
    savedCollectionRef.current = savedCollection;
    const map = mapRef.current;
    if (!map || !mapReadyRef.current) {
      return;
    }
    (map.getSource('admin-saved') as maplibregl.GeoJSONSource | undefined)?.setData(savedCollection as FeatureCollection);
  }, [savedCollection]);
  useEffect(() => {
    if (step !== 2) {
      return;
    }

    if (!containerRef.current || mapRef.current) {
      return;
    }

    const initialStyle = resolveStyleUrl();

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: initialStyle,
      center: initialCenter,
      zoom: 6,
      attributionControl: false,
    });

    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    const ensureSources = () => {
      if (!map.getSource('admin-draft')) {
        map.addSource('admin-draft', {
          type: 'geojson',
          data: draftCollectionRef.current,
        });
      }
      if (!map.getSource('admin-working')) {
        map.addSource('admin-working', {
          type: 'geojson',
          data: workingCollectionRef.current,
        });
      }
      if (!map.getSource('admin-areas')) {
        map.addSource('admin-areas', {
          type: 'geojson',
          data: areasCollectionRef.current,
        });
      }
      if (!map.getSource('admin-saved')) {
        map.addSource('admin-saved', {
          type: 'geojson',
          data: savedCollectionRef.current,
        });
      }
    };

    const ensureLayers = () => {
      if (!map.getLayer('admin-saved-fill')) {
        map.addLayer({
          id: 'admin-saved-fill',
          type: 'fill',
          source: 'admin-saved',
          paint: {
            'fill-color': '#c4b5fd',
            'fill-opacity': 0.2,
          },
        });
      }

      if (!map.getLayer('admin-saved-outline')) {
        map.addLayer({
          id: 'admin-saved-outline',
          type: 'line',
          source: 'admin-saved',
          paint: {
            'line-color': '#7c3aed',
            'line-width': 1.5,
          },
        });
      }

      if (!map.getLayer('admin-areas-fill')) {
        map.addLayer({
          id: 'admin-areas-fill',
          type: 'fill',
          source: 'admin-areas',
          paint: {
            'fill-color': '#10b981',
            'fill-opacity': 0.35,
          },
        });
      }

      if (!map.getLayer('admin-areas-outline')) {
        map.addLayer({
          id: 'admin-areas-outline',
          type: 'line',
          source: 'admin-areas',
          paint: {
            'line-color': '#047857',
            'line-width': 2.5,
          },
        });
      }

      if (!map.getLayer('admin-working-fill')) {
        map.addLayer({
          id: 'admin-working-fill',
          type: 'fill',
          source: 'admin-working',
          paint: {
            'fill-color': ['case', ['==', ['get', 'status'], 'final'], '#3b82f6', '#0ea5e9'],
            'fill-opacity': ['case', ['==', ['get', 'status'], 'final'], 0.45, 0.25],
          },
        });
      }

      if (!map.getLayer('admin-working-outline')) {
        map.addLayer({
          id: 'admin-working-outline',
          type: 'line',
          source: 'admin-working',
          paint: {
            'line-color': ['case', ['==', ['get', 'status'], 'final'], '#1d4ed8', '#0ea5e9'],
            'line-width': 2.5,
          },
        });
      }

      if (!map.getLayer('admin-draft-line')) {
        map.addLayer({
          id: 'admin-draft-line',
          type: 'line',
          source: 'admin-draft',
          filter: ['==', ['get', 'kind'], 'line'],
          paint: {
            'line-color': '#0ea5e9',
            'line-dasharray': [0.5, 1],
            'line-width': 2,
          },
        });
      }

      if (!map.getLayer('admin-draft-points')) {
        map.addLayer({
          id: 'admin-draft-points',
          type: 'circle',
          source: 'admin-draft',
          filter: ['==', ['get', 'kind'], 'vertex'],
          paint: {
            'circle-radius': 5,
            'circle-color': '#0ea5e9',
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 1.5,
          },
        });
      }
    };

    const refreshDataLayers = () => {
      (map.getSource('admin-draft') as maplibregl.GeoJSONSource | undefined)?.setData(draftCollectionRef.current as FeatureCollection);
      (map.getSource('admin-working') as maplibregl.GeoJSONSource | undefined)?.setData(workingCollectionRef.current as FeatureCollection);
      (map.getSource('admin-areas') as maplibregl.GeoJSONSource | undefined)?.setData(areasCollectionRef.current as FeatureCollection);
      (map.getSource('admin-saved') as maplibregl.GeoJSONSource | undefined)?.setData(savedCollectionRef.current as FeatureCollection);
    };

    const handleLoad = () => {
      mapReadyRef.current = true;
      ensureSources();
      ensureLayers();
      refreshDataLayers();
    };

    if (map.loaded()) {
      handleLoad();
    } else {
      map.once('load', handleLoad);
    }

    const applyThemeStyle = () => {
      const nextStyle = resolveStyleUrl();
      if (!map || nextStyle === map.getStyle().sprite) {
        return;
      }
      map.setStyle(nextStyle);
      map.once('styledata', () => {
        if (!mapReadyRef.current) {
          return;
        }
        ensureSources();
        ensureLayers();
        refreshDataLayers();
      });
    };

    let observer: MutationObserver | null = null;
    if (typeof document !== 'undefined') {
      observer = new MutationObserver(applyThemeStyle);
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }

    const handleMapClick = (event: maplibregl.MapMouseEvent) => {
      if (!isDrawingRef.current || preventClickRef.current || draggingVertexRef.current) {
        return;
      }
      const { lng, lat } = event.lngLat;
      setDraftPoints((prev) => [...prev, [lng, lat]]);
    };

    const stopDraggingVertex = () => {
      if (!draggingVertexRef.current) {
        return;
      }
      draggingVertexRef.current = null;
      map.dragPan.enable();
      map.getCanvas().style.cursor = isDrawingRef.current ? 'crosshair' : '';
      window.setTimeout(() => {
        preventClickRef.current = false;
      }, 0);
    };

    const handleVertexMouseDown = (event: MapLayerMouseEvent) => {
      if (!isDrawingRef.current || !event.features?.length) {
        return;
      }
      event.preventDefault();
      const feature = event.features[0];
      const rawIndex = feature?.properties?.index;
      const index = typeof rawIndex === 'number' ? rawIndex : Number(rawIndex);
      if (!Number.isFinite(index)) {
        return;
      }
      preventClickRef.current = true;
      draggingVertexRef.current = { index };
      map.dragPan.disable();
      map.getCanvas().style.cursor = 'grabbing';
    };

    const handleVertexMouseMove = (event: maplibregl.MapMouseEvent) => {
      const dragInfo = draggingVertexRef.current;
      if (!dragInfo) {
        return;
      }
      event.preventDefault();
      const { lng, lat } = event.lngLat;
      setDraftPoints((prev) => {
        if (!prev.length) {
          return prev;
        }
        if (dragInfo.index < 0 || dragInfo.index >= prev.length) {
          return prev;
        }
        const next = [...prev];
        next[dragInfo.index] = [lng, lat];
        return next;
      });
    };

    const handleVertexMouseUp = () => {
      stopDraggingVertex();
    };

    map.on('click', handleMapClick);
    map.on('mousedown', 'admin-draft-points', handleVertexMouseDown);
    map.on('mousemove', handleVertexMouseMove);
    map.on('mouseup', handleVertexMouseUp);
    map.on('mouseleave', handleVertexMouseUp);

    return () => {
      observer?.disconnect();
      map.off('click', handleMapClick);
      map.off('mousedown', 'admin-draft-points', handleVertexMouseDown);
      map.off('mousemove', handleVertexMouseMove);
      map.off('mouseup', handleVertexMouseUp);
      map.off('mouseleave', handleVertexMouseUp);
      map.remove();
      mapRef.current = null;
      mapReadyRef.current = false;
      draggingVertexRef.current = null;
      preventClickRef.current = false;
    };
  }, [initialCenter, resolveStyleUrl, step]);
  const spaceFields = useMemo<Array<keyof ParkingDetailsFormState>>(() => ['space_regular', 'space_handi', 'space_family', 'space_disabled', 'space_ev'], []);
  const formLabelClass = 'flex min-h-[2.75rem] items-end leading-tight';
  const spaceFieldLabels: Partial<Record<keyof ParkingDetailsFormState, string>> = {
    space_regular: 'Miejsca parkingowe',
    space_handi: 'Miejsca dla niepełnosprawnych',
    space_family: 'Miejsca dla rodzin z dziećmi',
    space_disabled: 'Miejsca z zakazem parkowania',
    space_ev: 'Miejsca z ładowarkami do aut elektrycznych',
  };
  const getSpaceFieldLabel = (field: keyof ParkingDetailsFormState) => spaceFieldLabels[field] ?? field.replace('space_', 'Miejsca ').replace('_', ' ');
  const rateFieldLabels: Partial<Record<keyof RateFormState, string>> = {
    charging_period: 'Okres naliczania opłat',
    passenger_cars: 'Samochody osobowe',
    trucks: 'Ciężarówki',
    buses: 'Autobusy',
    moto_bikes: 'Motocykle',
    campers: 'Kampery',
    charging_station: 'Ładowarki',
  };
  const getRateFieldLabel = (field: keyof RateFormState) => rateFieldLabels[field] ?? field.replace('_', ' ');

  const pricingFields = useMemo<RateFeeField[]>(() => ['passenger_cars', 'trucks', 'buses', 'moto_bikes', 'campers', 'charging_station'], []);

  const parkingStepValid = useMemo(() => {
    if (!parkingForm.name.trim() || !parkingForm.address.trim() || !parkingForm.city.trim()) {
      return false;
    }

    const hasValidSpaces = spaceFields.every((field) => {
      const raw = parkingForm[field].trim();
      if (!raw.length) {
        return true;
      }
      const parsed = Number(raw);
      return Number.isFinite(parsed) && parsed >= 0;
    });

    if (!hasValidSpaces) {
      return false;
    }

    const hasAtLeastOneSpace = spaceFields.some((field) => {
      const raw = parkingForm[field].trim();
      if (!raw.length) {
        return false;
      }
      const parsed = Number(raw);
      return Number.isFinite(parsed) && parsed > 0;
    });

    return hasAtLeastOneSpace;
  }, [parkingForm, spaceFields]);

  const areasStepValid = useMemo(() => {
    if (!areas.length) {
      return false;
    }
    return areas.every((area) => area.name.trim().length > 0 && area.polygon.length >= 3);
  }, [areas]);

  const pricingStepValid = useMemo(() => {
    if (!areas.length) {
      console.log('[Pricing validation] Brak obszarów - krok 3 nie jest gotowy.');
      return false;
    }
    for (const area of areas) {
      if (!area.rate.name.trim() || !area.rate.charging_period.trim()) {
        console.log('[Pricing validation] Brak nazwy lub okresu naliczania w strefie:', {
          areaId: area.id,
          areaName: area.name,
          rate: area.rate,
        });
        return false;
      }
      const missingFee = pricingFields.find((field) => area.rate[field].trim().length === 0);
      if (missingFee) {
        console.log('[Pricing validation] Nieuzupełnione pole stawki:', {
          areaId: area.id,
          areaName: area.name,
          field: missingFee,
          rate: area.rate,
        });
        return false;
      }
    }
    return true;
  }, [areas, pricingFields]);

  const declaredSpaceTarget = useMemo(
    () =>
      spaceFields.reduce((total, field) => {
        const raw = parkingForm[field];
        if (!raw || !raw.length) {
          return total;
        }
        return total + parsePositiveNumber(raw);
      }, 0),
    [parkingForm, spaceFields],
  );

  const layoutStepValid = useMemo(() => {
    if (layoutBuilder.width < 120 || layoutBuilder.height < 120) {
      return false;
    }
    if (declaredSpaceTarget > 0) {
      return layoutBuilder.spots.length === declaredSpaceTarget;
    }
    return layoutBuilder.spots.length > 0;
  }, [declaredSpaceTarget, layoutBuilder]);

  useEffect(() => {
    console.log('[Schemat parkingu] Stan kreatora:', layoutBuilder);
  }, [layoutBuilder]);

  useEffect(() => {
    if (!areas.length) {
      setLayoutAreaLocalId((prev) => (prev === null ? prev : null));
      return;
    }
    setLayoutAreaLocalId((prev) => {
      if (prev && areas.some((area) => area.id === prev)) {
        return prev;
      }
      const primaryArea = areas.find((area) => area.serverId !== null) ?? areas[0] ?? null;
      return primaryArea?.id ?? null;
    });
  }, [areas]);

  useEffect(() => {
    const previousId = layoutSelectionRef.current;
    if (previousId && previousId !== layoutAreaLocalId) {
      setLayoutDrafts((prev) => ({
        ...prev,
        [previousId]: layoutBuilderRef.current,
      }));
    }
    layoutSelectionRef.current = layoutAreaLocalId;
    if (layoutAreaLocalId) {
      const cachedLayout = layoutDraftsRef.current[layoutAreaLocalId];
      setLayoutBuilder(cachedLayout ? cachedLayout : createLayoutBuilderState());
    } else {
      setLayoutBuilder(createLayoutBuilderState());
    }
    setLayoutFetchError(null);
  }, [layoutAreaLocalId]);

  useEffect(() => {
    void fetchAvailableCameras();
  }, [fetchAvailableCameras]);

  const cameraAreas = areas;
  const cameraAnnotationAreas = useMemo(() => areas.filter((area) => area.cameraPlacement === 'external'), [areas]);

  const needsCameraAnnotations = cameraAnnotationAreas.length > 0;
  const layoutStepId = 5 as WizardStep;

  const layoutTargetArea = useMemo(() => areas.find((area) => area.id === layoutAreaLocalId) ?? null, [areas, layoutAreaLocalId]);
  const layoutServerAreaId = layoutTargetArea?.serverId ?? null;
  const layoutReferenceArea = layoutTargetArea && layoutTargetArea.cameraPlacement === 'external' ? layoutTargetArea : null;
  const layoutReferenceSnapshot = layoutReferenceArea ? cameraSnapshots[layoutReferenceArea.id] : undefined;
  const layoutReferenceSnapshotSrc = layoutReferenceSnapshot?.src ?? null;
  const layoutReferenceSnapshotTimestamp = layoutReferenceSnapshot?.fetchedAt ?? null;
  const layoutReferenceHasSpots = Boolean(layoutReferenceArea?.cameraSpots.length);
  const layoutReferenceHasSnapshot = Boolean(layoutReferenceSnapshot?.src);
  const isLayoutReferenceSnapshotLoading = layoutReferenceArea ? cameraSnapshotLoadingId === layoutReferenceArea.id : false;
  const isLayoutReferencePolygonLoading = layoutReferenceArea ? cameraPolygonsLoadingId === layoutReferenceArea.id : false;
  const isLayoutReferenceLoading = isLayoutReferenceSnapshotLoading || isLayoutReferencePolygonLoading;
  const layoutReferencePreviewStyle = layoutReferenceSnapshotSrc
    ? { backgroundImage: `url(${layoutReferenceSnapshotSrc})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }
    : { backgroundColor: '#0f172a' };
  const canRefreshLayoutReferenceSnapshot = useMemo(() => {
    if (!layoutReferenceArea) {
      return false;
    }
    return resolveCameraIdentifier(layoutReferenceArea).length > 0;
  }, [layoutReferenceArea]);
  const layoutReferenceTimestampLabel = useMemo(() => {
    if (!layoutReferenceSnapshotTimestamp) {
      return '';
    }
    return new Date(layoutReferenceSnapshotTimestamp).toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }, [layoutReferenceSnapshotTimestamp]);

  const layoutParkingId = parkingBoxId ?? editingParkingMeta?.id ?? null;
  const layoutInternalReferenceSpots = useMemo(() => flattenInternalParkingSpots(internalParkingSnapshot), [internalParkingSnapshot]);
  const layoutInternalReferenceOptions = useMemo<LayoutSpotReferenceOption[]>(
    () =>
      layoutInternalReferenceSpots.map((spot) => ({
        id: spot.id,
        label: spot.label,
        occupancy: spot.occupancy,
      })),
    [layoutInternalReferenceSpots],
  );
  const layoutInternalReferenceOptionIds = useMemo(() => new Set(layoutInternalReferenceOptions.map((spot) => spot.id)), [layoutInternalReferenceOptions]);
  const isInternalLayoutReferenceEnabled = layoutTargetArea?.cameraPlacement === 'internal';

  useEffect(() => {
    const nextKey = layoutParkingId === null ? null : String(layoutParkingId);
    if (nextKey === internalParkingSnapshotKeyRef.current) {
      return;
    }
    internalParkingSnapshotKeyRef.current = nextKey;
    internalParkingSnapshotAutoFetchKeyRef.current = null;
    setInternalParkingSnapshot(null);
    setInternalParkingSnapshotError(null);
  }, [layoutParkingId]);

  useEffect(() => {
    if (step !== layoutStepId || !layoutTargetArea || layoutTargetArea.cameraPlacement !== 'internal' || layoutParkingId === null) {
      return;
    }
    const requestKey = String(layoutParkingId);
    if (internalParkingSnapshotAutoFetchKeyRef.current === requestKey) {
      return;
    }
    if (layoutInternalReferenceSpots.length > 0) {
      return;
    }
    if (isFetchingInternalParkingSnapshot) {
      return;
    }
    internalParkingSnapshotAutoFetchKeyRef.current = requestKey;
    void fetchInternalParkingData({ silent: true });
  }, [fetchInternalParkingData, isFetchingInternalParkingSnapshot, layoutInternalReferenceSpots.length, layoutParkingId, layoutStepId, layoutTargetArea, step]);

  const layoutSubmitDisabledReason = useMemo(() => {
    if (isSubmittingLayout) {
      return 'Trwa zapisywanie schematu.';
    }
    if (layoutBuilder.width < 120 || layoutBuilder.height < 120) {
      return 'Minimalny rozmiar schematu to 120x120.';
    }
    if (layoutBuilder.spots.length === 0) {
      return 'Dodaj przynajmniej jedno miejsce parkingowe.';
    }
    if (layoutParkingId === null) {
      return 'Brak identyfikatora parkingu.';
    }
    if (layoutServerAreaId === null) {
      return 'Brak identyfikatora strefy (zapisz krok 2).';
    }
    return null;
  }, [isSubmittingLayout, layoutBuilder.height, layoutBuilder.spots.length, layoutBuilder.width, layoutParkingId, layoutServerAreaId]);

  useEffect(() => {
    if (layoutSubmitDisabledReason) {
      console.log('[Parking layout] Zapis schematu niedostepny:', layoutSubmitDisabledReason);
    }
  }, [layoutSubmitDisabledReason]);

  useEffect(() => {
    if (!editingParkingMeta) {
      return;
    }
    const targetParkingId = layoutParkingId ?? editingParkingMeta.id;
    if (targetParkingId === null || layoutServerAreaId === null) {
      return;
    }
    void fetchParkingLayout(targetParkingId, layoutServerAreaId, {
      silent: true,
      resetOnEmpty: true,
      localAreaId: layoutAreaLocalId,
    });
  }, [editingParkingMeta, fetchParkingLayout, layoutAreaLocalId, layoutParkingId, layoutServerAreaId]);

  const activeCameraArea = useMemo(
    () => cameraAreas.find((area) => area.id === activeCameraAreaId) ?? cameraAreas[0] ?? null,
    [activeCameraAreaId, cameraAreas],
  );
  const canAddExternalCamera = useMemo(() => {
    if (!activeCameraArea || activeCameraArea.cameraPlacement !== 'external') {
      return false;
    }
    if (activeCameraArea.serverId === null) {
      return false;
    }
    if (!hasCompleteExternalCameraData(activeCameraArea)) {
      return false;
    }
    return true;
  }, [activeCameraArea]);
  const internalCameraOptions = useMemo(
    () => availableCameras.filter((camera) => camera.placement !== 'external'),
    [availableCameras],
  );
  const externalCameraOptions = useMemo(
    () => availableCameras.filter((camera) => camera.placement !== 'internal'),
    [availableCameras],
  );

  useEffect(() => {
    setCameraDrawingState((prev) => {
      if (!prev) {
        return prev;
      }
      if (activeCameraArea && prev.areaId === activeCameraArea.id) {
        return prev;
      }
      return null;
    });
  }, [activeCameraArea?.id]);

  useEffect(() => {
    setCameraSpotDragState((prev) => {
      if (!prev) {
        return prev;
      }
      if (activeCameraArea && prev.areaId === activeCameraArea.id) {
        return prev;
      }
      return null;
    });
  }, [activeCameraArea?.id]);

  const activeCameraIdentifier = activeCameraArea ? resolveCameraIdentifier(activeCameraArea) : '';
  const isActiveCameraExternal = activeCameraArea?.cameraPlacement === 'external';
  const activeExternalCameraOption = useMemo(() => {
    if (!activeCameraArea || activeCameraArea.cameraPlacement !== 'external' || !activeCameraArea.externalCameraId.trim()) {
      return null;
    }
    return externalCameraOptions.find((camera) => camera.id === activeCameraArea.externalCameraId.trim()) ?? null;
  }, [activeCameraArea, externalCameraOptions]);
  const activeCameraSnapshotEntry = activeCameraArea ? cameraSnapshots[activeCameraArea.id] : undefined;
  const hasMatchingCameraSnapshot =
    Boolean(activeCameraSnapshotEntry) && activeCameraSnapshotEntry?.camId === activeCameraIdentifier && Boolean(activeCameraIdentifier);
  const activeCameraSnapshotSrc = hasMatchingCameraSnapshot ? (activeCameraSnapshotEntry?.src ?? null) : null;
  const activeCameraSnapshotTimestamp = hasMatchingCameraSnapshot ? (activeCameraSnapshotEntry?.fetchedAt ?? null) : null;
  const activeCameraSnapshotError = activeCameraArea ? (cameraSnapshotErrors[activeCameraArea.id] ?? null) : null;
  const isActiveCameraSnapshotLoading = activeCameraArea ? cameraSnapshotLoadingId === activeCameraArea.id : false;
  const isActiveCameraPolygonLoading = activeCameraArea ? cameraPolygonsLoadingId === activeCameraArea.id : false;
  const isActiveCameraLoading = isActiveCameraSnapshotLoading || isActiveCameraPolygonLoading;
  const cameraPreviewStyle = activeCameraSnapshotSrc
    ? { backgroundImage: `url(${activeCameraSnapshotSrc})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }
    : { backgroundColor: '#0f172a' };
  const activeCameraDrawing = activeCameraArea && cameraDrawingState?.areaId === activeCameraArea.id ? cameraDrawingState : null;
  const cameraDrawingPreviewPoints = activeCameraDrawing
    ? [...activeCameraDrawing.points, ...(activeCameraDrawing.hoverPoint ? [activeCameraDrawing.hoverPoint] : [])]
    : [];
  const cameraDrawingCloseHint = Boolean(
    activeCameraDrawing &&
    activeCameraDrawing.hoverPoint &&
    activeCameraDrawing.points.length >= MIN_CAMERA_SPOT_POINTS &&
    cameraPointDistance(activeCameraDrawing.points[0], activeCameraDrawing.hoverPoint) <= CAMERA_POINT_CLOSE_DISTANCE,
  );
  const isCameraDrawingInProgress = Boolean(activeCameraDrawing && activeCameraDrawing.points.length > 0);
  const isCameraSpotDragInProgress = Boolean(cameraSpotDragState && activeCameraArea && cameraSpotDragState.areaId === activeCameraArea.id);

  const cameraSnapshotTimestampLabel = useMemo(() => {
    if (!activeCameraSnapshotTimestamp) {
      return '';
    }
    return new Date(activeCameraSnapshotTimestamp).toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }, [activeCameraSnapshotTimestamp]);
  const canRefreshCameraSnapshot = Boolean(
    activeCameraArea && (activeCameraArea.cameraPlacement !== 'external' || activeCameraArea.externalCameraId.trim().length > 0) && activeCameraIdentifier,
  );
  const cameraAnnotationStepValid = useMemo(() => {
    if (!needsCameraAnnotations) {
      return true;
    }
    return cameraAnnotationAreas.every((area) => area.serverId !== null && hasCompleteExternalCameraData(area) && area.cameraSpots.length > 0);
  }, [cameraAnnotationAreas, needsCameraAnnotations]);

  useEffect(() => {
    if (!needsCameraAnnotations) {
      return;
    }
    console.log('[Kamera i miejsca] Strefy kamer:', cameraAnnotationAreas);
  }, [cameraAnnotationAreas, needsCameraAnnotations]);

  useEffect(() => {
    if (!activeCameraArea) {
      return;
    }
    console.log('[Kamera i miejsca] Aktywna kamera i miejsca:', {
      id: activeCameraArea.id,
      name: activeCameraArea.name,
      cameraPlacement: activeCameraArea.cameraPlacement,
      cameraUrl: activeCameraArea.cameraUrl,
      cameraDevice: activeCameraArea.cameraDevice,
      cameraDescription: activeCameraArea.cameraDescription,
      cameraSpots: activeCameraArea.cameraSpots,
    });
  }, [activeCameraArea]);

  const fetchCameraSnapshot = useCallback(async (area: AreaFormEntry, identifier: string) => {
    setCameraSnapshotLoadingId(area.id);
    setCameraSnapshotErrors((prev) => {
      if (!prev[area.id]) {
        return prev;
      }
      const next = { ...prev };
      delete next[area.id];
      return next;
    });
    try {
      const response = await callParkingBoxApi(
        {
          action: 'get-cam-snapshot',
          _data_: {
            cam_id: identifier,
          },
        },
        { useStoredToken: true },
      );
      const snapshotData = extractCameraSnapshotData(response.json);
      if (!snapshotData) {
        throw new Error('Brak danych obrazu.');
      }
      setCameraSnapshots((prev) => ({
        ...prev,
        [area.id]: {
          camId: identifier,
          fetchedAt: Date.now(),
          src: normalizeCameraSnapshotSource(snapshotData),
        },
      }));
    } catch (snapshotError) {
      const message = snapshotError instanceof Error && snapshotError.message.length > 0 ? snapshotError.message : 'Nie udało się pobrać podglądu kamery.';
      setCameraSnapshotErrors((prev) => ({
        ...prev,
        [area.id]: message,
      }));
    } finally {
      setCameraSnapshotLoadingId((prev) => (prev === area.id ? null : prev));
    }
  }, []);

  const requestActiveCameraSnapshot = useCallback(
    async (force = false) => {
      if (!activeCameraArea) {
        return;
      }
      const requiresExternalId = activeCameraArea.cameraPlacement === 'external';
      const identifier = requiresExternalId ? activeCameraArea.externalCameraId.trim() : resolveCameraIdentifier(activeCameraArea);
      if (!identifier) {
        if (force) {
          setCameraSnapshotErrors((prev) => ({
            ...prev,
            [activeCameraArea.id]: requiresExternalId ? 'Brak cam_id kamery. Dodaj kamerę i zapisz w kroku 4.' : 'Brak identyfikatora kamery.',
          }));
        }
        setCameraSnapshots((prev) => {
          if (!prev[activeCameraArea.id]) {
            return prev;
          }
          const next = { ...prev };
          delete next[activeCameraArea.id];
          return next;
        });
        return;
      }
      if (!force) {
        const cached = cameraSnapshots[activeCameraArea.id];
        if (cached && cached.camId === identifier) {
          return;
        }
      }
      await fetchCameraSnapshot(activeCameraArea, identifier);
    },
    [activeCameraArea, cameraSnapshots, fetchCameraSnapshot],
  );

  const handleRefreshCameraSnapshot = useCallback(() => {
    void requestActiveCameraSnapshot(true);
  }, [requestActiveCameraSnapshot]);

  const handleRefreshLayoutReferenceSnapshot = useCallback(async () => {
    if (!layoutReferenceArea) {
      setError('Wybierz strefę ze schematem, aby odświeżyć podgląd kamery.');
      return;
    }
    const identifier = resolveCameraIdentifier(layoutReferenceArea);
    if (!identifier) {
      setError('Brak cam_id kamery. Najpierw dodaj kamerę zewnetrzna w kroku 4.');
      return;
    }
    await fetchCameraSnapshot(layoutReferenceArea, identifier);
  }, [fetchCameraSnapshot, layoutReferenceArea]);

  useEffect(() => {
    if (!editingParkingMeta || step !== 4) {
      return;
    }
    if (!activeCameraArea) {
      return;
    }
    const identifier = activeCameraArea.cameraPlacement === 'external' ? activeCameraArea.externalCameraId.trim() : resolveCameraIdentifier(activeCameraArea);
    if (!identifier) {
      return;
    }
    const cached = cameraSnapshots[activeCameraArea.id];
    if (cached && cached.camId === identifier) {
      return;
    }
    void requestActiveCameraSnapshot();
  }, [activeCameraArea, cameraSnapshots, editingParkingMeta, requestActiveCameraSnapshot, step]);

  const fetchLayoutCameraPolygons = useCallback(
    async (area: AreaFormEntry | null) => {
      if (!area) {
        return;
      }
      const camId = resolveCameraIdentifier(area);
      if (!camId) {
        return;
      }
      if (area.cameraPlacement === 'external' && area.cameraSpots.length > 0) {
        return;
      }
      const fetchKey = `${area.id}:${camId}`;
      if (fetchedCameraPolygonsRef.current.has(fetchKey)) {
        return;
      }
      setCameraPolygonsLoadingId(area.id);
      try {
        const response = await callParkingBoxApi(
          {
            action: 'get-cam-polygons',
            _data_: {
              cam_id: camId,
            },
          },
          { useStoredToken: true },
        );
        fetchedCameraPolygonsRef.current.add(fetchKey);
        if (area.cameraPlacement !== 'external') {
          return;
        }
        const spots = normalizeCameraPolygonsResponse(response.json);
        if (!spots.length) {
          return;
        }
        setAreas((prev) => prev.map((item) => (item.id === area.id ? { ...item, cameraSpots: spots } : item)));
      } catch (polygonError) {
        console.warn('[Camera polygons] Nie udało się pobrać poligonów kamery:', polygonError);
      } finally {
        setCameraPolygonsLoadingId((prev) => (prev === area.id ? null : prev));
      }
    },
    [setAreas],
  );

  useEffect(() => {
    if (!editingParkingMeta || step !== 4) {
      return;
    }
    if (!activeCameraArea || activeCameraArea.cameraPlacement !== 'external') {
      return;
    }
    if (activeCameraArea.cameraSpots.length > 0) {
      return;
    }
    void fetchLayoutCameraPolygons(activeCameraArea);
  }, [activeCameraArea, editingParkingMeta, fetchLayoutCameraPolygons, step]);

  useEffect(() => {
    if (!editingParkingMeta) {
      return;
    }
    cameraAnnotationAreas.forEach((area) => {
      if (area.cameraPlacement !== 'external') {
        return;
      }
      if (area.cameraSpots.length > 0) {
        return;
      }
      if (!resolveCameraIdentifier(area)) {
        return;
      }
      void fetchLayoutCameraPolygons(area);
    });
  }, [cameraAnnotationAreas, editingParkingMeta, fetchLayoutCameraPolygons]);

  useEffect(() => {
    if (!editingParkingMeta) {
      return;
    }
    areas.forEach((area) => {
      if (area.cameraPlacement !== 'internal') {
        return;
      }
      if (!resolveCameraIdentifier(area)) {
        return;
      }
      void fetchLayoutCameraPolygons(area);
    });
  }, [areas, editingParkingMeta, fetchLayoutCameraPolygons]);

  useEffect(() => {
    if (step !== layoutStepId || !layoutReferenceArea) {
      return;
    }
    if (layoutReferenceArea.cameraSpots.length > 0) {
      return;
    }
    void fetchLayoutCameraPolygons(layoutReferenceArea);
  }, [fetchLayoutCameraPolygons, layoutReferenceArea, layoutStepId, step]);

  useEffect(() => {
    if (step !== layoutStepId || !layoutReferenceArea) {
      return;
    }
    const identifier =
      layoutReferenceArea.cameraPlacement === 'external' ? layoutReferenceArea.externalCameraId.trim() : resolveCameraIdentifier(layoutReferenceArea);
    if (!identifier) {
      return;
    }
    const cached = cameraSnapshots[layoutReferenceArea.id];
    if (cached && cached.camId === identifier) {
      return;
    }
    void fetchCameraSnapshot(layoutReferenceArea, identifier);
  }, [cameraSnapshots, fetchCameraSnapshot, layoutReferenceArea, layoutStepId, step]);

  const canAccessStep = useCallback(
    (target: WizardStep) =>
      stepRequirements[target].every((required) => {
        if (required === 1) {
          return parkingStepValid;
        }
        if (required === 2) {
          return areasStepValid;
        }
        if (required === 3) {
          return pricingStepValid;
        }
        if (required === 4) {
          return cameraAnnotationStepValid;
        }
        if (required === 5) {
          return layoutStepValid;
        }
        return true;
      }),
    [areasStepValid, cameraAnnotationStepValid, layoutStepValid, parkingStepValid, pricingStepValid],
  );

  const preparedRecord = useMemo<CustomParkingRecord | null>(() => {
    if (!parkingStepValid || !areasStepValid || !pricingStepValid || !layoutStepValid) {
      return null;
    }

    const parking = {
      name: parkingForm.name.trim(),
      address: parkingForm.address.trim(),
      city: parkingForm.city.trim(),
      space_regular: parsePositiveNumber(parkingForm.space_regular),
      space_handi: parsePositiveNumber(parkingForm.space_handi),
      space_family: parsePositiveNumber(parkingForm.space_family),
      space_disabled: parsePositiveNumber(parkingForm.space_disabled),
      space_ev: parsePositiveNumber(parkingForm.space_ev),
    };

    const areaCameraPairs = areas.map((area) => {
      const trimmedName = area.name.trim();
      const trimmedUrl = area.cameraPlacement === 'external' ? area.cameraUrl.trim() : '';
      const longitude = area.cameraPlacement === 'external' ? parseCoordinateValue(area.cameraLongitude) : 0;
      const latitude = area.cameraPlacement === 'external' ? parseCoordinateValue(area.cameraLatitude) : 0;
      const device = area.cameraPlacement === 'external' ? area.cameraDevice.trim() : '';
      const description = area.cameraPlacement === 'external' ? area.cameraDescription.trim() : '';
      const normalizedCameraSpots = sanitizeCameraSpots(area);
      const areaEntry = {
        id: area.id,
        name: trimmedName,
        polygon: formatPolygon(area.polygon),
        cameraPlacement: area.cameraPlacement,
        cameraUrl: trimmedUrl,
        cameraLongitude: longitude,
        cameraLatitude: latitude,
        cameraDevice: device,
        cameraDescription: description,
        externalCameraId: area.externalCameraId,
        internalCameraId: area.internalCameraId,
        cameraSpots: normalizedCameraSpots,
      };
      const cameraEntry = {
        id: `${area.id}-camera`,
        areaId: area.id,
        cam_type: area.cameraPlacement,
        cam_id:
          area.cameraPlacement === 'internal'
            ? area.internalCameraId
            : area.externalCameraId && area.externalCameraId.length
              ? area.externalCameraId
              : undefined,
        cam_url: trimmedUrl,
        longitude,
        latitude,
        device,
        description,
        spots: normalizedCameraSpots,
      };
      return { areaEntry, cameraEntry };
    });

    const normalizedAreas = areaCameraPairs.map((entry) => entry.areaEntry);
    const cameras = areaCameraPairs.map((entry) => entry.cameraEntry);

    const pricing = areas.map((area) => ({
      id: area.rate.id,
      areaId: area.id,
      name: area.rate.name.trim(),
      charging_period: area.rate.charging_period.trim(),
      passenger_cars: parsePositiveNumber(area.rate.passenger_cars),
      trucks: parsePositiveNumber(area.rate.trucks),
      buses: parsePositiveNumber(area.rate.buses),
      moto_bikes: parsePositiveNumber(area.rate.moto_bikes),
      campers: parsePositiveNumber(area.rate.campers),
      charging_station: parsePositiveNumber(area.rate.charging_station),
    }));

    const layoutDefinition = builderToLayoutDefinition(layoutBuilder);
    if (!layoutDefinition) {
      return null;
    }

    return {
      id: recordId,
      parking,
      area: normalizedAreas,
      tb_parking_type: pricing,
      tb_cameras: cameras,
      layout: layoutDefinition,
    };
  }, [areas, areasStepValid, cameraAnnotationStepValid, layoutBuilder, layoutStepValid, parkingForm, parkingStepValid, pricingStepValid, recordId]);

  const preparedPayload = useMemo(() => {
    if (!preparedRecord) {
      return null;
    }
    return {
      parking: preparedRecord.parking,
      area: preparedRecord.area.map((entry) => {
        const {
          id: _ignored,
          cameraPlacement: _cameraPlacement,
          cameraUrl: _cameraUrl,
          cameraLongitude: _cameraLongitude,
          cameraLatitude: _cameraLatitude,
          cameraDevice: _cameraDevice,
          cameraDescription: _cameraDescription,
          externalCameraId: _externalCameraId,
          internalCameraId: _internalCameraId,
          cameraSpots: _cameraSpots,
          ...rest
        } = entry as unknown as AreaFormEntry;
        return rest;
      }),
      tb_parking_type: preparedRecord.tb_parking_type.map(({ id: _id, areaId: _areaId, ...rest }) => rest),
      tb_cameras: preparedRecord.tb_cameras.map(({ id: _cameraId, areaId: _cameraAreaId, ...rest }) => rest),
      layout: preparedRecord.layout,
    };
  }, [preparedRecord]);

  const payloadJson = useMemo(() => (preparedPayload ? JSON.stringify(preparedPayload, null, 2) : ''), [preparedPayload]);

  useEffect(() => {
    setPayloadCopied(false);
  }, [payloadJson]);

  useEffect(() => {
    if (bulkRateSource && !areas.some((area) => area.id === bulkRateSource)) {
      setBulkRateSource('');
    }
    setBulkRateTargets((prev) => {
      const validIds = new Set(areas.map((area) => area.id));
      const filtered = prev.filter((targetId) => targetId !== bulkRateSource && validIds.has(targetId));
      if (filtered.length === prev.length && filtered.every((value, index) => value === prev[index])) {
        return prev;
      }
      return filtered;
    });
  }, [areas, bulkRateSource]);

  useEffect(() => {
    if (!cameraAreas.length) {
      setActiveCameraAreaId(null);
      setCameraDrawingState(null);
      setCameraSpotDragState(null);
      return;
    }
    setActiveCameraAreaId((prev) => {
      if (prev && cameraAreas.some((area) => area.id === prev)) {
        return prev;
      }
      return cameraAreas[0]?.id ?? null;
    });
  }, [cameraAreas]);

  useEffect(() => {
    if (step > layoutStepId) {
      setStep(layoutStepId);
    }
  }, [layoutStepId, step]);

  useEffect(() => {
    setCameraDrawingState((prev) => {
      if (!prev) {
        return prev;
      }
      if (!activeCameraArea || prev.areaId !== activeCameraArea.id || activeCameraArea.cameraPlacement !== 'external') {
        return null;
      }
      return prev;
    });
  }, [activeCameraArea]);
  const startDrawing = () => {
    setDraftPoints([]);
    setFinalPolygon([]);
    setIsDrawing(true);
    setEditingAreaId(null);
    setDraftAreaName('');
    setFeedback('Tryb rysowania aktywny. Klikaj na mapie, aby dodawać wierzcholki.');
    setError(null);
  };

  const finishDrawing = () => {
    if (draftPoints.length < 3) {
      setError('Poligon musi miec co najmniej trzy wierzcholki.');
      return;
    }
    setFinalPolygon(draftPoints);
    setIsDrawing(false);
    setFeedback('Poligon zamkniety. Nazwij obszar i zapisz go do listy.');
    setError(null);
  };

  const cancelDrawing = () => {
    setDraftPoints([]);
    setFinalPolygon([]);
    setIsDrawing(false);
    setDraftAreaName('');
    setEditingAreaId(null);
    setFeedback('Rysowanie przerwane.');
    setError(null);
  };

  const undoLastPoint = () => {
    setDraftPoints((prev) => prev.slice(0, -1));
    if (draftPoints.length <= 3) {
      setFinalPolygon([]);
    }
  };

  const resumeEditing = () => {
    if (!hasFinalPolygon) {
      return;
    }
    setDraftPoints(finalPolygon);
    setFinalPolygon([]);
    setIsDrawing(true);
    setFeedback('Edycja poligonu. Dodaj punkty i zakoncz rysowanie.');
  };

  const saveArea = () => {
    if (!hasFinalPolygon) {
      if (canFinishDrawing) {
        setError('Najpierw zakoncz rysowanie.');
      } else {
        setError('Narysuj i zakoncz poligon, aby zapisać obszar.');
      }
      return;
    }
    if (!draftAreaName.trim()) {
      setError('Podaj nazwe obszaru.');
      return;
    }
    const baseAreaId = editingAreaId ?? createAreaId();
    const normalizedPolygon = formatPolygon(finalPolygon);
    const existing = areas.find((area) => area.id === baseAreaId);
    const rate = createRateForm(
      baseAreaId,
      draftAreaName.trim(),
      existing
        ? {
            ...existing.rate,
            name: existing.rate.name || draftAreaName.trim(),
          }
        : undefined,
    );

    const nextEntry: AreaFormEntry = {
      id: baseAreaId,
      serverId: existing?.serverId ?? null,
      name: draftAreaName.trim(),
      polygon: normalizedPolygon,
      rate,
      cameraPlacement: existing?.cameraPlacement ?? DEFAULT_CAMERA_PLACEMENT,
      cameraUrl: existing?.cameraUrl ?? '',
      cameraLongitude: existing?.cameraLongitude ?? '',
      cameraLatitude: existing?.cameraLatitude ?? '',
      cameraDevice: existing?.cameraDevice ?? '',
      cameraDescription: existing?.cameraDescription ?? '',
      externalCameraId: existing?.externalCameraId ?? '',
      internalCameraId: existing?.internalCameraId ?? '',
      cameraSpots: existing?.cameraSpots ?? [],
      internalParkingSpots: existing?.internalParkingSpots ?? [],
    };

    setAreas((prev) => {
      const clone = [...prev];
      const index = clone.findIndex((area) => area.id === baseAreaId);
      if (index === -1) {
        clone.push(nextEntry);
      } else {
        clone[index] = {
          ...nextEntry,
          rate: {
            ...rate,
            id: clone[index].rate.id,
          },
        };
      }
      return clone;
    });

    setDraftPoints([]);
    setFinalPolygon([]);
    setDraftAreaName('');
    setEditingAreaId(null);
    setIsDrawing(false);
    setFeedback(existing ? 'Zaktualizowano obszar.' : 'Dodano obszar do listy.');
    setError(null);
  };

  const handleEditArea = (areaId: string) => {
    const match = areas.find((area) => area.id === areaId);
    if (!match) {
      return;
    }
    setEditingAreaId(areaId);
    setDraftAreaName(match.name);
    setDraftPoints(match.polygon);
    setFinalPolygon([]);
    setIsDrawing(true);
    setFeedback(`Edytujesz obszar ${match.name}. Zmien wierzcholki i zakoncz rysowanie, aby zapisać.`);
    setError(null);
    const map = mapRef.current;
    if (map && mapReadyRef.current) {
      focusPolygon(map, ensureClosedPolygon(match.polygon));
    }
  };

  const removeArea = (areaId: string) => {
    setAreas((prev) => prev.filter((area) => area.id !== areaId));
  };

  const focusArea = (area: AreaFormEntry) => {
    const map = mapRef.current;
    if (!map || !mapReadyRef.current) {
      return;
    }
    focusPolygon(map, ensureClosedPolygon(area.polygon));
  };

  const handleRateChange = (areaId: string, field: keyof RateFormState, value: string) => {
    setAreas((prev) =>
      prev.map((area) => {
        if (area.id !== areaId) {
          return area;
        }
        return {
          ...area,
          rate: {
            ...area.rate,
            templateId: null,
            [field]: value,
          },
        };
      }),
    );
  };

  const handleCameraPlacementChange = (areaId: string, isExternal: boolean) => {
    setAreas((prev) =>
      prev.map((area) => {
        if (area.id !== areaId) {
          return area;
        }
        const cameraPlacement: CameraPlacement = isExternal ? 'external' : 'internal';
        return {
          ...area,
          cameraPlacement,
          cameraUrl: cameraPlacement === 'external' ? area.cameraUrl : '',
          cameraLongitude: cameraPlacement === 'external' ? area.cameraLongitude : '',
          cameraLatitude: cameraPlacement === 'external' ? area.cameraLatitude : '',
          cameraDevice: cameraPlacement === 'external' ? area.cameraDevice : '',
          cameraDescription: cameraPlacement === 'external' ? area.cameraDescription : '',
          externalCameraId: cameraPlacement === 'external' ? area.externalCameraId : '',
          internalCameraId: cameraPlacement === 'internal' ? area.internalCameraId : '',
          cameraSpots: cameraPlacement === 'external' ? area.cameraSpots : [],
          internalParkingSpots: cameraPlacement === 'internal' ? area.internalParkingSpots : [],
        };
      }),
    );
    if (!isExternal && activeCameraAreaId === areaId) {
      setCameraDrawingState(null);
      setCameraSpotDragState(null);
    }
  };

  const handleCameraUrlChange = (areaId: string, value: string) => {
    setAreas((prev) =>
      prev.map((area) => {
        if (area.id !== areaId) {
          return area;
        }
        return {
          ...area,
          cameraUrl: value,
        };
      }),
    );
  };

  const handleCameraDetailsChange = (areaId: string, field: 'cameraLongitude' | 'cameraLatitude' | 'cameraDevice' | 'cameraDescription', value: string) => {
    setAreas((prev) =>
      prev.map((area) => {
        if (area.id !== areaId) {
          return area;
        }
        return {
          ...area,
          [field]: value,
        };
      }),
    );
  };

  const handleAddExternalCamera = useCallback(async () => {
    if (!activeCameraArea) {
      setError('Wybierz strefę kamer, aby dodać kamerę.');
      return;
    }
    if (activeCameraArea.cameraPlacement !== 'external') {
      setError('Dodawanie dotyczy tylko kamer zewnetrznych.');
      return;
    }
    if (!hasCompleteExternalCameraData(activeCameraArea)) {
      setError('Uzupełnij wszystkie dane kamery, aby ją dodać.');
      return;
    }
    if (activeCameraArea.serverId === null) {
      setError('Brak identyfikatora strefy. Cofnij się do kroku 2 i zapisz obszary.');
      setStep(2);
      return;
    }
    const payload = {
      area_id: activeCameraArea.serverId,
      cam_url: activeCameraArea.cameraUrl.trim(),
      longitude: parseCoordinateValue(activeCameraArea.cameraLongitude),
      latitude: parseCoordinateValue(activeCameraArea.cameraLatitude),
      device: activeCameraArea.cameraDevice.trim(),
      description: activeCameraArea.cameraDescription.trim(),
    };
    setIsAddingExternalCamera(true);
    try {
      await callParkingBoxApi(
        {
          action: 'add-external-cam',
          _data_: payload,
        },
        { useStoredToken: true },
      );
      const cameraListResponse = await callParkingBoxApi(
        {
          action: 'get-cams',
        },
        { useStoredToken: true },
      );
      const normalizedCameras = normalizeCameraListResponse(cameraListResponse.json);
      const camId = findCameraIdForExternalPayload(normalizedCameras, payload);
      if (!camId) {
        throw new Error('Serwer nie zwrócił identyfikatora kamery dla podanego URL.');
      }
      await callParkingBoxApi(
        {
          action: 'add-area-cam',
          _data_: {
            area_id: activeCameraArea.serverId,
            cam_id: camId,
          },
        },
        { useStoredToken: true },
      );
      setAreas((prev) => prev.map((area) => (area.id === activeCameraArea.id ? { ...area, externalCameraId: camId } : area)));
      const areaWithCamId: AreaFormEntry = { ...activeCameraArea, externalCameraId: camId };
      await fetchCameraSnapshot(activeCameraArea, camId);
      if (areaWithCamId.cameraSpots.length > 0) {
        try {
          await callParkingBoxApi(
            {
              action: 'add-cam-polygons',
              _data_: {
                cam_id: camId,
                polygons: sanitizeCameraSpots(areaWithCamId),
              },
            },
            { useStoredToken: true },
          );
        } catch (polygonError) {
          console.warn('[External camera] Nie udało się zapisać poligonów kamery:', polygonError);
        }
      }
      setFeedback('Dodano kamerę zewnetrzna i powiazano ze strefa.');
      setError(null);
    } catch (addError) {
      const message = addError instanceof Error && addError.message.length > 0 ? addError.message : 'Nie udało się dodać kamery zewnętrznej.';
      setError(message);
    } finally {
      setIsAddingExternalCamera(false);
    }
  }, [activeCameraArea, fetchCameraSnapshot]);

  const handleAssignInternalCamera = useCallback(
    async (areaId: string) => {
      const area = areas.find((entry) => entry.id === areaId);
      if (!area) {
        return;
      }
      if (area.cameraPlacement === 'external') {
        return;
      }
      if (!area.internalCameraId.trim()) {
        setError('Wybierz kamerę, aby przypisać ją do strefy.');
        return;
      }
      if (area.serverId === null) {
        setError('Zapisz obszary w Parking Box, aby przypisać kamerę do strefy.');
        setStep(2);
        return;
      }
      setAssigningAreaCameraId(area.id);
      try {
        await callParkingBoxApi(
          {
            action: 'add-area-cam',
            _data_: {
              area_id: area.serverId,
              cam_id: area.internalCameraId,
            },
          },
          { useStoredToken: true },
        );
        void fetchLayoutCameraPolygons(area);
        setFeedback(`Przypisano kamerę do strefy ${area.name}.`);
        setError(null);
      } catch (assignError) {
        const message = assignError instanceof Error && assignError.message.length > 0 ? assignError.message : 'Nie udało się przypisać kamery do strefy.';
        setError(message);
      } finally {
        setAssigningAreaCameraId((prev) => (prev === area.id ? null : prev));
      }
    },
    [areas, fetchLayoutCameraPolygons, setStep],
  );

  const handleInternalCameraChange = (areaId: string, cameraId: string) => {
    setAreas((prev) =>
      prev.map((area) => {
        if (area.id !== areaId) {
          return area;
        }
        return {
          ...area,
          internalCameraId: cameraId,
        };
      }),
    );
  };

  const handleExternalCameraChange = (areaId: string, cameraId: string) => {
    const selectedCamera = externalCameraOptions.find((camera) => camera.id === cameraId) ?? null;
    setAreas((prev) =>
      prev.map((area) => {
        if (area.id !== areaId) {
          return area;
        }
        if (!cameraId) {
          return {
            ...area,
            externalCameraId: '',
          };
        }
        return {
          ...area,
          externalCameraId: cameraId,
          cameraUrl: selectedCamera?.camUrl ?? area.cameraUrl,
          cameraLongitude: selectedCamera?.longitude !== undefined ? String(selectedCamera.longitude) : area.cameraLongitude,
          cameraLatitude: selectedCamera?.latitude !== undefined ? String(selectedCamera.latitude) : area.cameraLatitude,
          cameraDevice: selectedCamera?.device ?? area.cameraDevice,
          cameraDescription: selectedCamera?.description ?? area.cameraDescription,
        };
      }),
    );
    setCameraSnapshots((prev) => {
      if (!prev[areaId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[areaId];
      return next;
    });
    setCameraSnapshotErrors((prev) => {
      if (!prev[areaId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[areaId];
      return next;
    });
  };

  const addCameraSpotToArea = (areaId: string, polygonPoints: CameraSpotPoint[]) => {
    const normalizedPoints = sanitizeCameraSpotPoints(polygonPoints);
    if (normalizedPoints.length < MIN_CAMERA_SPOT_POINTS) {
      return;
    }
    setAreas((prev) =>
      prev.map((area) => {
        if (area.id !== areaId) {
          return area;
        }
        const nextLabel = getNextCameraSpotLabel(area.cameraSpots);
        return {
          ...area,
          cameraSpots: [
            ...area.cameraSpots,
            {
              id: createCameraSpotId(),
              label: nextLabel,
              points: normalizedPoints,
            },
          ],
        };
      }),
    );
  };

  const updateCameraSpotLabel = (areaId: string, spotId: string, label: string) => {
    setAreas((prev) =>
      prev.map((area) => {
        if (area.id !== areaId) {
          return area;
        }
        return {
          ...area,
          cameraSpots: area.cameraSpots.map((spot) => {
            if (spot.id !== spotId) {
              return spot;
            }
            return {
              ...spot,
              label,
            };
          }),
        };
      }),
    );
  };

  const updateCameraSpotPoints = (areaId: string, spotId: string, points: CameraSpotPoint[]) => {
    const normalizedPoints = sanitizeCameraSpotPoints(points);
    if (normalizedPoints.length < MIN_CAMERA_SPOT_POINTS) {
      return;
    }
    setAreas((prev) =>
      prev.map((area) => {
        if (area.id !== areaId) {
          return area;
        }
        return {
          ...area,
          cameraSpots: area.cameraSpots.map((spot) => {
            if (spot.id !== spotId) {
              return spot;
            }
            return {
              ...spot,
              points: normalizedPoints,
            };
          }),
        };
      }),
    );
  };

  const removeCameraSpot = (areaId: string, spotId: string) => {
    setAreas((prev) =>
      prev.map((area) => {
        if (area.id !== areaId) {
          return area;
        }
        return {
          ...area,
          cameraSpots: area.cameraSpots.filter((spot) => spot.id !== spotId),
        };
      }),
    );
  };

  const clearCameraSpots = (areaId: string) => {
    setAreas((prev) =>
      prev.map((area) => {
        if (area.id !== areaId) {
          return area;
        }
        return {
          ...area,
          cameraSpots: [],
        };
      }),
    );
  };

  const getPointerRelativePosition = (element: HTMLDivElement | null, clientX: number, clientY: number): CameraSpotPoint | null => {
    if (!element) {
      return null;
    }
    const bounds = element.getBoundingClientRect();
    if (!bounds.width || !bounds.height) {
      return null;
    }
    const x = clampNormalizedValue((clientX - bounds.left) / bounds.width);
    const y = clampNormalizedValue((clientY - bounds.top) / bounds.height);
    return [x, y];
  };

  const resetCameraDrawingHover = () => {
    setCameraDrawingState((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        hoverPoint: null,
      };
    });
  };

  const handleCameraSpotPointerDown = (event: ReactPointerEvent<SVGPolygonElement>, spotId: string) => {
    if (!activeCameraArea || activeCameraArea.cameraPlacement !== 'external' || isCameraDrawingInProgress) {
      return;
    }
    const point = getPointerRelativePosition(annotationOverlayRef.current, event.clientX, event.clientY);
    if (!point) {
      return;
    }
    const spot = activeCameraArea.cameraSpots.find((entry) => entry.id === spotId);
    if (!spot) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setCameraSpotDragState({
      areaId: activeCameraArea.id,
      spotId,
      pointerId: event.pointerId,
      startPointer: point,
      originPoints: spot.points.map((spotPoint) => [spotPoint[0], spotPoint[1]] as CameraSpotPoint),
    });
    try {
      annotationOverlayRef.current?.setPointerCapture(event.pointerId);
    } catch {
      // Ignore browsers that reject pointer capture for this element.
    }
  };

  const handleAnnotationPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!activeCameraArea || activeCameraArea.cameraPlacement !== 'external') {
      return;
    }
    const point = getPointerRelativePosition(event.currentTarget, event.clientX, event.clientY);
    if (!point) {
      return;
    }
    event.preventDefault();
    setCameraDrawingState((prev) => {
      if (!prev || prev.areaId !== activeCameraArea.id || prev.points.length === 0) {
        return {
          areaId: activeCameraArea.id,
          points: [point],
          hoverPoint: null,
        };
      }
      if (prev.areaId !== activeCameraArea.id) {
        return {
          areaId: activeCameraArea.id,
          points: [point],
          hoverPoint: null,
        };
      }
      const canClose = prev.points.length >= MIN_CAMERA_SPOT_POINTS;
      const closeToStart = canClose && cameraPointDistance(prev.points[0], point) <= CAMERA_POINT_CLOSE_DISTANCE;
      if (closeToStart) {
        addCameraSpotToArea(prev.areaId, prev.points);
        return null;
      }
      return {
        ...prev,
        points: [...prev.points, point],
        hoverPoint: null,
      };
    });
  };

  const handleAnnotationPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (cameraSpotDragState && activeCameraArea && cameraSpotDragState.areaId === activeCameraArea.id) {
      const point = getPointerRelativePosition(event.currentTarget, event.clientX, event.clientY);
      if (!point) {
        return;
      }
      event.preventDefault();
      const deltaX = point[0] - cameraSpotDragState.startPointer[0];
      const deltaY = point[1] - cameraSpotDragState.startPointer[1];
      const [nextDeltaX, nextDeltaY] = clampCameraSpotOffset(cameraSpotDragState.originPoints, deltaX, deltaY);
      updateCameraSpotPoints(
        cameraSpotDragState.areaId,
        cameraSpotDragState.spotId,
        translateCameraSpotPoints(cameraSpotDragState.originPoints, nextDeltaX, nextDeltaY),
      );
      return;
    }

    if (!cameraDrawingState || !activeCameraArea || cameraDrawingState.areaId !== activeCameraArea.id) {
      return;
    }
    const point = getPointerRelativePosition(event.currentTarget, event.clientX, event.clientY);
    if (!point) {
      resetCameraDrawingHover();
      return;
    }
    event.preventDefault();
    setCameraDrawingState((prev) => {
      if (!prev || prev.areaId !== activeCameraArea.id) {
        return prev;
      }
      return {
        ...prev,
        hoverPoint: point,
      };
    });
  };

  const handleAnnotationPointerLeave = () => {
    if (cameraSpotDragState) {
      return;
    }
    resetCameraDrawingHover();
  };

  const handleAnnotationPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!cameraSpotDragState || event.pointerId !== cameraSpotDragState.pointerId) {
      return;
    }
    event.preventDefault();
    try {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    } catch {
      // Ignore browsers that reject pointer capture release.
    }
    setCameraSpotDragState(null);
  };

  const handleAnnotationPointerCancel = (event?: ReactPointerEvent<HTMLDivElement>) => {
    if (cameraSpotDragState && event && event.pointerId === cameraSpotDragState.pointerId) {
      try {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      } catch {
        // Ignore browsers that reject pointer capture release.
      }
    }
    setCameraSpotDragState(null);
    resetCameraDrawingHover();
  };

  const handleCancelCameraDrawing = () => {
    setCameraDrawingState((prev) => {
      if (!prev || !activeCameraArea || prev.areaId !== activeCameraArea.id || prev.points.length === 0) {
        return prev;
      }
      return null;
    });
  };

  const toggleBulkRateTarget = (areaId: string) => {
    setBulkRateTargets((prev) => {
      if (prev.includes(areaId)) {
        return prev.filter((candidate) => candidate !== areaId);
      }
      return [...prev, areaId];
    });
  };

  const applyBulkRates = () => {
    const sourceArea = areas.find((area) => area.id === bulkRateSource);
    if (!sourceArea) {
      setError('Wybierz strefę z ustawionymi stawkami.');
      return;
    }
    const targets = bulkRateTargets.filter((targetId) => targetId !== bulkRateSource);
    if (!targets.length) {
      setError('Zaznacz przynajmniej jedną strefę docelową.');
      return;
    }
    const targetSet = new Set(targets);
    setAreas((prev) =>
      prev.map((area) => {
        if (!targetSet.has(area.id)) {
          return area;
        }
        const template = sourceArea.rate;
        return {
          ...area,
          rate: {
            ...area.rate,
            templateId: template.templateId,
            name: template.name,
            charging_period: template.charging_period,
            passenger_cars: template.passenger_cars,
            trucks: template.trucks,
            buses: template.buses,
            moto_bikes: template.moto_bikes,
            campers: template.campers,
            charging_station: template.charging_station,
          },
        };
      }),
    );
    setFeedback(`Skopiowano stawki z ${sourceArea.name} do ${targets.length} stref.`);
    setError(null);
    setBulkRateTargets([]);
  };

  const handleRefreshPriceTemplates = useCallback(() => {
    void fetchParkingPrices();
  }, [fetchParkingPrices]);

  const handleApplySavedPrice = async (areaId: string, templateId: string) => {
    if (!templateId) {
      setAreas((prev) =>
        prev.map((area) => {
          if (area.id !== areaId) {
            return area;
          }
          return {
            ...area,
            rate: {
              ...area.rate,
              templateId: null,
            },
          };
        }),
      );
      return;
    }
    const template = savedPriceTemplates.find((price) => identifiersAreEqual(price.id, templateId));
    if (!template) {
      setError('Nie znaleziono wybranej stawki.');
      return;
    }
    const targetArea = areas.find((area) => area.id === areaId);
    const areaLabel = targetArea?.name ? `dla strefy ${targetArea.name}` : 'dla wybranej strefy';
    setAreas((prev) =>
      prev.map((area) => {
        if (area.id !== areaId) {
          return area;
        }
        return {
          ...area,
          rate: {
            ...area.rate,
            templateId: template.id,
            name: template.name,
            charging_period: template.charging_period,
            passenger_cars: template.passenger_cars,
            trucks: template.trucks,
            buses: template.buses,
            moto_bikes: template.moto_bikes,
            campers: template.campers,
            charging_station: template.charging_station,
          },
        };
      }),
    );
    const targetParkingId = parkingBoxId ?? editingParkingMeta?.id ?? null;
    if (targetParkingId === null) {
      setError('Brak identyfikatora parkingu. Zapisz dane w kroku 1, aby przypisać stawkę do Parking Box.');
      return;
    }
    try {
      await callParkingBoxApi(
        {
          action: 'add-parking-type',
          _data_: {
            parking_id: targetParkingId,
            id: template.id,
          },
        },
        { useStoredToken: true },
      );
      setFeedback(`Zastosowano stawkę ${template.name} ${areaLabel}.`);
      setError(null);
    } catch (applyError) {
      const message = applyError instanceof Error && applyError.message.length > 0 ? applyError.message : 'Nie udało się przypisać stawki do parkingu.';
      setError(message);
    }
  };

  const handleSavePriceTemplate = async (areaId: string) => {
    const area = areas.find((entry) => entry.id === areaId);
    if (!area) {
      return;
    }
    const targetParkingId = parkingBoxId;
    if (targetParkingId === null) {
      setError('Brak identyfikatora parkingu. Zapisz dane w kroku 1, aby zapisać stawki.');
      return;
    }
    const payload = buildParkingPricePayload(area.rate);
    if (!payload) {
      setError('Uzupełnij wszystkie pola stawki poprawnymi wartościami liczbowymi.');
      return;
    }
    setSavingPriceAreaId(areaId);
    try {
      await callParkingBoxApi(
        {
          action: 'add-parking-price',
          _data_: {
            id: targetParkingId,
            ...payload,
          },
        },
        { useStoredToken: true },
      );
      setFeedback(`Stawka ${payload.name} została zapisana w Parking Box.`);
      setError(null);
      const updated = await fetchParkingPrices({ silent: true });
      const latestTemplates = updated ?? savedPriceTemplates;
      const match = findMatchingPriceTemplate(latestTemplates, payload);
      if (match) {
        setAreas((prev) =>
          prev.map((entry) => {
            if (entry.id !== areaId) {
              return entry;
            }
            return {
              ...entry,
              rate: {
                ...entry.rate,
                templateId: match.id,
              },
            };
          }),
        );
      }
    } catch (priceError) {
      const message = priceError instanceof Error && priceError.message.length > 0 ? priceError.message : 'Nie udało się zapisać stawki w Parking Box.';
      setError(message);
    } finally {
      setSavingPriceAreaId((prev) => (prev === areaId ? null : prev));
    }
  };

  const handleReloadParkingAreas = useCallback(() => {
    if (!editingParkingMeta) {
      return;
    }
    const targetIdentifier = parkingBoxId ?? editingParkingMeta.id;
    if (targetIdentifier === null) {
      setError('Brak identyfikatora parkingu. Cofnij się do kroku 1 i zapisz dane parkingu ponownie.');
      setStep(1);
      return;
    }
    void fetchParkingAreas(targetIdentifier, { resetDraft: true });
  }, [editingParkingMeta, fetchParkingAreas, parkingBoxId]);

  const handleRefreshLayout = useCallback(() => {
    const targetParkingId = layoutParkingId ?? editingParkingMeta?.id ?? null;
    if (targetParkingId === null) {
      setError('Brak identyfikatora parkingu. Cofnij się do kroku 1 i zapisz dane parkingu ponownie.');
      setStep(1);
      return;
    }
    if (layoutServerAreaId === null) {
      setError('Brak identyfikatora strefy. Upewnij się, ze krok 2 został zapisany w Parking Box.');
      return;
    }
    void fetchParkingLayout(targetParkingId, layoutServerAreaId, { localAreaId: layoutAreaLocalId });
  }, [editingParkingMeta, fetchParkingLayout, layoutAreaLocalId, layoutParkingId, layoutServerAreaId]);

  const handleApplyInternalSpotIdsToLayout = useCallback(async () => {
    if (!layoutTargetArea || layoutTargetArea.cameraPlacement !== 'internal') {
      return;
    }

    let referenceSpots = getAreaReferenceSpots(layoutTargetArea, layoutInternalReferenceSpots);
    if (!referenceSpots.length) {
      const snapshot = await fetchInternalParkingData({ silent: true });
      referenceSpots = flattenInternalParkingSpots(snapshot).map((spot) => ({
        id: spot.id,
        label: spot.label,
        occupancy: spot.occupancy,
      }));
    }

    if (!referenceSpots.length) {
      setError('Brak identyfikatorow miejsc w odpowiedzi get-parking.');
      return;
    }

    setLayoutBuilder((prev) => applyReferenceSpotIdsToBuilder(prev, referenceSpots));
    setFeedback('Przypisano identyfikatory miejsc do schematu.');
    setError(null);
  }, [fetchInternalParkingData, layoutInternalReferenceSpots, layoutTargetArea]);

  const handleLayoutBuilderChange = useCallback(
    (next: ParkingLayoutBuilderValue) => {
      if (!isInternalLayoutReferenceEnabled || layoutInternalReferenceOptions.length === 0) {
        setLayoutBuilder(next);
        return;
      }
      setLayoutBuilder((previous) => autoAssignReferenceIdsToAddedSpots(previous, next, layoutInternalReferenceOptions));
    },
    [isInternalLayoutReferenceEnabled, layoutInternalReferenceOptions],
  );

  const handleAssignLayoutSpotReference = useCallback(
    (spotIndex: number, referenceId: string) => {
      if (!referenceId.length) {
        return;
      }
      const selectedReference = layoutInternalReferenceOptions.find((option) => option.id === referenceId);
      if (!selectedReference) {
        return;
      }
      setLayoutBuilder((previous) => ({
        ...previous,
        spots: previous.spots.map((spot, index) => (index === spotIndex ? applyReferenceSpotToLayoutSpot(spot, selectedReference) : spot)),
      }));
      setError(null);
    },
    [layoutInternalReferenceOptions],
  );

  const handleSaveLayoutDraft = useCallback(() => {
    if (!layoutAreaLocalId) {
      setError('Wybierz strefę, aby zapisać szkic schematu.');
      return;
    }
    setLayoutDrafts((prev) => ({
      ...prev,
      [layoutAreaLocalId]: layoutBuilder,
    }));
    const areaName = layoutTargetArea?.name ?? 'wybranej strefy';
    setFeedback(`Schemat został zapisany lokalnie dla ${areaName}.`);
    setError(null);
  }, [layoutAreaLocalId, layoutBuilder, layoutTargetArea]);

  const handleSubmitLayout = useCallback(async () => {
    const targetParkingId = layoutParkingId ?? editingParkingMeta?.id ?? null;
    if (targetParkingId === null) {
      console.warn('[Parking layout] Przerwano wysylke schematu - brak ID parkingu.');
      setError('Brak identyfikatora parkingu. Cofnij się do kroku 1 i zapisz dane parkingu ponownie.');
      setStep(1);
      return;
    }
    if (layoutServerAreaId === null) {
      console.warn('[Parking layout] Przerwano wysylke schematu - brak ID strefy.');
      setError('Brak identyfikatora strefy. Zapisz krok 2, aby kontynuowac.');
      setStep(2);
      return;
    }
    if (!layoutStepValid) {
      console.warn('[Parking layout] Przerwano wysylke schematu - krok schematu nie jest gotowy.', {
        width: layoutBuilder.width,
        height: layoutBuilder.height,
        spots: layoutBuilder.spots.length,
      });
      setError('Uzupełnij schemat parkingu przed wysłaniem do Parking Box.');
      return;
    }
    const areaContext = layoutAreaLocalId !== null ? (areas.find((area) => area.id === layoutAreaLocalId) ?? null) : null;
    const builderWithReferenceIds = applyReferenceSpotIdsToBuilder(layoutBuilder, getAreaReferenceSpots(areaContext, layoutInternalReferenceSpots));
    const payload = buildParkingSpotsPayload(builderWithReferenceIds);
    if (!payload) {
      console.warn('[Parking layout] Brak kompletnego payloadu schematu.', layoutBuilder);
      setError('Brak kompletnego schematu do zapisania.');
      return;
    }
    console.log('[Parking layout] Wysylanie schematu do Parking Box:', {
      parkingId: targetParkingId,
      areaId: layoutServerAreaId,
      size: payload.size,
      spotCount: payload.spots.length,
    });
    setIsSubmittingLayout(true);
    try {
      const action = editingParkingMeta ? 'edit-parking-spots' : 'add-parking-spots';
      await callParkingBoxApi(
        {
          action,
          _data_: {
            parking_id: targetParkingId,
            area_id: layoutServerAreaId,
            size: payload.size,
            spots: payload.spots,
          },
        },
        { useStoredToken: true },
      );
      setFeedback(
        editingParkingMeta ? 'Schemat parkingu został zaktualizowany w systemie Parking Box.' : 'Schemat parkingu został zapisany w systemie Parking Box.',
      );
      setError(null);
      setLayoutBuilder(builderWithReferenceIds);
      if (layoutAreaLocalId) {
        setLayoutDrafts((prev) => ({
          ...prev,
          [layoutAreaLocalId]: builderWithReferenceIds,
        }));
      }
      void fetchParkingLayout(targetParkingId, layoutServerAreaId, { silent: true });
    } catch (layoutError) {
      const message =
        layoutError instanceof Error && layoutError.message.length > 0
          ? layoutError.message
          : editingParkingMeta
            ? 'Nie udało się zaktualizować schematu parkingu.'
            : 'Nie udało się zapisać schematu parkingu.';
      setError(message);
    } finally {
      setIsSubmittingLayout(false);
    }
  }, [areas, editingParkingMeta, fetchParkingLayout, layoutAreaLocalId, layoutBuilder, layoutInternalReferenceSpots, layoutParkingId, layoutServerAreaId, layoutStepValid]);

  const resetForm = () => {
    setRecordId(createRecordId());
    setParkingForm({
      name: '',
      address: '',
      city: '',
      space_regular: '',
      space_handi: '',
      space_family: '',
      space_disabled: '',
      space_ev: '',
    });
    setAreas([]);
    setDraftPoints([]);
    setFinalPolygon([]);
    setDraftAreaName('');
    setEditingAreaId(null);
    setBulkRateSource('');
    setBulkRateTargets([]);
    setActiveCameraAreaId(null);
    setCameraDrawingState(null);
    setLayoutBuilder(createLayoutBuilderState());
    setLayoutAreaLocalId(null);
    setLayoutDrafts({});
    setLayoutFetchError(null);
    setInternalParkingSnapshot(null);
    setInternalParkingSnapshotError(null);
    setIsFetchingInternalParkingSnapshot(false);
    internalParkingSnapshotKeyRef.current = null;
    internalParkingSnapshotAutoFetchKeyRef.current = null;
    setIsSubmittingLayout(false);
    setIsSubmittingCamera(false);
    setIsFetchingLayout(false);
    setEditingParkingMeta(null);
    setDeletingParkingId(null);
    setParkingBoxId(null);
    setIsSubmittingAreas(false);
    setIsFetchingRemoteAreas(false);
    setStep(1);
    setFeedback('Rozpoczeto nowy proces dodawania parkingu.');
    setError(null);
  };

  const cancelEditingParking = () => {
    resetForm();
    setFeedback('Anulowano tryb edycji. Formularz wyczyszczony dla nowego parkingu.');
  };

  const handleEditParking = (item: ParkingListItem) => {
    const identifier = parseParkingIdentifier(item.id);
    if (!identifier) {
      setError('Nie można edytować parkingu bez identyfikatora.');
      return;
    }

    setParkingForm({
      name: item.name ?? '',
      address: item.address ?? '',
      city: item.city ?? '',
      space_regular: formatInputValue(item.space_regular),
      space_handi: formatInputValue(item.space_handi),
      space_family: formatInputValue(item.space_family),
      space_disabled: formatInputValue(item.space_disabled),
      space_ev: formatInputValue(item.space_ev),
    });
    setStep(1);
    setEditingParkingMeta({
      id: identifier.raw,
      label: item.name ?? `Parking ${identifier.text}`,
    });
    setParkingBoxId(identifier.raw);
    setFeedback(`Edycja parkingu ${item.name ?? identifier.text}. Zmien dane i zatwierdz, by przeslac je do systemu.`);
    setError(null);
    void fetchParkingAreas(identifier.raw, { resetDraft: true });
  };

  const requestDeleteParking = (item: ParkingListItem) => {
    const identifier = parseParkingIdentifier(item.id);
    if (!identifier) {
      setError('Nie można usunąć parkingu bez identyfikatora.');
      return;
    }
    const { name, address } = describeParking(item);
    setPendingDelete({
      identifier,
      name,
      address,
    });
    setError(null);
  };

  const confirmDeleteParking = async () => {
    if (!pendingDelete) {
      return;
    }
    setDeletingParkingId(pendingDelete.identifier.raw);
    try {
      await callParkingBoxApi(
        {
          action: 'delete-parking',
          _data_: { id: pendingDelete.identifier.raw },
        },
        { useStoredToken: true },
      );
      setFeedback(`Parking ${pendingDelete.name} został usunięty.`);
      setError(null);
      if (editingParkingMeta && identifiersAreEqual(editingParkingMeta.id, pendingDelete.identifier.raw)) {
        setEditingParkingMeta(null);
      }
      await fetchParkingList();
      setPendingDelete(null);
    } catch (deleteError) {
      const message = deleteError instanceof Error && deleteError.message.length > 0 ? deleteError.message : 'Nie udało się usunąć parkingu.';
      setError(message);
    } finally {
      setDeletingParkingId(null);
    }
  };

  const cancelDeleteParking = () => {
    if (deletingParkingId !== null) {
      return;
    }
    setPendingDelete(null);
  };

  const saveParking = () => {
    if (!preparedRecord) {
      setError('Uzupenij wszystkie kroki przed zapisem.');
      return;
    }
    upsert(preparedRecord);
    setFeedback('Parking zapisany lokalnie.');
    setError(null);
  };

  const copyPayload = async () => {
    if (!preparedPayload || !payloadJson) {
      setError('Brak kompletnego payloadu do skopiowania.');
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      setError('Schowek nie jest dostepny w tej przegladarce.');
      return;
    }
    await navigator.clipboard.writeText(payloadJson);
    setPayloadCopied(true);
    setFeedback('Payload skopiowany do schowka.');
    setError(null);
  };

  const handleStepChange = (target: WizardStep) => {
    if (target === step) {
      return;
    }
    if (target > step && !canAccessStep(target)) {
      if (editingParkingMeta) {
        setStep(target);
        return;
      }
      return;
    }
    setStep(target);
  };

  const goToNextStep = () => {
    const targetParkingId = parkingBoxId ?? editingParkingMeta?.id ?? null;
    const hasPersistedParking = targetParkingId !== null;

    if (step === 1) {
      if (!parkingStepValid || isSubmittingParking) {
        return;
      }

      const submitParkingStep = async () => {
        setIsSubmittingParking(true);
        try {
          const parkingPayload = {
            name: parkingForm.name.trim(),
            address: parkingForm.address.trim(),
            city: parkingForm.city.trim(),
            space_regular: parsePositiveNumber(parkingForm.space_regular),
            space_handi: parsePositiveNumber(parkingForm.space_handi),
            space_family: parsePositiveNumber(parkingForm.space_family),
            space_disabled: parsePositiveNumber(parkingForm.space_disabled),
            space_ev: parsePositiveNumber(parkingForm.space_ev),
          };

          const editingMeta = editingParkingMeta;
          const shouldEditParking = editingMeta || hasPersistedParking;

          if (editingMeta || hasPersistedParking) {
            await callParkingBoxApi(
              {
                action: 'edit-parking',
                _data_: {
                  id: targetParkingId,
                  ...parkingPayload,
                },
              },
              { useStoredToken: true },
            );
            setFeedback(
              editingMeta
                ? `Dane parkingu ${editingMeta.label} zostały zaktualizowane w systemie Parking Box.`
                : 'Dane parkingu zostały zaktualizowane w systemie Parking Box.',
            );
            if (editingMeta) {
              setParkingBoxId(editingMeta.id);
            }
          } else {
            setParkingBoxId(null);
            const response = await callParkingBoxApi(
              {
                action: 'add-parking',
                _data_: parkingPayload,
              },
              { useStoredToken: true },
            );
            const createdId = extractParkingBoxIdFromResponse(response.json);
            if (!createdId) {
              throw new Error('Serwer nie zwrócił identyfikatora nowego parkingu. Spróbuj ponownie lub skontaktuj się z administratorem.');
            }
            setParkingBoxId(createdId);
            setFeedback('Dane parkingu zostały wysłane do systemu Parking Box.');
          }

          setError(null);
          // action: "add-cam-polygons",
          // _data_:{
          //   cam_id: "0e99602f8f9df86d655316e94014bf84",
          //   polygons: [{
          //     id: "spot-ha4oyhiq",
          //     label: "Miejsce 1",
          //     points: [[0.5321, 0.4648], [0.5563, 0.5869], [0.6231, 0.5787], [0.5981, 0.4747]]
          //   }, {
          //     id: "spot-o41nl8iq",
          //     label: "Miejsce 2",
          //     points: [[0.6018,0.4697],[0.6278,0.5754],[0.7002,0.5622],[0.6696,0.4615]]
          //   }],
          // }
          // action: "add-parking-spots",
          // _data_:{
          //   parking_id: 5,
          //   area_id: 7,
          //   size: [600,400],
          //   spots: [
          //     {
          //       id: "slot-24crpz5",
          //       label: "M1",
          //       cx: 125,
          //       cy: 120,
          //       width: 48,
          //       height: 96,
          //       rotation: 0,
          //     },
          //     {
          //       id: "slot-lijx8jh",
          //       label: "M2",
          //       cx: 225,
          //       cy: 120,
          //       width: 48,
          //       height: 96,
          //       rotation: 0,
          //     },
          //     {
          //       id: "slot-jgc9xja",
          //       label: "M3",
          //       cx: 300,
          //       cy: 200,
          //       width: 48,
          //       height: 96,
          //       rotation: 0,
          //     },
          //   ],
          // }
          // action: "get-parking-spots",
          // _data_:{
          //   parking_id: 5,
          //   area_id: 7,
          // }
          // action: "get-parking-area",
          // _data_: {
          //   id: 5,
          // }
          // _data_:{cam_id: "0e99602f8f9df86d655316e94014bf84"}
          void fetchParkingList();
          setStep(editingMeta ? 1 : 2);
        } catch (submissionError) {
          const editingMeta = editingParkingMeta;
          const message =
            submissionError instanceof Error && submissionError.message.length > 0
              ? submissionError.message
              : editingMeta
                ? 'Nie udało się zaktualizować danych parkingu.'
                : 'Nie udało się wysłać danych parkingu.';
          setError(message);
          setFeedback(null);
        } finally {
          setIsSubmittingParking(false);
        }
      };

      void submitParkingStep();
      return;
    }

    if (step === 2) {
      if (!areasStepValid || isSubmittingAreas || isFetchingRemoteAreas) {
        return;
      }

      const submitAreasStep = async () => {
        const existingParkingId = parkingBoxId ?? editingParkingMeta?.id ?? null;
        if (existingParkingId === null) {
          setError('Brak identyfikatora parkingu. Cofnij się do kroku 1 i zapisz dane parkingu ponownie.');
          setStep(1);
          return;
        }

        setIsSubmittingAreas(true);
        try {
          const editingMeta = editingParkingMeta;
          const areaPayload = areas.map((area) => {
            const payload: Record<string, unknown> = {
              name: area.name.trim(),
              polygon: formatPolygon(area.polygon),
            };
            if ((editingMeta || hasPersistedParking) && area.serverId !== null) {
              payload.id = area.serverId;
              payload.area_id = area.serverId;
            }
            return payload;
          });

          const hasServerAreas = areaPayload.some((entry) => 'id' in entry || 'area_id' in entry);
          const action = hasServerAreas || editingMeta || hasPersistedParking ? 'edit-area' : 'add-area';

          if (action === 'edit-area') {
            await callParkingBoxApi(
              {
                action: 'edit-area',
                _data_: {
                  id: existingParkingId,
                  area: areaPayload,
                },
              },
              { useStoredToken: true },
            );
            setFeedback(
              editingMeta ? `Zapisano obszary parkingu ${editingMeta.label} w systemie Parking Box.` : 'Zapisano obszary parkingu w systemie Parking Box.',
            );
            setError(null);
            void fetchParkingAreas(existingParkingId, { silent: true });
          } else {
            await callParkingBoxApi(
              {
                action: 'add-area',
                _data_: {
                  id: existingParkingId,
                  area: areaPayload,
                },
              },
              { useStoredToken: true },
            );
            setFeedback('Obszary zostały wysłane do systemu Parking Box.');
            setError(null);
            void fetchParkingAreas(existingParkingId, { silent: true });
          }
          setStep(3);
        } catch (areaError) {
          const editingMeta = editingParkingMeta;
          const message =
            areaError instanceof Error && areaError.message.length > 0
              ? areaError.message
              : editingMeta
                ? 'Nie udało się zaktualizować obszarów parkingu.'
                : 'Nie udało się dodać obszarów do parkingu.';
          setError(message);
        } finally {
          setIsSubmittingAreas(false);
        }
      };

      void submitAreasStep();
      return;
    } else if (step === 3) {
      if (!pricingStepValid || isSubmittingPricing) {
        return;
      }
      const submitPricingStep = async () => {
        const targetParkingId = parkingBoxId ?? editingParkingMeta?.id ?? null;
        if (targetParkingId === null) {
          setError('Brak identyfikatora parkingu. Cofnij się do kroku 1 i zapisz dane parkingu ponownie.');
          setStep(1);
          return;
        }
        setIsSubmittingPricing(true);
        try {
          const action = editingParkingMeta || hasPersistedParking ? 'edit-parking-price' : 'add-parking-price';
          for (const area of areas) {
            const payload = buildParkingPricePayload(area.rate);
            if (!payload) {
              throw new Error('Nie można zapisać niepełnych stawek. Uzupełnij formularze stref.');
            }
            const requestPayload: Record<string, unknown> = {
              id: targetParkingId,
              ...payload,
            };
            if ((editingParkingMeta || hasPersistedParking) && area.rate.templateId) {
              requestPayload.price_id = area.rate.templateId;
            }
            await callParkingBoxApi(
              {
                action,
                _data_: requestPayload,
              },
              { useStoredToken: true },
            );
          }
          setFeedback(
            editingParkingMeta ? 'Stawki parkingu zostały zaktualizowane w systemie Parking Box.' : 'Stawki parkingu zostały zapisane w systemie Parking Box.',
          );
          setError(null);
          const updatedTemplates = await fetchParkingPrices({ silent: true });
          const latestTemplates = updatedTemplates ?? savedPriceTemplates;
          if (latestTemplates.length) {
            setAreas((prev) =>
              prev.map((area) => {
                const payload = buildParkingPricePayload(area.rate);
                if (!payload) {
                  return area;
                }
                const match = findMatchingPriceTemplate(latestTemplates, payload);
                if (!match || area.rate.templateId === match.id) {
                  return area;
                }
                return {
                  ...area,
                  rate: {
                    ...area.rate,
                    templateId: match.id,
                  },
                };
              }),
            );
          }
          setStep(4);
        } catch (pricingError) {
          const message =
            pricingError instanceof Error && pricingError.message.length > 0
              ? pricingError.message
              : editingParkingMeta
                ? 'Nie udało się zaktualizować stawek w systemie Parking Box.'
                : 'Nie udało się zapisać stawek w systemie Parking Box.';
          setError(message);
        } finally {
          setIsSubmittingPricing(false);
        }
      };
      void submitPricingStep();
      return;
    } else if (step === 4) {
      if (!cameraAnnotationStepValid || isSubmittingCamera) {
        return;
      }

      const submitCameraStep = async () => {
        const missingAreaIds = cameraAnnotationAreas.filter((area) => area.serverId === null);
        if (missingAreaIds.length > 0) {
          setError('Brak identyfikatorów stref kamer. Cofnij się do kroku 2 i zapisz obszary w Parking Box.');
          setStep(2);
          return;
        }

        const missingCamIds = cameraAnnotationAreas
          .filter((area) => area.serverId !== null && hasCompleteExternalCameraData(area))
          .filter((area) => !area.externalCameraId.trim());
        if (missingCamIds.length) {
          setError("Dodaj kamerę przyciskiem 'Dodaj kamerę' dla każdej strefy przed przejściem dalej.");
          return;
        }

        setIsSubmittingCamera(true);
        try {
          const assignmentTargets = cameraAnnotationAreas.filter((area) => area.serverId !== null && area.externalCameraId.trim().length > 0);
          await Promise.all(
            assignmentTargets.map((area) =>
              callParkingBoxApi(
                {
                  action: 'add-area-cam',
                  _data_: {
                    area_id: area.serverId,
                    cam_id: area.externalCameraId.trim(),
                  },
                },
                { useStoredToken: true },
              ),
            ),
          );
          const polygonTargets = cameraAnnotationAreas.filter(
            (area) => area.cameraPlacement === 'external' && area.externalCameraId.trim().length > 0 && sanitizeCameraSpots(area).length > 0,
          );
          await Promise.all(
            polygonTargets.map((area) =>
              callParkingBoxApi(
                {
                  action: 'add-cam-polygons',
                  _data_: {
                    cam_id: area.externalCameraId.trim(),
                    polygons: sanitizeCameraSpots(area),
                  },
                },
                { useStoredToken: true },
              ),
            ),
          );
          setFeedback(
            cameraAnnotationAreas.length > 0
              ? 'Dane kamer zewnetrznych sa juz zapisane. Przechodzimy do schematu.'
              : 'Konfiguracja kamer jest gotowa. Przechodzimy do schematu.',
          );
          setError(null);
          setStep(layoutStepId);
        } catch (polygonError) {
          const message = polygonError instanceof Error && polygonError.message.length ? polygonError.message : 'Nie udało się zapisać poligonów kamer.';
          setError(message);
        } finally {
          setIsSubmittingCamera(false);
        }
      };

      void submitCameraStep();
      return;
    }
  };

  const goToPreviousStep = () => {
    if (step === layoutStepId) {
      setStep(4);
    } else if (step === 4) {
      setStep(3);
    } else if (step === 3) {
      setStep(2);
    } else if (step === 2) {
      setStep(1);
    }
  };

  const cameraStepReady = parkingStepValid && areasStepValid && pricingStepValid && cameraAnnotationStepValid;
  const layoutStepReady = cameraStepReady && layoutStepValid;

  const steps = [
    { id: 1 as WizardStep, label: 'Dane parkingu', ready: parkingStepValid },
    { id: 2 as WizardStep, label: 'Obszary', ready: areasStepValid },
    { id: 3 as WizardStep, label: 'Stawki', ready: pricingStepValid },
    { id: 4 as WizardStep, label: 'Kamera', ready: cameraStepReady },
    { id: layoutStepId, label: 'Schemat', ready: layoutStepReady },
  ];
  const deleteInProgress = pendingDelete && deletingParkingId !== null ? identifiersAreEqual(pendingDelete.identifier.raw, deletingParkingId) : false;
  return (
    <main className="min-h-screen overflow-x-hidden bg-slate-200 px-2 py-3 text-slate-900 transition-colors dark:bg-neutral-950 dark:text-slate-100 sm:px-4 sm:py-6 lg:px-6 lg:py-10">
      <div className="mx-auto flex w-full max-w-[1920px] flex-col gap-4 overflow-hidden rounded-[20px] bg-white shadow-[0_30px_80px_-40px_rgba(15,23,42,0.45)] transition-colors dark:bg-neutral-900/80 dark:shadow-[0_30px_80px_-40px_rgba(0,0,0,0.6)] sm:gap-6 sm:rounded-[32px] lg:gap-8">
        <header className="flex flex-col gap-3 border-b border-slate-200 px-3 py-4 transition-colors dark:border-white/10 sm:gap-6 sm:px-6 sm:py-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <Link
              to="/?admin=1"
              className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-600 transition hover:border-emerald-500 hover:text-emerald-600 dark:border-white/10 dark:text-slate-200 dark:hover:border-emerald-400 dark:hover:text-emerald-300"
            >
              Powrót
            </Link>
            <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.4em]">
              <span className="rounded-lg bg-emerald-500 px-3 py-1 text-white">Parking</span>
              <span className="rounded-lg border border-emerald-500 px-3 py-1 text-emerald-600 dark:border-emerald-400 dark:text-emerald-300">Box</span>
            </div>
          </div>
        </header>

        <section className="flex flex-col gap-3 px-3 sm:gap-6 sm:px-6">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {steps.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleStepChange(item.id)}
                className={`flex items-center gap-2 rounded-2xl border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.24em] transition sm:gap-3 sm:px-4 sm:py-2 sm:text-[11px] sm:tracking-[0.3em] ${
                  step === item.id
                    ? 'border-emerald-500 bg-emerald-500 text-white'
                    : item.ready
                      ? 'border-emerald-200 text-emerald-600'
                      : 'border-slate-200 text-slate-400'
                }`}
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-white/30 text-[10px] font-bold sm:h-6 sm:w-6 sm:text-xs">
                  {item.id}
                </span>
                {item.label}
              </button>
            ))}
            {editingParkingMeta ? (
              <button
                type="button"
                onClick={resetForm}
                className="ml-auto rounded-2xl border border-slate-200 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-600 transition hover:border-rose-400 hover:text-rose-500 dark:border-white/10 dark:text-slate-200 dark:hover:border-rose-400 dark:hover:text-rose-300 sm:px-4 sm:py-2 sm:text-[11px] sm:tracking-[0.3em]"
              >
                Nowy parking
              </button>
            ) : null}
          </div>

          {feedback && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm font-semibold text-emerald-700 dark:border-emerald-400/40 dark:bg-emerald-400/10 dark:text-emerald-200 sm:px-4 sm:py-3">
              {feedback}
            </div>
          )}
          {error && (
            <div className="rounded-2xl border border-rose-300 bg-rose-50 px-3 py-2.5 text-sm font-semibold text-rose-600 dark:border-rose-400/40 dark:bg-rose-400/10 dark:text-rose-200 sm:px-4 sm:py-3">
              {error}
            </div>
          )}

          {step === 1 && (
            <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-3 transition-colors dark:border-white/10 dark:bg-white/5 sm:p-6">
              <div className="mb-4 text-xs font-semibold uppercase tracking-[0.4em] text-slate-500 dark:text-slate-300/80 sm:mb-6">Krok 1 - Dane parkingu</div>
              {editingParkingMeta ? (
                <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-amber-700 dark:border-amber-400/40 dark:bg-amber-400/10 dark:text-amber-200">
                  <span>Tryb edycji: {editingParkingMeta.label}</span>
                  <button
                    type="button"
                    onClick={cancelEditingParking}
                    className="rounded-lg border border-amber-400 px-3 py-1 text-[10px] font-semibold tracking-[0.3em] text-amber-700 transition hover:bg-amber-400 hover:text-white dark:border-amber-300/60 dark:text-amber-100"
                  >
                    Anuluj
                  </button>
                </div>
              ) : null}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                  <span>Nazwa parkingu</span>
                  <input
                    type="text"
                    value={parkingForm.name}
                    onChange={(event) => setParkingForm((prev) => ({ ...prev, name: event.target.value }))}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
                  />
                </label>
                <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                  <span>Miasto</span>
                  <input
                    type="text"
                    value={parkingForm.city}
                    onChange={(event) => setParkingForm((prev) => ({ ...prev, city: event.target.value }))}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
                  />
                </label>
                <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                  <span>Adres</span>
                  <input
                    type="text"
                    value={parkingForm.address}
                    onChange={(event) => setParkingForm((prev) => ({ ...prev, address: event.target.value }))}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
                  />
                </label>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3 sm:mt-6">
                {spaceFields.map((field) => (
                  <label key={field} className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                    <span className={formLabelClass}>{getSpaceFieldLabel(field)}</span>
                    <input
                      type="number"
                      min={0}
                      value={parkingForm[field]}
                      onChange={(event) => setParkingForm((prev) => ({ ...prev, [field]: event.target.value }))}
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
                    />
                  </label>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap justify-end gap-3 sm:mt-6">
                <button
                  type="button"
                  onClick={goToNextStep}
                  disabled={!parkingStepValid || isSubmittingParking}
                  aria-busy={isSubmittingParking}
                  className="rounded-md border border-emerald-500 px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.4em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmittingParking ? (editingParkingMeta ? 'Zapisywanie...' : 'Wysylanie...') : editingParkingMeta ? 'Zapisz' : 'Dalej'}
                </button>
              </div>
            </section>
          )}

          {step === 2 && (
            <section className="grid gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] sm:gap-6">
              <div className="min-w-0 flex flex-col gap-4">
                <div className="relative min-h-[480px] overflow-hidden rounded-3xl border border-slate-200 transition-colors dark:border-white/10">
                  <div ref={containerRef} className="h-full w-full" />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={startDrawing}
                    className="rounded-lg border border-emerald-500 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white"
                  >
                    Rysuj poligon
                  </button>
                  <button
                    type="button"
                    onClick={undoLastPoint}
                    disabled={draftPoints.length === 0}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-600 transition hover:bg-slate-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cofnij punkt
                  </button>
                  <button type="button" onClick={finishDrawing} disabled={!finishButtonActive} className={finishButtonClasses}>
                    Zakoncz rysowanie
                  </button>
                  <button
                    type="button"
                    onClick={cancelDrawing}
                    className="rounded-lg border border-rose-400 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-rose-500 transition hover:bg-rose-500 hover:text-white"
                  >
                    Resetuj
                  </button>
                  <button
                    type="button"
                    onClick={resumeEditing}
                    disabled={!hasFinalPolygon}
                    className="rounded-lg border border-slate-300 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-600 transition hover:bg-slate-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Edytuj szkic
                  </button>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 transition-colors dark:border-white/10 dark:bg-white/5">
                  <div className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">Nazwa poligonu</div>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <input
                      type="text"
                      value={draftAreaName}
                      onChange={(event) => setDraftAreaName(event.target.value)}
                      placeholder="Np. Strefa A"
                      className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
                    />
                    <button type="button" onClick={saveArea} className={saveAreaButtonClasses}>
                      Zapisz obszar
                    </button>
                  </div>
                </div>
              </div>

              <div className="min-w-0 flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-3 transition-colors dark:border-white/10 dark:bg-white/5 sm:p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">Lista poligonów</div>
                  {editingParkingMeta ? (
                    <button
                      type="button"
                      onClick={handleReloadParkingAreas}
                      disabled={isFetchingRemoteAreas}
                      className="rounded-lg border border-emerald-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isFetchingRemoteAreas ? 'Pobieranie...' : 'Odśwież z Parking Box'}
                    </button>
                  ) : null}
                </div>
                {editingParkingMeta ? (
                  <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300/80">
                    Tryb edycji parkingu {editingParkingMeta.label}. Kliknij "Odśwież", aby ponownie pobrać obszary z systemu Parking Box.
                  </p>
                ) : null}
                {isFetchingRemoteAreas ? (
                  <p className="text-sm text-slate-500 dark:text-slate-300/80">Trwa pobieranie obszarów z systemu Parking Box...</p>
                ) : areas.length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-300/80">Dodaj co najmniej jeden poligon, aby przejść dalej.</p>
                ) : null}
                {areas.map((area) => (
                  <div key={area.id} className="rounded-2xl border border-slate-200 p-4 text-sm transition-colors dark:border-white/10">
                    <div className="flex flex-col gap-2">
                      <div className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-600 dark:text-slate-200">{area.name}</div>
                      <div className="flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300/80">
                        <span>{area.polygon.length} punktow</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300/80">
                        <span
                          className={`rounded-full border px-3 py-1 text-[10px] font-semibold ${
                            area.cameraPlacement === 'external'
                              ? 'border-amber-400 text-amber-600 dark:border-amber-300 dark:text-amber-200'
                              : 'border-slate-300 text-slate-600 dark:border-white/15 dark:text-slate-200'
                          }`}
                        >
                          {area.cameraPlacement === 'external' ? 'Kamera zewnetrzna' : 'Kamera wewnetrzna'}
                        </span>
                      </div>
                      {false && area.cameraPlacement !== 'external' && (
                        <div className="flex flex-col gap-2 rounded-xl border border-slate-200 p-3 text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors dark:border-white/10 dark:bg-white/5 dark:text-slate-300/80">
                          <div className="flex flex-wrap items-center gap-3">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">Przypisz kamerę</span>
                            <button
                              type="button"
                              onClick={() => void fetchAvailableCameras()}
                              disabled={isFetchingCameras}
                              className="rounded-full border border-slate-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600 transition hover:bg-slate-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/10"
                            >
                              {isFetchingCameras ? 'Ładowanie...' : 'Odśwież liste kamer'}
                            </button>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              value={area.internalCameraId}
                              onChange={(event) => handleInternalCameraChange(area.id, event.target.value)}
                              disabled={isFetchingCameras || availableCameras.length === 0}
                              className="min-w-[220px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:bg-neutral-900 dark:text-slate-100 dark:[&>option]:bg-neutral-900 dark:[&>option]:text-slate-100"
                            >
                              <option value="">{availableCameras.length ? '-- wybierz kamerę --' : 'Brak kamer'}</option>
                              {availableCameras.map((camera) => (
                                <option key={camera.id} value={camera.id}>
                                  {camera.name}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => void handleAssignInternalCamera(area.id)}
                              disabled={isFetchingCameras || assigningAreaCameraId === area.id || !area.internalCameraId}
                              className="rounded-full border border-emerald-300 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {assigningAreaCameraId === area.id ? 'Zapisywanie...' : 'Przypisz'}
                            </button>
                            {isFetchingAreaCameras && (
                              <span className="text-[10px] font-normal normal-case tracking-normal text-slate-500 dark:text-slate-300">
                                Pobieram przypisane kamery...
                              </span>
                            )}
                          </div>
                          {area.internalCameraId && (
                            <div className="text-[10px] font-semibold normal-case tracking-normal text-emerald-600 dark:text-emerald-300">
                              Aktualnie przypisana kamera: {area.internalCameraId}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => focusArea(area)}
                          className="rounded-lg border border-slate-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-600 transition hover:bg-slate-600 hover:text-white"
                        >
                          Pokaz na mapie
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEditArea(area.id)}
                          className="rounded-lg border border-emerald-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white"
                        >
                          Edytuj geometrie
                        </button>
                        <button
                          type="button"
                          onClick={() => removeArea(area.id)}
                          className="rounded-lg border border-rose-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-rose-500 transition hover:bg-rose-500 hover:text-white"
                        >
                          Usuń
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="flex flex-wrap justify-between gap-3 pt-4">
                  <button
                    type="button"
                    onClick={goToPreviousStep}
                    className="rounded-2xl border border-slate-200 px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.4em] text-slate-600 transition hover:bg-slate-600 hover:text-white"
                  >
                    Wróć
                  </button>
                  <button
                    type="button"
                    onClick={goToNextStep}
                    disabled={!areasStepValid || isSubmittingAreas || isFetchingRemoteAreas}
                    aria-busy={isSubmittingAreas}
                    className="rounded-2xl border border-emerald-500 px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.4em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSubmittingAreas ? (editingParkingMeta ? 'Zapisywanie...' : 'Wysylanie...') : 'Dalej'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {step === 3 && (
            <section className="rounded-3xl border border-slate-200 bg-white p-3 transition-colors dark:border-white/10 dark:bg-white/5 sm:p-6">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-500 dark:text-slate-300/80">Krok 3 - Stawki i payload</div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={goToPreviousStep}
                    className="rounded-2xl border border-slate-200 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-600 transition hover:bg-slate-600 hover:text-white sm:px-5 sm:py-2 sm:text-[11px] sm:tracking-[0.4em]"
                  >
                    Wróć
                  </button>
                  <button
                    type="button"
                    onClick={goToNextStep}
                    disabled={!pricingStepValid || isSubmittingPricing}
                    aria-busy={isSubmittingPricing}
                    className="rounded-2xl border border-emerald-500 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.28em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 sm:px-5 sm:py-2 sm:text-[11px] sm:tracking-[0.4em]"
                  >
                    {isSubmittingPricing ? (editingParkingMeta ? 'Zapisywanie...' : 'Wysylanie...') : 'Dalej'}
                  </button>
                  <button
                    type="button"
                    onClick={copyPayload}
                    disabled={!preparedPayload}
                    className={`rounded-2xl border px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.4em] transition ${
                      payloadCopied ? 'border-emerald-200 bg-emerald-500 text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-600 hover:text-white'
                    } disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    {payloadCopied ? 'Skopiowano' : 'Kopiuj'}
                  </button>
                </div>
              </div>

              {areas.length === 0 && <p className="text-sm text-slate-500 dark:text-slate-300/80">Dodaj obszary w kroku 2.</p>}

              <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-xs font-semibold uppercase tracking-[0.3em] text-slate-600 transition-colors dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
                <span>
                  Biblioteka zapisanych stawek:{' '}
                  {isFetchingPriceTemplates
                    ? 'Trwa pobieranie...'
                    : savedPriceTemplates.length
                      ? `${savedPriceTemplates.length} pozycji`
                      : 'Brak zapisanych stawek'}
                </span>
                <button
                  type="button"
                  onClick={handleRefreshPriceTemplates}
                  disabled={isFetchingPriceTemplates}
                  className="rounded-lg border border-emerald-300 px-4 py-2 text-[10px] font-semibold tracking-[0.3em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Odśwież stawki
                </button>
                {priceTemplateError ? (
                  <span className="text-[10px] font-normal normal-case tracking-normal text-rose-500 dark:text-rose-300">{priceTemplateError}</span>
                ) : null}
              </div>

              {areas.length > 1 && (
                <div className="mb-6 rounded-2xl border border-dashed border-slate-300 p-4 text-sm transition-colors dark:border-white/15">
                  <div className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-600 dark:text-slate-200">Kopiuj stawki miedzy strefami</div>
                  <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
                    <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                      <span className={formLabelClass}>Źródło stawek</span>
                      <select
                        value={bulkRateSource}
                        onChange={(event) => {
                          const nextSource = event.target.value;
                          setBulkRateSource(nextSource);
                          setBulkRateTargets((prev) => prev.filter((targetId) => targetId !== nextSource));
                        }}
                        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold uppercase tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/15 dark:bg-neutral-900/60 dark:text-slate-100 dark:[&>option]:bg-neutral-900 dark:[&>option]:text-slate-100"
                      >
                        <option value="">-- wybierz strefę --</option>
                        {areas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                      <span className={formLabelClass}>Strefy docelowe</span>
                      <div className="flex flex-wrap gap-2">
                        {areas.map((area) => {
                          const disabled = !bulkRateSource || bulkRateSource === area.id;
                          const checked = bulkRateTargets.includes(area.id);
                          return (
                            <label
                              key={area.id}
                              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] ${
                                checked
                                  ? 'border-emerald-400 bg-emerald-500 text-white'
                                  : 'border-slate-200 text-slate-600 dark:border-white/15 dark:text-slate-200'
                              } ${disabled ? 'opacity-50' : ''}`}
                            >
                              <input
                                type="checkbox"
                                disabled={disabled}
                                checked={checked}
                                onChange={() => toggleBulkRateTarget(area.id)}
                                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 dark:border-white/20"
                              />
                              <span>{area.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      onClick={applyBulkRates}
                      disabled={!bulkRateSource || !bulkRateTargets.length}
                      className="rounded-2xl border border-emerald-500 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Skopiuj stawki
                    </button>
                  </div>
                </div>
              )}

              {areas.map((area) => (
                <div key={area.id} className="mb-6 rounded-2xl border border-slate-200 p-4 transition-colors dark:border-white/10">
                  <div className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-slate-600 dark:text-slate-200">{area.name}</div>
                  <div className="mb-4 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300/80">
                    <span
                      className={`rounded-full border px-3 py-1 text-[10px] font-semibold ${
                        area.cameraPlacement === 'external'
                          ? 'border-amber-400 text-amber-600 dark:border-amber-300 dark:text-amber-200'
                          : 'border-slate-300 text-slate-600 dark:border-white/15 dark:text-slate-200'
                      }`}
                    >
                      {area.cameraPlacement === 'external' ? 'Kamera zewnetrzna' : 'Kamera wewnetrzna'}
                    </span>
                    {area.cameraPlacement === 'external' && (
                      <span className="text-[10px] uppercase tracking-[0.2em] text-amber-600 dark:text-amber-300">Skonfiguruj kamerę w zakładce Kamera</span>
                    )}
                  </div>
                  <div className="mb-4 grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
                    <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                      <span className={formLabelClass}>Wybierz zapisaną stawkę</span>
                      <select
                        value={area.rate.templateId ?? ''}
                        onChange={(event) => void handleApplySavedPrice(area.id, event.target.value)}
                        disabled={savedPriceTemplates.length === 0}
                        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-white/10 dark:text-slate-100 dark:[&>option]:bg-neutral-900 dark:[&>option]:text-slate-100"
                      >
                        <option value="">{savedPriceTemplates.length ? '-- wybierz --' : 'Brak zapisanych stawek'}</option>
                        {savedPriceTemplates.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                      <span className={formLabelClass}>Zapisz bieżącą stawkę</span>
                      <button
                        type="button"
                        onClick={() => void handleSavePriceTemplate(area.id)}
                        disabled={savingPriceAreaId === area.id}
                        aria-busy={savingPriceAreaId === area.id}
                        className="rounded-lg border border-emerald-300 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {savingPriceAreaId === area.id ? 'Zapisywanie...' : 'Zapisz w Parking Box'}
                      </button>
                    </div>
                  </div>
                  <div className="grid gap-4 lg:grid-cols-2">
                    <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                      <span className={formLabelClass}>Nazwa stawki</span>
                      <input
                        type="text"
                        value={area.rate.name}
                        onChange={(event) => handleRateChange(area.id, 'name', event.target.value)}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                      <span className={formLabelClass}>{getRateFieldLabel('charging_period')}</span>
                      <input
                        type="text"
                        value={area.rate.charging_period}
                        onChange={(event) => handleRateChange(area.id, 'charging_period', event.target.value)}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
                      />
                    </label>
                  </div>
                  <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {pricingFields.map((field) => (
                      <label key={field} className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                        <span className={formLabelClass}>{getRateFieldLabel(field)}</span>
                        <input
                          type="number"
                          min={0}
                          step="0.5"
                          value={area.rate[field]}
                          onChange={(event) => handleRateChange(area.id, field, event.target.value)}
                          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          )}

          {step === 4 && (
            <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-3 transition-colors dark:border-white/10 dark:bg-white/5 sm:p-6">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 sm:mb-6 sm:gap-3">
                <div className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-500 dark:text-slate-300/80">Krok 4 - Kamera i miejsca</div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={goToPreviousStep}
                    className="rounded-2xl border border-slate-200 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-600 transition hover:bg-slate-600 hover:text-white sm:px-5 sm:py-2 sm:text-[11px] sm:tracking-[0.4em]"
                  >
                    Wróć
                  </button>
                  <button
                    type="button"
                    onClick={goToNextStep}
                    disabled={!cameraAnnotationStepValid || isSubmittingCamera}
                    aria-busy={isSubmittingCamera}
                    className="rounded-2xl border border-emerald-500 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.28em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 sm:px-5 sm:py-2 sm:text-[11px] sm:tracking-[0.4em]"
                  >
                    {isSubmittingCamera ? 'Zapisywanie...' : 'Dalej'}
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 p-3 text-xs uppercase tracking-[0.25em] text-slate-500 transition-colors dark:border-white/10 dark:bg-white/5 dark:text-slate-300/80 sm:p-4 sm:tracking-[0.3em]">
                <div className="flex flex-wrap items-center gap-3">
                  <span>Obszary parkingu</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {cameraAreas.map((area) => {
                    const isActive = activeCameraArea?.id === area.id;
                    const isConfigured = area.cameraPlacement === 'external' ? area.cameraSpots.length > 0 : area.internalCameraId.trim().length > 0;
                    return (
                      <button
                        key={area.id}
                        type="button"
                        onClick={() => setActiveCameraAreaId(area.id)}
                        className={`max-w-full whitespace-normal break-words rounded-full border px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-center transition sm:px-4 sm:py-2 sm:text-[10px] sm:tracking-[0.2em] ${
                          isActive
                            ? 'border-emerald-500 bg-emerald-500 text-white'
                            : isConfigured
                              ? 'border-emerald-200 text-emerald-600 dark:border-emerald-400/60 dark:text-emerald-200'
                              : 'border-slate-200 text-slate-600 dark:border-white/15 dark:text-slate-200'
                        }`}
                      >
                        {area.name}
                        <span
                          className={`ml-2 rounded-full px-2 py-0.5 text-[9px] font-bold ${
                            isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-200'
                          }`}
                        >
                          {area.cameraPlacement === 'external' ? 'zew' : 'wew'}
                        </span>
                        {area.cameraPlacement === 'external' ? (
                          <span
                            className={`ml-2 rounded-full px-2 py-0.5 text-[9px] font-bold ${
                              isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-200'
                            }`}
                          >
                            {area.cameraSpots.length}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] sm:mt-6 sm:gap-6">
                <div className="min-w-0 space-y-2.5 sm:space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
                    <div className="min-w-0 break-words">
                      <p className="text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">Aktywna strefa</p>
                      <p className="text-lg font-semibold tracking-[0.1em] text-slate-700 dark:text-slate-100">{activeCameraArea?.name ?? 'Wybierz strefę'}</p>
                      {activeCameraArea && (
                        <>
                          {isActiveCameraExternal && activeCameraArea.cameraUrl.trim().length > 0 ? (
                            <p className="text-[11px] tracking-[0.1em] text-slate-500 dark:text-slate-300/70">
                              URL:{' '}
                              <span className="inline-block max-w-full break-all font-mono text-[10px] text-emerald-600 dark:text-emerald-300">
                                {activeCameraArea.cameraUrl}
                              </span>
                            </p>
                          ) : null}
                          {activeCameraIdentifier && (
                            <p className="text-[11px] tracking-[0.1em] text-slate-500 dark:text-slate-300/70">
                              ID kamery:{' '}
                              <span className="inline-block max-w-full break-all font-mono text-[10px] text-emerald-600 dark:text-emerald-300">
                                {activeCameraIdentifier}
                              </span>
                            </p>
                          )}
                        </>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                      <button
                        type="button"
                        onClick={() => activeCameraArea && clearCameraSpots(activeCameraArea.id)}
                        disabled={!activeCameraArea || !isActiveCameraExternal || activeCameraArea.cameraSpots.length === 0}
                        className="rounded-full border border-slate-200 px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10 sm:px-4 sm:py-1.5 sm:text-[10px] sm:tracking-[0.2em]"
                      >
                        Wyczysc
                      </button>
                      <button
                        type="button"
                        onClick={handleRefreshCameraSnapshot}
                        disabled={!canRefreshCameraSnapshot || isActiveCameraLoading}
                        className="rounded-full border border-emerald-300 px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-400/60 dark:text-emerald-200 dark:hover:bg-white/10 sm:px-4 sm:py-1.5 sm:text-[10px] sm:tracking-[0.2em]"
                      >
                        {isActiveCameraLoading ? 'Ładowanie...' : 'Odśwież podgląd'}
                      </button>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-3 transition-colors dark:border-white/10 dark:bg-white/5 sm:p-4">
                    <div className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">Konfiguracja kamery</div>
                    {activeCameraArea ? (
                      <div className="grid gap-3 sm:gap-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <label className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600 transition dark:border-white/15 dark:text-slate-200">
                            <input
                              type="radio"
                              name={`camera-placement-${activeCameraArea.id}`}
                              checked={activeCameraArea.cameraPlacement === 'internal'}
                              onChange={() => handleCameraPlacementChange(activeCameraArea.id, false)}
                              className="h-4 w-4 border-slate-300 text-emerald-600 focus:ring-emerald-500 dark:border-white/20"
                            />
                            <span>Kamera wewnetrzna</span>
                          </label>
                          <label className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600 transition dark:border-white/15 dark:text-slate-200">
                            <input
                              type="radio"
                              name={`camera-placement-${activeCameraArea.id}`}
                              checked={activeCameraArea.cameraPlacement === 'external'}
                              onChange={() => handleCameraPlacementChange(activeCameraArea.id, true)}
                              className="h-4 w-4 border-slate-300 text-emerald-600 focus:ring-emerald-500 dark:border-white/20"
                            />
                            <span>Kamera zewnetrzna</span>
                          </label>
                          {isFetchingAreaCameras ? (
                            <span className="text-[10px] font-normal normal-case tracking-normal text-slate-500 dark:text-slate-300">
                              Pobieram przypisane kamery...
                            </span>
                          ) : null}
                        </div>
                        {activeCameraArea.cameraPlacement === 'internal' ? (
                          <div className="grid gap-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <select
                                value={activeCameraArea.internalCameraId}
                                onChange={(event) => handleInternalCameraChange(activeCameraArea.id, event.target.value)}
                                disabled={isFetchingCameras}
                                className="min-w-[220px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:bg-neutral-900 dark:text-slate-100 dark:[&>option]:bg-neutral-900 dark:[&>option]:text-slate-100"
                              >
                                <option value="">{internalCameraOptions.length ? '-- wybierz kamere wewnetrzna --' : 'Brak kamer wewnetrznych'}</option>
                                {activeCameraArea.internalCameraId && !internalCameraOptions.some((camera) => camera.id === activeCameraArea.internalCameraId) ? (
                                  <option value={activeCameraArea.internalCameraId}>Aktualna kamera ({activeCameraArea.internalCameraId})</option>
                                ) : null}
                                {internalCameraOptions.map((camera) => (
                                  <option key={camera.id} value={camera.id}>
                                    {camera.name}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => void handleAssignInternalCamera(activeCameraArea.id)}
                                disabled={isFetchingCameras || assigningAreaCameraId === activeCameraArea.id || !activeCameraArea.internalCameraId}
                                className="rounded-full border border-emerald-300 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {assigningAreaCameraId === activeCameraArea.id ? 'Zapisywanie...' : 'Przypisz'}
                              </button>
                            </div>
                            {activeCameraArea.internalCameraId ? (
                              <div className="text-[10px] font-semibold normal-case tracking-normal text-emerald-600 dark:text-emerald-300">
                                Aktualnie przypisana kamera: {activeCameraArea.internalCameraId}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 dark:text-slate-300/80">
                                Wybierz kamere z listy i przypisz ja do tej strefy.
                              </p>
                            )}
                          </div>
                        ) : (
                          <>
                        <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                          <span className={formLabelClass}>Wybierz kamere zewnetrzna</span>
                          <select
                            value={activeCameraArea.externalCameraId}
                            onChange={(event) => handleExternalCameraChange(activeCameraArea.id, event.target.value)}
                            disabled={isFetchingCameras}
                            className="min-w-[220px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:bg-neutral-900 dark:text-slate-100 dark:[&>option]:bg-neutral-900 dark:[&>option]:text-slate-100"
                          >
                            <option value="">{externalCameraOptions.length ? '-- nowa lub wybierz zewnetrzna --' : 'Brak kamer zewnetrznych'}</option>
                            {activeCameraArea.externalCameraId && !externalCameraOptions.some((camera) => camera.id === activeCameraArea.externalCameraId) ? (
                              <option value={activeCameraArea.externalCameraId}>Aktualna kamera ({activeCameraArea.externalCameraId})</option>
                            ) : null}
                            {externalCameraOptions.map((camera) => (
                              <option key={camera.id} value={camera.id}>
                                {camera.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                          <span className={formLabelClass}>URL kamery</span>
                          <input
                            type="url"
                            value={activeCameraArea.cameraUrl}
                            onChange={(event) => handleCameraUrlChange(activeCameraArea.id, event.target.value)}
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
                          />
                        </label>
                        <div className="grid gap-3 md:grid-cols-2 sm:gap-4">
                          <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                            <span className={formLabelClass}>Longitude</span>
                            <input
                              type="number"
                              step="0.000001"
                              value={activeCameraArea.cameraLongitude}
                              onChange={(event) => handleCameraDetailsChange(activeCameraArea.id, 'cameraLongitude', event.target.value)}
                              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
                            />
                          </label>
                          <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                            <span className={formLabelClass}>Latitude</span>
                            <input
                              type="number"
                              step="0.000001"
                              value={activeCameraArea.cameraLatitude}
                              onChange={(event) => handleCameraDetailsChange(activeCameraArea.id, 'cameraLatitude', event.target.value)}
                              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
                            />
                          </label>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2 sm:gap-4">
                          <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                            <span className={formLabelClass}>Urzadzenie</span>
                            <input
                              type="text"
                              value={activeCameraArea.cameraDevice}
                              onChange={(event) => handleCameraDetailsChange(activeCameraArea.id, 'cameraDevice', event.target.value)}
                              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
                            />
                          </label>
                          <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                            <span className={formLabelClass}>Opis</span>
                            <textarea
                              rows={3}
                              value={activeCameraArea.cameraDescription}
                              onChange={(event) => handleCameraDetailsChange(activeCameraArea.id, 'cameraDescription', event.target.value)}
                              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
                            />
                          </label>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          {!activeExternalCameraOption ? (
                            <button
                              type="button"
                              onClick={() => void handleAddExternalCamera()}
                              disabled={!canAddExternalCamera || isAddingExternalCamera}
                              aria-busy={isAddingExternalCamera}
                              className="rounded-full border border-emerald-300 px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-400/60 dark:text-emerald-200 dark:hover:bg-white/10 sm:px-4 sm:py-1.5 sm:text-[10px] sm:tracking-[0.2em]"
                            >
                              {isAddingExternalCamera ? 'Dodawanie...' : 'Dodaj kamere'}
                            </button>
                          ) : (
                            <span className="text-[10px] font-semibold normal-case tracking-normal text-slate-500 dark:text-slate-300">
                              Wybrana kamera zewnetrzna zostanie przypisana do obszaru przy przejsciu dalej.
                            </span>
                          )}
                          {activeCameraArea.externalCameraId ? (
                            <span className="text-[10px] font-semibold normal-case tracking-normal text-emerald-600 dark:text-emerald-300">
                              Aktualne ID: {activeCameraArea.externalCameraId}
                            </span>
                          ) : null}
                        </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 dark:text-slate-300/80">Wybierz strefę z listy powyżej.</p>
                    )}
                  </div>

                  <div
                    className="relative w-full max-w-full min-h-[200px] min-w-0 overflow-hidden rounded-3xl border border-slate-200 bg-neutral-900 shadow-inner transition-colors dark:border-white/10 sm:min-h-[360px]"
                    style={{ ...cameraPreviewStyle, aspectRatio: '16 / 9' }}
                  >
                    {isCameraDrawingInProgress ? (
                      <div className="pointer-events-none absolute left-2 top-2 z-10 flex flex-wrap items-center gap-2 rounded-full bg-white/90 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-600 shadow-sm dark:bg-neutral-900/80 dark:text-slate-200 sm:left-3 sm:top-3 sm:px-3 sm:py-2">
                        <span className="text-[10px] tracking-[0.2em]">Rysujesz miejsce ({activeCameraDrawing?.points.length ?? 0} pkt)</span>
                        <button
                          type="button"
                          onClick={handleCancelCameraDrawing}
                          className="pointer-events-auto rounded-full border border-rose-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-rose-500 transition hover:bg-rose-500 hover:text-white dark:border-rose-300 dark:text-rose-200 dark:hover:bg-rose-400/20"
                        >
                          Anuluj
                        </button>
                      </div>
                    ) : null}
                    {isActiveCameraLoading && (
                      <div className="pointer-events-auto absolute inset-0 flex items-center justify-center bg-black/40 text-xs font-semibold uppercase tracking-[0.3em] text-white">
                        Ładowanie danych kamery...
                      </div>
                    )}
                    {!activeCameraSnapshotSrc && !isActiveCameraLoading && (
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/30 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-white">
                        Brak podglądu kamery. Odśwież, aby pobrać zdjęcie.
                      </div>
                    )}
                    {activeCameraSnapshotError && (
                      <div className="pointer-events-none absolute inset-x-3 bottom-3 rounded-2xl bg-black/70 px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-rose-100 sm:inset-x-6 sm:bottom-6 sm:px-4 sm:py-3">
                        {activeCameraSnapshotError}
                      </div>
                    )}
                    <div
                      ref={annotationOverlayRef}
                      className="absolute inset-0 touch-none"
                      style={{ cursor: activeCameraArea ? (isActiveCameraExternal ? (isCameraSpotDragInProgress ? 'grabbing' : 'crosshair') : 'not-allowed') : 'not-allowed' }}
                      onPointerDown={handleAnnotationPointerDown}
                      onPointerMove={handleAnnotationPointerMove}
                      onPointerUp={handleAnnotationPointerUp}
                      onPointerLeave={handleAnnotationPointerLeave}
                      onPointerCancel={handleAnnotationPointerCancel}
                    >
                      <svg
                        className="absolute inset-0 h-full w-full"
                        viewBox={`0 0 ${CAMERA_SVG_SCALE} ${CAMERA_SVG_SCALE}`}
                        preserveAspectRatio="none"
                      >
                        {activeCameraArea?.cameraSpots.map((spot, index) => {
                          return (
                            <g key={spot.id || `spot-${index}`}>
                              <polygon
                                points={cameraSpotPointsToSvg(spot.points)}
                                className="fill-emerald-400/10 stroke-emerald-400/80"
                                strokeWidth={3}
                                style={{ cursor: isCameraDrawingInProgress ? 'crosshair' : isCameraSpotDragInProgress ? 'grabbing' : 'grab' }}
                                onPointerDown={(event) => handleCameraSpotPointerDown(event, spot.id)}
                              />
                            </g>
                          );
                        })}
                        {activeCameraDrawing && cameraDrawingPreviewPoints.length > 0 && (
                          <>
                            <polyline
                              points={cameraSpotPointsToSvg(cameraDrawingPreviewPoints)}
                              className="fill-transparent stroke-emerald-300/80"
                              strokeWidth={3}
                              strokeDasharray="10 6"
                            />
                            {activeCameraDrawing.points.map((point, index) => (
                              <circle
                                key={`vertex-${index}`}
                                cx={(point[0] * CAMERA_SVG_SCALE).toFixed(1)}
                                cy={(point[1] * CAMERA_SVG_SCALE).toFixed(1)}
                                r={10}
                                className={`${
                                  index === 0 && cameraDrawingCloseHint ? 'fill-emerald-500 stroke-white' : 'fill-white stroke-emerald-500'
                                } stroke-[3px]`}
                              />
                            ))}
                            {activeCameraDrawing.hoverPoint && (
                              <circle
                                cx={(activeCameraDrawing.hoverPoint[0] * CAMERA_SVG_SCALE).toFixed(1)}
                                cy={(activeCameraDrawing.hoverPoint[1] * CAMERA_SVG_SCALE).toFixed(1)}
                                r={8}
                                className="fill-emerald-400 stroke-white stroke-[2px]"
                              />
                            )}
                          </>
                        )}
                      </svg>
                      <div className="pointer-events-none absolute inset-0">
                        {activeCameraArea?.cameraSpots.map((spot, index) => {
                          const centroid = calculateCameraSpotCentroid(spot.points);
                          if (!centroid) {
                            return null;
                          }
                          const label = spot.label?.trim() || formatCameraSpotLabel(index + 1);
                          return (
                            <span
                              key={`${spot.id || `spot-${index}`}-label`}
                              className="absolute -translate-x-1/2 -translate-y-1/2 rounded bg-white/85 px-2 py-0.5 text-[8px] font-semibold uppercase tracking-[0.2em] text-emerald-600 shadow sm:text-[9px]"
                              style={{
                                left: `${centroid[0] * 100}%`,
                                top: `${centroid[1] * 100}%`,
                              }}
                            >
                              {label}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  {activeCameraSnapshotSrc && cameraSnapshotTimestampLabel && (
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300/80">
                      Ostatnia aktualizacja: <span className="font-semibold text-emerald-600 dark:text-emerald-300">{cameraSnapshotTimestampLabel}</span>
                    </p>
                  )}
                  {isActiveCameraExternal ? (
                    <>
                      <p className="text-[11px] text-slate-500 dark:text-slate-300/80">
                        Klikaj na podglądzie, aby dodawać kolejne punkty poligonu. Ponowne kliknięcie pierwszego punktu zamknie kształt i zapisze miejsce.
                      </p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-300/80">
                        Gotowe poligony można przeciągać, aby zmienić ich położenie bez zmiany kształtu.
                      </p>
                    </>
                  ) : (
                    <p className="text-[11px] text-slate-500 dark:text-slate-300/80">
                      Oznaczanie miejsc jest dostępne tylko dla kamer zewnętrznych. Dla kamery wewnętrznej w tym kroku przypisujesz urządzenie do strefy i możesz odświeżyć podgląd.
                    </p>
                  )}
                </div>

                <div className="min-w-0 flex flex-col gap-3 rounded-2xl border border-slate-200 p-3 transition-colors dark:border-white/10 dark:bg-white/5 sm:gap-4 sm:p-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">Lista oznaczonych miejsc</div>
                  {activeCameraArea && isActiveCameraExternal && activeCameraArea.cameraSpots.length > 0 ? (
                    <div className="flex flex-col gap-3">
                      {activeCameraArea.cameraSpots.map((spot) => {
                        const pointPreview = spot.points
                          .slice(0, 3)
                          .map((point) => `${Math.round(point[0] * 100)}%/${Math.round(point[1] * 100)}%`)
                          .join(' • ');
                        const extraPoints = spot.points.length > 3 ? ` +${spot.points.length - 3}` : '';
                        return (
                          <div key={spot.id} className="rounded-xl border border-slate-200 p-3 text-[11px] transition-colors dark:border-white/15">
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={spot.label}
                                onChange={(event) => updateCameraSpotLabel(activeCameraArea.id, spot.id, event.target.value)}
                                className="flex-1 rounded-lg border border-slate-300 px-3 py-1 text-[11px] font-semibold tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/15 dark:bg-white/5 dark:text-slate-100"
                              />
                              <button
                                type="button"
                                onClick={() => removeCameraSpot(activeCameraArea.id, spot.id)}
                                className="rounded-full border border-rose-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-rose-500 transition hover:bg-rose-500 hover:text-white"
                              >
                                Usuń
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-300/80">
                      {isActiveCameraExternal
                        ? 'Brak oznaczeń. Klikaj na podglądzie kamery, aby narysować poligon dla każdego miejsca parkingowego.'
                        : 'Dla kamer wewnętrznych lista oznaczeń nie jest używana.'}
                    </p>
                  )}
                </div>
              </div>
            </section>
          )}

          {step === layoutStepId && (
            <section className="rounded-3xl border border-slate-200 bg-white p-3 transition-colors dark:border-white/10 dark:bg-white/5 sm:p-6">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-500 dark:text-slate-300/80">
                  Krok 5 - Schemat parkingu
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={goToPreviousStep}
                    className="rounded-2xl border border-slate-200 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.4em] text-slate-600 transition hover:bg-slate-600 hover:text-white"
                  >
                    Wróć
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSubmitLayout()}
                    disabled={Boolean(layoutSubmitDisabledReason)}
                    aria-busy={isSubmittingLayout}
                    className="rounded-2xl border border-emerald-500 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.4em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSubmittingLayout
                      ? editingParkingMeta
                        ? 'Aktualizowanie...'
                        : 'Wysylanie...'
                      : editingParkingMeta
                        ? 'Aktualizuj schemat'
                        : 'Zapisz schemat'}
                  </button>
                </div>
              </div>

              <div className="space-y-5">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-[11px] uppercase tracking-[0.3em] text-slate-500 transition-colors dark:border-white/10 dark:bg-white/5 dark:text-slate-300/80">
                  {declaredSpaceTarget > 0 ? (
                    <p>
                      Liczba zadeklarowanych miejsc: <span className="font-semibold text-emerald-600 dark:text-emerald-300">{declaredSpaceTarget}</span>.
                    </p>
                  ) : (
                    <p>Brak zadeklarowanej liczby miejsc - dodaj przynajmniej jedno miejsce w kreatorze.</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-600 transition-colors dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
                  <label className="flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                    <span>Docelowa strefa schematu:</span>
                    <select
                      value={layoutAreaLocalId ?? ''}
                      onChange={(event) => {
                        const nextId = event.target.value;
                        setLayoutAreaLocalId(nextId.length ? nextId : null);
                      }}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-100"
                    >
                      <option value="">{areas.length ? '-- wybierz strefę --' : 'Brak stref'}</option>
                      {areas.map((area) => (
                        <option key={area.id} value={area.id} disabled={area.serverId === null}>
                          {area.name}
                          {area.serverId === null ? ' (brak ID w PB)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={handleRefreshLayout}
                    disabled={layoutServerAreaId === null || layoutParkingId === null || isFetchingLayout}
                    className="rounded-lg border border-emerald-300 px-4 py-2 text-[10px] font-semibold tracking-[0.3em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isFetchingLayout ? 'Pobieranie...' : 'Odśwież schemat'}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveLayoutDraft}
                    disabled={!layoutAreaLocalId}
                    className="rounded-lg border border-slate-300 px-4 py-2 text-[10px] font-semibold tracking-[0.3em] text-slate-600 transition hover:bg-slate-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Zapisz szkic
                  </button>
                  {layoutTargetArea?.cameraPlacement === 'internal' ? (
                    <button
                      type="button"
                      onClick={() => void handleApplyInternalSpotIdsToLayout()}
                      disabled={layoutParkingId === null || isFetchingInternalParkingSnapshot}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-[10px] font-semibold tracking-[0.3em] text-slate-600 transition hover:bg-slate-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Nadaj ID do schematu
                    </button>
                  ) : null}
                  {layoutFetchError ? (
                    <span className="text-[10px] font-normal normal-case tracking-normal text-rose-500 dark:text-rose-300">{layoutFetchError}</span>
                  ) : layoutServerAreaId === null ? (
                    <span className="text-[10px] font-normal normal-case tracking-normal text-slate-500 dark:text-slate-300">
                      Zapisz krok 2, aby pobrać identyfikatory stref z Parking Box.
                    </span>
                  ) : null}
                  {layoutTargetArea?.cameraPlacement === 'internal' && internalParkingSnapshotError ? (
                    <span className="text-[10px] font-normal normal-case tracking-normal text-rose-500 dark:text-rose-300">{internalParkingSnapshotError}</span>
                  ) : null}
                </div>
                {isInternalLayoutReferenceEnabled && layoutBuilder.spots.length > 0 ? (
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 text-[11px] uppercase tracking-[0.25em] text-slate-600 transition-colors dark:border-white/10 dark:bg-white/5 dark:text-slate-300/80">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <span className="font-semibold">Mapowanie ID miejsc</span>
                      <span className="text-[10px] normal-case tracking-normal text-slate-500 dark:text-slate-300">
                        Wybierz identyfikator dla kazdego miejsca na schemacie.
                      </span>
                    </div>
                    <div className="space-y-2">
                      {layoutBuilder.spots.map((spot, index) => {
                        const currentReferenceId = layoutInternalReferenceOptionIds.has(spot.id) ? spot.id : '';
                        const usedByOtherSpots = new Set(
                          layoutBuilder.spots
                            .filter((candidate, candidateIndex) => candidateIndex !== index)
                            .map((candidate) => candidate.id),
                        );
                        return (
                          <div
                            key={`${spot.id}-${index}`}
                            className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 dark:border-white/10 dark:bg-white/5 lg:grid-cols-[minmax(0,1fr)_minmax(220px,280px)]"
                          >
                            <div className="min-w-0">
                              <div className="text-[10px] text-slate-500 dark:text-slate-300/80">Miejsce na schemacie</div>
                              <div className="mt-1 truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                                {spot.label} <span className="font-mono text-emerald-600 dark:text-emerald-300">({spot.id})</span>
                              </div>
                            </div>
                            <label className="flex flex-col gap-1">
                              <span className="text-[10px] text-slate-500 dark:text-slate-300/80">ID</span>
                              <select
                                value={currentReferenceId}
                                onChange={(event) => handleAssignLayoutSpotReference(index, event.target.value)}
                                className="min-w-0 rounded-lg border border-slate-300 bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/15 dark:bg-slate-900/80 dark:text-slate-100"
                              >
                                <option value="">{currentReferenceId ? '-- wybierz ID --' : `Aktualne: ${spot.id}`}</option>
                                {layoutInternalReferenceOptions.map((option) => {
                                  const disabled = usedByOtherSpots.has(option.id) && option.id !== currentReferenceId;
                                  return (
                                    <option key={option.id} value={option.id} disabled={disabled}>
                                      {option.label}{disabled ? ' - uzyte' : ''}
                                    </option>
                                  );
                                })}
                              </select>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                {layoutReferenceArea ? (
                  <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
                    <div
                      className="relative min-h-[240px] min-w-0 overflow-hidden rounded-3xl border border-slate-200 bg-neutral-900 shadow-inner transition-colors dark:border-white/10 sm:min-h-[360px]"
                      style={{ ...layoutReferencePreviewStyle, aspectRatio: '16 / 9' }}
                    >
                      {isLayoutReferenceLoading && (
                        <div className="pointer-events-auto absolute inset-0 flex items-center justify-center bg-black/45 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-white">
                          Ładowanie danych kamery...
                        </div>
                      )}
                      {!layoutReferenceHasSnapshot && !isLayoutReferenceLoading && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-white">
                          Brak podglądu kamery. Dodaj kamerę i odśwież podgląd.
                        </div>
                      )}
                      <div className="pointer-events-none absolute inset-0">
                        <svg className="absolute inset-0 h-full w-full" viewBox={`0 0 ${CAMERA_SVG_SCALE} ${CAMERA_SVG_SCALE}`} preserveAspectRatio="none">
                          {layoutReferenceArea.cameraSpots.map((spot, index) => {
                            return (
                              <g key={spot.id || `layout-spot-${index}`}>
                                <polygon points={cameraSpotPointsToSvg(spot.points)} className="fill-emerald-400/10 stroke-emerald-400/80" strokeWidth={3} />
                              </g>
                            );
                          })}
                        </svg>
                        <div className="pointer-events-none absolute inset-0">
                          {layoutReferenceArea.cameraSpots.map((spot, index) => {
                            const centroid = calculateCameraSpotCentroid(spot.points);
                            if (!centroid) {
                              return null;
                            }
                            const label = spot.label?.trim() || formatCameraSpotLabel(index + 1);
                            return (
                              <span
                                key={spot.id || `layout-spot-label-${index}`}
                                className="absolute -translate-x-1/2 -translate-y-1/2 rounded bg-white/85 px-2 py-0.5 text-[8px] font-semibold uppercase tracking-[0.15em] text-emerald-600 shadow"
                                style={{ left: `${centroid[0] * 100}%`, top: `${centroid[1] * 100}%` }}
                              >
                                {label}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <div className="pointer-events-none absolute inset-x-3 bottom-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-black/60 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white sm:inset-x-4 sm:bottom-4 sm:px-4 sm:py-3 sm:text-[11px]">
                        <span>Podglad kamery dla strefy {layoutReferenceArea.name}</span>
                        {layoutReferenceTimestampLabel ? <span>{layoutReferenceTimestampLabel}</span> : null}
                      </div>
                    </div>
                    <div className="min-w-0 space-y-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-[11px] uppercase tracking-[0.25em] text-slate-600 transition-colors dark:border-white/10 dark:bg-white/5 dark:text-slate-300/80">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold">Mapa miejsc z kamery</span>
                        <button
                          type="button"
                          onClick={() => void handleRefreshLayoutReferenceSnapshot()}
                          disabled={!canRefreshLayoutReferenceSnapshot || isLayoutReferenceLoading}
                          className="rounded-full border border-emerald-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-400/60 dark:text-emerald-200 dark:hover:bg-white/10"
                        >
                          {isLayoutReferenceLoading ? 'Ładowanie...' : 'Odśwież podgląd'}
                        </button>
                      </div>
                      {layoutReferenceHasSpots ? (
                        <div className="space-y-2">
                          {layoutReferenceArea.cameraSpots.map((spot, index) => (
                            <div
                              key={spot.id}
                              className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-600 transition-colors dark:border-white/10 dark:bg-white/0 dark:text-slate-200"
                            >
                              <span className="truncate">{spot.label || formatCameraSpotLabel(index + 1)}</span>
                              <span className="font-mono text-[10px] text-emerald-600 dark:text-emerald-300">{spot.id}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[11px] normal-case tracking-normal text-slate-500 dark:text-slate-300">
                          Brak miejsc narysowanych na podglądzie kamery.
                        </p>
                      )}
                      <p className="text-[10px] normal-case tracking-normal text-slate-500 dark:text-slate-300">
                        Podczas zapisu schematu ID miejsc z kamery zostana uzyte jako ID miejsc na schemacie.
                      </p>
                    </div>
                  </div>
                ) : null}
                <ParkingLayoutBuilder
                  value={layoutBuilder}
                  onChange={handleLayoutBuilderChange}
                  targetSpots={declaredSpaceTarget > 0 ? declaredSpaceTarget : null}
                />
              </div>
            </section>
          )}
        </section>

        <section className="flex flex-col gap-4 border-t border-slate-200 px-4 py-5 transition-colors dark:border-white/10 sm:px-6 sm:py-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-500 dark:text-slate-300/80">Parkingi w systemie</div>

            <button
              type="button"
              onClick={() => void fetchParkingList()}
              disabled={isParkingListLoading}
              className="rounded-full border border-slate-300 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-600 transition hover:bg-slate-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
            >
              {isParkingListLoading ? 'Odświeżanie...' : 'Odśwież'}
            </button>
          </div>

          {parkingListError ? <p className="text-sm font-semibold text-rose-500 dark:text-rose-300">{parkingListError}</p> : null}

          {isParkingListLoading && parkingListItems.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-300/80">Trwa pobieranie listy parkingów...</p>
          ) : null}

          {!parkingListItems.length && !isParkingListLoading && !parkingListError ? (
            <p className="text-sm text-slate-500 dark:text-slate-300/80">Brak parkingów w systemie.</p>
          ) : null}

          {parkingListItems.map((item, index) => {
            const identifier = parseParkingIdentifier(item.id);

            const cardKey = identifier?.text ?? `${item.name ?? 'parking'}-${index}`;

            const isEditingCard = editingParkingMeta ? identifiersAreEqual(editingParkingMeta.id, identifier?.raw ?? null) : false;

            const isDeletingCard = identifier ? identifiersAreEqual(deletingParkingId, identifier.raw) : false;

            const canModify = Boolean(identifier);

            const cardClasses = isEditingCard
              ? 'flex flex-wrap items-center gap-4 rounded-2xl border border-amber-300 bg-amber-50 p-4 transition-colors dark:border-amber-300/60 dark:bg-amber-400/10'
              : 'flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 p-4 transition-colors dark:border-white/10';

            return (
              <div key={cardKey} className={cardClasses}>
                <div className="flex flex-col">
                  <span className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-600 dark:text-emerald-300">{item.name ?? 'Brak nazwy'}</span>

                  <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300/80">{item.address ?? 'Brak adresu'}</span>

                  {identifier ? (
                    <span className="text-[10px] uppercase tracking-[0.35em] text-slate-400 dark:text-slate-500">ID: {identifier.text}</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.35em] text-rose-500 dark:text-rose-300">Brak identyfikatora</span>
                  )}
                </div>

                <div className="flex flex-wrap gap-3 text-[11px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300/80">
                  <span>Miejsca zwykłe: {formatSpaceCount(item.space_regular)}</span>
                </div>

                <div className="ml-auto flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleEditParking(item)}
                    disabled={!canModify}
                    className={`rounded-lg border px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] transition ${
                      isEditingCard ? 'border-amber-400 bg-amber-500 text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-600 hover:text-white'
                    } disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    {isEditingCard ? 'Edytujesz' : 'Edytuj'}
                  </button>

                  <button
                    type="button"
                    onClick={() => requestDeleteParking(item)}
                    disabled={!canModify || isDeletingCard}
                    className={`rounded-lg border px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] transition ${
                      isDeletingCard ? 'border-rose-400 bg-rose-500 text-white' : 'border-rose-300 text-rose-500 hover:bg-rose-500 hover:text-white'
                    } disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    {isDeletingCard ? 'Usuwanie...' : 'Usuń'}
                  </button>
                </div>
              </div>
            );
          })}
        </section>
        {pendingDelete ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-8">
            <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 text-slate-900 shadow-xl transition-colors dark:border-white/10 dark:bg-neutral-900 dark:text-slate-100">
              <h2 className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">Usunąć parking?</h2>
              <p className="mt-4 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                Czy na pewno chcesz usunąć parking <span className="font-semibold text-emerald-600 dark:text-emerald-300">{pendingDelete.name}</span> z adresem{' '}
                <span className="font-semibold text-emerald-600 dark:text-emerald-300">{pendingDelete.address}</span>? Tej operacji nie można cofnąć.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={cancelDeleteParking}
                  disabled={deleteInProgress}
                  className="rounded-2xl border border-slate-300 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.35em] text-slate-600 transition hover:bg-slate-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-slate-200"
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  onClick={() => void confirmDeleteParking()}
                  disabled={deleteInProgress}
                  className="rounded-2xl border border-rose-400 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.35em] text-rose-500 transition hover:bg-rose-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deleteInProgress ? 'Usuwanie...' : 'Usuń'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </main>
  );
}
